import i18n from "i18next";
import { initReactI18next } from "react-i18next";

// Safely read initial language from localStorage (default to 'en')
let savedLanguage = "en";
try {
  const stored = window.localStorage.getItem("portalLanguage");
  if (stored === "en" || stored === "fr") {
    savedLanguage = stored;
  }
} catch {
  // ignore access errors (e.g. disabled storage)
}

const resources = {
  en: {
    portal: {
      header: {
        searchPlaceholder: "Search",
        notifications: "Notifications",
        trialDays: "{{count}} day left",
        trialDays_plural: "{{count}} days left",
        profile: "Profile",
        logout: "Logout",
        superAdmin: "Super Admin",
        language: "Language",
        language_en: "English",
        language_fr: "French",
      },
      sidebar: {
        dashboard: "Dashboard",
        asapProfile: "ASAP Profile",
        asapCV: "ASAP CV",
        asapSkills: "ASAP Skills",
        asapPilot: "ASAP Pilot",
        asapPilotOpen: "ASAP Pilot ▾",
        asapPilotClosed: "ASAP Pilot ▸",
        pilotClients: "Companies",
        pilotNeeds: "Needs",
        pilotConsultants: "Consultants",
        pilotConsultantTracking: "Tracking",
        pilotPerformance: "My activity",
        pilotTasks: "Tasks",
        pilotMatches: "Matches",
        asapPartners: "ASAP Partners",
        asapPartnersOpen: "ASAP Partners ▾",
        asapPartnersClosed: "ASAP Partners ▸",
        partnersPools: "Pools",
        partnersSentRequests: "Sent Requests",
        partnersReceivedRequests: "Received Requests",
        partnersPartners: "Partners",
        partnersPoolsAndPartners: "Pools & Partners",
        partnersDashboard: "Dashboard",
        settings: "Settings",
      },
      trialBanner: {
        title: "Free trial",
        message:
          "You have <b>{{count}} day left</b> on your trial. Renew now to continue using the platform.",
        message_plural:
          "You have <b>{{count}} days left</b> on your trial. Renew now to continue using the platform.",
        endsAt: " (ends {{date}})",
        contactUs: "Contact us",
      },
      expiredModal: {
        title: "Trial expired",
        body:
          "Your free trial has expired. Please contact your administrator or our team to renew your subscription and regain access to the platform.",
        ok: "Okay",
        contactUs: "Contact us",
      },
      sessionExpiredModal: {
        title: "Session expired",
        bodyExpired: "Your session has expired. Please log in again to continue.",
        bodyInvalid: "Your session is invalid. Please log in again to continue.",
        action: "Log in",
      },
      dashboard: {
        eyebrow: "ASAP Hub",
        title: "Dashboard",
        errors: {
          fetchStats: "Failed to load statistics",
        },
        filters: {
          businessUnits: {
            label: "Business units",
            all: "All Business Units",
            none: "No BU selected",
            singleFallback: "1 BU",
            count: "{{count}} BUs",
            showSingle: "Show current BU only",
            selectAll: "Select all BUs",
            menuTitle: "Business Units",
          },
          person: {
            label: "Collaborator",
            all: "All collaborators",
            groups: {
              sales: "Sales",
              recruiter: "Recruiters",
            },
          },
          granularity: {
            label: "Granularity",
          },
        },
        noData: "No data available.",
        rangeLabel: "Range: {{start}} to {{end}}",
        totalLabel: "Total: {{value}}",
        cards: {
          cv: {
            eyebrow: "ASAP CV",
            subtitle: "CVs processed",
            empty: "No CV activity for the current selection.",
          },
          skills: {
            eyebrow: "ASAP SKILLS",
            subtitle: "Assessments sent",
            empty: "No skills activity for the current selection.",
          },
          partners: {
            eyebrow: "ASAP Partners",
            jobPostingsToPool: "Job postings to candidates",
            jobPostingsToPartners: "Job postings to partners",
            consultantsProposed: "Consultants proposed to partners",
            signedRate: "Signed rate: {{rate}}%",
            noData: "No Partners data available.",
            period: {
              week: "Week",
              month: "Month",
              all: "All",
            },
          },
        },
        pilotChart: {
          eyebrow: "ASAP PILOT",
          title: "Monthly pipeline",
          subtitle: "Needs and match progression by month",
          legend: {
            needsCreated: "Needs created",
            needsCovered: "Needs covered",
            matchesProposed: "Matches proposed",
            matchesConfirmed: "Signed",
          },
          empty: "No pilot activity for the current selection.",
        },
        detailTable: {
          eyebrow: "ASAP PILOT",
          title: "Positioning detail",
          subtitle: "Current assignments and client follow-up",
          empty: "No open positionings matched the current filters.",
          prevWeek: "Previous week",
          nextWeek: "Next week",
          allMatches: "All matches",
          thisWeek: "This week",
          showAll: "Show all",
          columns: {
            consultant: "Consultant",
            client: "Client",
            jobTitle: "Job title",
            status: "Positioning status",
            sales: "Sales",
            recruiter: "Recruiter",
          },
          statuses: {
            positioned: "Positioned",
            awaitingClientFeedback: "Awaiting client feedback",
            signed: "Signed",
          },
        },
        podiums: {
          recruiterTitle: "Recruiter podium",
          salesTitle: "Sales podium",
          recruiterMetric: "{{count}} placement",
          recruiterMetric_plural: "{{count}} placements",
          salesMetric: "{{count}} conversion",
          salesMetric_plural: "{{count}} conversions",
          empty: "No ranking data available.",
        },
        drilldown: {
          monthLabel: "Drill-down month",
          button: "Open weekly view",
          help: "Open the selected month in ASAP PILOT weekly performance.",
          disabledMultiBu: "Select a single Business Unit to open the weekly drill-down.",
        },
      },
      consultantPortal: {
        sidebar: {
          dashboard: "Dashboard",
          activityReports: "Activity Reports",
          leaveRequests: "Leave Requests",
          assignmentFeedback: "Assignment Feedback",
          calendar: "Calendar",
        },
        common: {
          eyebrow: "Consultant Portal",
          loading: "Loading...",
          monthNames: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
        },
        dashboard: {
          pageTitle: "Dashboard",
          welcomeMessage: "Welcome back, {{name}}",
          currentMonthTimesheet: "Current month timesheet",
          recentTimesheets: "Recent timesheets",
          notSubmitted: "Not submitted",
          pendingLeaveRequests: "Pending leave requests",
          pendingLeaveDescription: "{{count}} request(s) awaiting review",
          noPendingLeave: "No pending requests",
          currentAssignment: "Current assignment",
          noAssignment: "No active assignment",
          quickActions: "Quick actions",
          fillTimesheet: "Fill timesheet",
          requestLeave: "Request leave",
          viewCalendar: "View calendar",
          billable: "billable",
          nonBillable: "non-billable",
          statusLabels: {
            draft: "Draft",
            submitted: "Submitted",
            approved: "Approved",
            rejected: "Rejected",
          },
          toasts: {
            loadFailedTitle: "Failed to load dashboard",
            loadFailedDescription: "Please try again.",
          },
        },
        calendar: {
          pageTitle: "Calendar",
          pageDescription: "View your work schedule, leave days, and public holidays.",
          legend: "Legend",
          workDay: "Work day",
          leaveDay: "Leave",
          publicHoliday: "Public holiday",
          weekend: "Weekend",
          today: "Today",
          dayNames: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
          toasts: {
            loadFailedTitle: "Failed to load calendar",
            loadFailedDescription: "Please try again.",
          },
        },
        activityReports: {
          pageTitle: "Activity Reports",
          pageDescription: "Record your monthly billable and non-billable days, then submit for review.",
          createNewReport: "Create a new report",
          formTitleEdit: "Edit draft",
          formTitleNew: "New report",
          fields: {
            year: "Year",
            month: "Month",
            billableDays: "Billable days",
            nonBillableDays: "Non-billable days",
            notes: "Notes",
          },
          placeholders: {
            notes: "Add any context for Sales or HR",
          },
          buttons: {
            saveDraft: "Save draft",
            submitReport: "Submit report",
          },
          historyTitle: "History",
          empty: "No reports yet.",
          emptyHint: "Fill out the form to record your first monthly report.",
          table: {
            period: "Period",
            days: "Days",
            status: "Status",
            review: "Review",
            edit: "Edit",
          },
          statusLabels: {
            draft: "Draft",
            submitted: "Submitted",
            approved: "Approved",
            rejected: "Rejected",
          },
          toasts: {
            loadFailedTitle: "Failed to load activity reports",
            loadFailedDescription: "Please try again.",
            submitted: "Activity report submitted",
            saved: "Activity report saved",
            saveFailedTitle: "Could not save activity report",
            saveFailedDescription: "Please review the form and try again.",
          },
        },
        assignmentFeedback: {
          pageTitle: "Assignment Feedback",
          pageDescription: "Share quarterly feedback on your client assignments with Sales and HR.",
          empty: "No eligible assignments found yet.",
          emptyHint: "Feedback cycles will appear here once you are on an assignment.",
          clientAssignment: "Client assignment",
          untitledNeed: "Untitled need",
          ongoing: "ongoing",
          ratingLabel: "Rating",
          rateStarsAria: "Rate {{count}} stars",
          commentPlaceholder: "Describe how the assignment is going",
          submittedAt: "Submitted at {{date}}",
          buttons: {
            saveDraft: "Save draft",
            submitFeedback: "Submit feedback",
          },
          feedbackSubmitted: "Feedback submitted",
          availableEndOfCycle: "Feedback becomes available at the end of this cycle.",
          noCyclesYet: "No feedback cycles have been created for this assignment yet. Feedback will appear here when cycles are available.",
          statusLabels: {
            draft: "Draft",
            submitted: "Submitted",
          },
          toasts: {
            loadFailedTitle: "Failed to load assignment feedback",
            loadFailedDescription: "Please try again.",
            submitted: "Assignment feedback submitted",
            saved: "Assignment feedback saved",
            saveFailedTitle: "Could not save assignment feedback",
            saveFailedDescription: "Please review the form and try again.",
          },
        },
        leaveRequests: {
          pageTitle: "Leave Requests",
          pageDescription: "Create, submit, and track your time-off requests.",
          newRequest: "New request",
          formTitleEdit: "Edit request",
          formTitleNew: "New request",
          listTitle: "Requests",
          fields: {
            leaveType: "Leave type",
            startDate: "Start date",
            endDate: "End date",
            notes: "Notes",
          },
          placeholders: {
            notes: "Add any supporting context",
          },
          buttons: {
            saveDraft: "Save draft",
            submitRequest: "Submit request",
            edit: "Edit",
            cancel: "Cancel",
          },
          leaveTypes: {
            annual_leave: "Annual leave",
            sick_leave: "Sick leave",
            unpaid_leave: "Unpaid leave",
            rtt: "RTT",
            other: "Other",
          },
          empty: "No leave requests yet.",
          emptyHint: "Submit a request to book time off.",
          table: {
            type: "Type",
            dates: "Dates",
            status: "Status",
            review: "Review",
          },
          statusLabels: {
            draft: "Draft",
            submitted: "Submitted",
            approved: "Approved",
            rejected: "Rejected",
            cancelled: "Cancelled",
          },
          validation: {
            invalidDatesTitle: "Invalid dates",
            endBeforeStart: "End date cannot be before start date.",
          },
          toasts: {
            loadFailedTitle: "Failed to load leave requests",
            loadFailedDescription: "Please try again.",
            submitted: "Leave request submitted",
            saved: "Leave request saved",
            saveFailedTitle: "Could not save leave request",
            saveFailedDescription: "Please review the form and try again.",
            cancelled: "Leave request cancelled",
            cancelFailedTitle: "Could not cancel request",
            cancelFailedDescription: "Please try again.",
          },
        },
      },
      pilot: {
        unknownCompany: {
          label: "Unknown",
        },
        import: {
          downloadTemplate: "Download template",
          uploadWorkbook: "Import workbook",
          uploadWorkbookTitle: "Import workbook",
          workbookDescription:
            "Upload a 5-tab workbook (Summary / Company / Client / Need / Consultant). The Summary tab is informational and is skipped.",
          workbookFileLabel: "Workbook (.xlsx)",
          dropzoneText: "Drop the .xlsx workbook here or click to browse",
          dropzoneAccepted: "Only .xlsx files are accepted.",
          defaultManagerEmailLabel: "Default manager email",
          defaultManagerEmailPlaceholder: "manager@company.com",
          summaryTitle: "Import summary",
          added: "Added",
          updated: "Updated",
          deleted: "Deleted",
          duplicates: "Duplicates",
          skipped: "Skipped",
          skippedRows: "Skipped",
          errors: "Errors",
          errorRow: "Row",
          summary: {
            company: "Companies",
            client: "Contacts",
            need: "Needs",
            consultant: "Consultants",
          },
          buttons: {
            close: "Close",
            import: "Import",
            done: "Done",
          },
          toasts: {
            importCompleted: "Workbook imported",
            importFailedTitle: "Import failed",
          },
          templateError: "Failed to download template.",
        },
      },
      pilotStatuses: {
        client: {
          inactive_dormant: "Inactive / Dormant",
          active_account: "Active Account",
          strategic_key_account: "Strategic / Key Account",
          blacklisted_excluded: "Blacklisted / Excluded",
        },
        contact: {
          new_lead_pool: "New Lead Pool",
          lead_to_qualify: "Lead to Qualify",
          cold_prospect: "Cold Prospect",
          warm_prospect_opportunity: "Warm Prospect / Opportunity",
          active_client: "Active Client",
          lost_dropped: "Lost / Dropped",
        },
        need: {
          open_early_stage: "Open – Early stage",
          open_rfp_received: "Open – RFP received",
          closed_need_closed: "Closed – Need closed",
          abandoned: "Abandoned",
          postponed: "Postponed",
          reopened: "Reopened",
        },
        consultant: {
          active: "Active",
          bench: "Bench",
          prospect: "Prospect",
          inactive: "Inactive",
        },
        consultantStaffing: {
          active: "Internal (active)",
          recruiting: "Talent pool (recruiting)",
          unavailable: "Unavailable",
          talentPool: "Talent Pool",
          betweenAssignments: "Between Assignments",
          matchInProgress: "Match in Progress",
          presentedToClient: "Presented to Client",
          signed: "Signed",
          rejected: "Rejected",
          tenderAbandoned: "Tender Abandoned",
          departure: "Inactive (departure / unavailable)",
        },
        matchAssignment: {
          potential: "Potential",
          proposed: "Proposed",
          confirmed: "Confirmed",
          active: "Active",
          completed: "Completed",
          cancelled: "Cancelled",
          lost: "Lost",
        },
        matchPurchaseOrder: {
          pending: "Pending",
          received: "Received",
          not_required: "Not required",
          cancelled: "Cancelled",
        },
        interview: {
          scheduled: "Scheduled",
          completed: "Completed",
          cancelled: "Cancelled",
        },
      },
      pilotPerformance: {
        common: {
          bucket: "Period",
          totals: "Totals",
          empty: "No data available.",
        },
        sections: {
          sales: "Sales activity",
          recruiter: "Recruiter activity",
        },
        metrics: {
          offMarketOpportunities: "Off-market opportunities",
          referralOpportunities: "Referral/listing opportunities",
          recruiterProfilesReceived: "Profiles received from recruiters",
          avgAsapSkillsScoreProposed: "Avg ASAP Skills score (proposed)",
          candidatesSubmitted: "Candidates submitted",
          clientInterviews: "Client interviews",
          wins: "Assignments won",
          conversionOppToInterview: "Conversion: opportunities → interviews",
          conversionOppToWin: "Conversion: opportunities → wins",
          staffingNeedsToCover: "Staffing needs to cover",
          profilesProposed: "Profiles proposed",
          avgAtsScore: "Avg ATS score",
          leadTimeOppToFirstProfile: "Time to first profile (days)",
          opportunitiesTotal: "Total opportunities",
          totalOpenRequirements: "Total Open Requirements",
          newOpenRequirements: "New Open Requirements",
          coveredRequirements: "Covered Requirements",
          profilesPresented: "Profiles Presented",
          wonRequirements: "Won Requirements",
        },
        list: {
          pageTitle: "My activity",
          eyebrow: "ASAP PILOT",
          emptyHint: "Adjust the period or filters to see activity metrics.",
          backToDashboard: "Back to dashboard",
          clearDashboardFilter: "Clear dashboard filter",
          dashboardFilterNotice: "Dashboard drill-down filter active for {{count}} user.",
          dashboardFilterNotice_plural:
            "Dashboard drill-down filter active for {{count}} users.",
          filters: {
            bucket: "Bucket",
            bucketWeek: "Week",
            bucketMonth: "Month",
            bucketYear: "Year",
            fromDate: "From",
            toDate: "To",
            metricsAs: "Metrics as",
            metricsAsSales: "Sales",
            metricsAsRecruiter: "Recruiter",
            roleScope: "Role scope",
            roleScopeAll: "All",
            roleScopeSales: "Sales",
            roleScopeRecruiter: "Recruiter",
            search: "Search",
            searchPlaceholder: "Search users…",
            refresh: "Refresh",
          },
          summary: {
            loading: "Loading…",
            totalUsers: "{{count}} users ({{roleScope}})",
            myRange: "Range: {{start}} → {{end}}",
          },
          table: {
            user: "User",
            role: "Role",
            opportunities: "Opportunities",
            recruitmentWorkload: "Recruitment workload",
            matches: "Matches",
            missionsStarted: "Assignments started",
            noItems: "No users found.",
          },
        },
        detail: {
          backAria: "Back",
          backToList: "Back",
          titleFallback: "User",
          meTitle: "My activity",
          chart: {
            title: "Activity over time",
            metric: "Metric",
            selectedPeriod: "Selected: {{period}}",
            tooltip:
              "{{period}} — {{metric}}: {{value}}\nOpportunities: {{opportunities}}\nRecruitment workload: {{workload}}\nMatches: {{matches}}\nAssignments started: {{missions}}",
          },
          summary: {
            loading: "Loading…",
            range: "Range: {{start}} → {{end}}",
          },
        },
      },
      tasks: {
        pageTitle: "Tasks",
        eyebrow: "ASAP Pilot",
        create: "New task",
        createTask: "New task",
        editTask: "Edit task",
        loadError: "Failed to load tasks",
        saveError: "Failed to save task",
        deleteError: "Failed to delete task",
        created: "Task created",
        updated: "Task updated",
        deleted: "Task deleted",
        titleRequired: "Title is required",
        viewMine: "My tasks",
        viewAll: "All tasks",
        empty: "No tasks found.",
        emptyHint: "Create a task to start tracking your follow-ups.",
        totalTasks: "Total",
        openTasks: "Open",
        inProgressTasks: "In progress",
        doneTasks: "Done",
        summary: {
          loading: "Loading...",
          total: "{{count}} task(s)",
        },
        allStatuses: "All statuses",
        allPriorities: "All priorities",
        titleCol: "Title",
        statusCol: "Status",
        priorityCol: "Priority",
        dueDateCol: "Due date",
        ownerCol: "Owner",
        assigneeCol: "Assignee",
        entityCol: "Linked to",
        actionsCol: "Actions",
        edit: "Edit",
        delete: "Delete",
        titleLabel: "Title",
        descriptionLabel: "Description",
        dueDateLabel: "Due date",
        priorityLabel: "Priority",
        statusLabel: "Status",
        assigneeLabel: "Assign to",
        assigneePlaceholder: "Unassigned",
        entityTypeLabel: "Link to entity type",
        entityTypePlaceholder: "None",
        entityIdLabel: "Linked entity",
        entityLoading: "Loading...",
        entityPlaceholder: "Select an entity",
        entityTypeFirst: "Select a type first",
        deleteConfirmTitle: "Delete task",
        deleteConfirmBody: "Are you sure you want to delete this task? This action cannot be undone.",
        status: {
          open: "Open",
          in_progress: "In progress",
          done: "Done",
          cancelled: "Cancelled",
        },
        priority: {
          low: "Low",
          normal: "Normal",
          high: "High",
        },
        entityType: {
          consultant: "Consultant",
          client: "Company",
          need: "Need",
          match: "Match",
        },
        widget: {
          heading: "My tasks",
          subtitle: "Open tasks assigned to you or that you own",
          viewAll: "View all tasks",
          empty: "You have no open tasks.",
          emptyHint: "You're all caught up.",
          openCount_one: "{{count}} open task",
          openCount_other: "{{count}} open tasks",
          dueLabel: "Due {{date}}",
          noDueDate: "No due date",
        },
      },
      financialKpis: {
        title: "Financial KPIs",
        loading: "Loading financial KPIs…",
        empty: "No won assignments with financial data.",
        consultant: "Consultant",
        client: "Client",
        need: "Need",
        assignmentStatus: "Status",
        dailyRate: "Daily rate",
        costRate: "Cost rate",
        billableDaysTarget: "Target days",
        billableDaysActual: "Actual days",
        revenue: "Revenue",
        cost: "Cost",
        grossMargin: "Margin",
        grossMarginPercent: "Margin %",
        summary: {
          totalRevenue: "Total revenue",
          totalCost: "Total cost",
          totalMargin: "Total margin",
          avgMarginPercent: "Avg margin %",
          totalBillableDays: "Total billable days",
          assignmentCount: "Assignments",
        },
      },
      portfolioAnalysis: {
        title: "Portfolio analysis",
        loading: "Loading portfolio analysis…",
        empty: "No portfolio data available.",
        user: "Sales rep",
        consultants: "Consultants",
        activeAssignments: "Active assignments",
        totalRevenue: "Revenue",
        totalMargin: "Margin",
        avgMarginPercent: "Avg margin %",
        utilization: "Utilization %",
        companyAvg: "Company average",
        companyMargin: "Company avg margin %",
        companyUtilization: "Company avg utilization %",
      },
      comingSoon: {
        defaultTitle: "Coming Soon",
        defaultDescription: "This feature is under active development. Check back soon!",
        goBack: "Go back",
      },
      asapProfile: {
        eyebrow: "ASAP Profile",
        pageTitle: "ASAP Profile",
        emptyList: "No searches yet. Pick a PILOT need or upload an RFP to get started.",
        list: {
          title: "Searches",
          loading: "Loading searches…",
          newSearch: "New search",
          searchPlaceholder: "Search by title or client",
          noMatch: "No searches match your filter.",
          untitled: "Untitled search",
          cardViewCandidates: "View candidates",
          forkBadge: "from #{{parent}}",
          forkBadgeTooltip:
            "Re-run from search #{{parent}} with edited criteria. The original is still in this list.",
        },
        detail: {
          title: "Search details",
          newSearchTitle: "New search",
          disabledMessage:
            "Click \"+ New search\" to pick a PILOT need or upload an RFP.",
          extractingMessage: "Analyzing the RFP… extracting criteria.",
          extractFailedMessage:
            "We couldn't extract the criteria from this RFP. You can still edit the fields manually below.",
          inProgressMessage: "Searching candidates… this can take up to a minute.",
        },
        seniority: {
          junior: "Junior",
          mid: "Mid",
          senior: "Senior",
          expert: "Expert",
        },
        status: {
          draft: "Draft",
          extracted: "Extracted",
          validated: "Validated",
          queued: "Searching",
          running: "Searching",
          completed: "Completed",
          failed: "Failed",
        },
        errors: {
          loadFailed: "Could not load searches.",
          detailLoadFailed: "Could not load this search.",
          profilesLoadFailed: "Could not load the candidate profiles.",
        },
        create: {
          title: "Create a new search",
          orLabel: "or",
          creating: "Creating search…",
          successToast: "Search created — extracting criteria…",
          tabs: {
            pilot: "Pick from PILOT",
          },
          upload: {
            dropText: "Drag & drop a PDF or DOCX, or click to browse.",
            acceptedFormats: "Accepted: .pdf, .docx (max 10 MB)",
          },
          pilot: {
            searchPlaceholder: "Search by title or client",
          },
          errors: {
            createFailed: "Could not create the search.",
            extractFailed:
              "Search created but criteria extraction failed. Edit the fields manually before validating.",
            fileTooLarge: "The file is too large (max 10 MB).",
            unsupportedType: "Only PDF and DOCX files are supported.",
            needNotFound: "This need could not be found.",
            needsLoadFailed: "Could not load PILOT needs.",
            searchBudgetExceededTitle: "Monthly search budget reached",
            searchBudgetExceededBody:
              "Your company has used {{used}} of its {{budget}} searches for the month. Contact your administrator to raise the budget.",
          },
        },
        criteria: {
          addSkillAria: "Add skill",
          skillsPlaceholder: "Add a skill and press Enter",
          fields: {
            rfpTitle: "RFP title",
            client: "Client",
            seniority: "Seniority",
            seniorityPlaceholder: "— select —",
            experienceYears: "Years of experience",
            geoScope: "Location scope",
            geoScopePlaceholder: "— select —",
            geoCountry: "Country",
            geoCountryPlaceholder: "e.g. France",
            geoRegion: "Region",
            geoRegionPlaceholder: "— select region —",
            geoCity: "City",
            geoCityPlaceholder: "e.g. Paris",
            geoSection: "Location",
            summary: "Summary",
            skills: "Skills",
          },
          experience: {
            any: "Any",
            "0_2": "0 to 2 years",
            "2_4": "2 to 4 years",
            "4_6": "4 to 6 years",
            "6_10": "6 to 10 years",
            "10_plus": "10+ years",
          },
          experienceSlider: {
            indifferent: "Any",
            anyValue: "Any",
            range: "{{min}} to {{max}} years",
          },
          geoScope: {
            city: "Same city",
            region: "Same region",
            country: "Same country",
            remote: "No limit (remote)",
          },
          skillsTiers: {
            required: "Required",
            optional: "Optional",
          },
          skillTierToggle: {
            markOptional: "Mark as optional",
            markRequired: "Mark as required",
          },
          skillReorderHint: "Drag tags to reorder by importance.",
          skillReorderAria: "Drag to reorder {{label}}",
          skillTypes: {
            technical: "Technical",
            functional: "Functional",
            business: "Business",
          },
          errors: {
            requiredSkillsEmpty: "At least one required skill is needed.",
            invalidSeniority: "Invalid seniority value.",
            invalidExperienceYears: "Invalid years-of-experience range.",
            invalidGeoScope: "Invalid location scope.",
            geoCityRequired: "A city is required when scope is set to \"Same city\".",
            geoRegionRequired: "A region is required when scope is set to \"Same region\".",
            geoCountryRequired: "A country is required when scope is set to \"Same country\".",
            validateFailed: "Could not save the criteria.",
            regionsLoadFailed: "Could not load regions for this country. You can retry below.",
          },
          regionsRetry: "Retry loading regions",
        },
        run: {
          runSearch: "Run search",
          startSearch: "Start search",
          runAgain: "Run again",
          successToast: "Search completed.",
          startedToast: "Search started. We'll notify you when results are ready.",
          completedToast: "Search completed. View candidates from the list.",
          failedToast: "Search failed. See the search detail for the reason.",
          rerunDialog: {
            title: "Re-run this search?",
            body:
              "This search has already completed. Re-running won't run a new profile search; it will only re-render the existing profiles.",
            keep: "Cancel",
            confirm: "Re-render",
            titleDirty: "Create a new search with these changes?",
            bodyDirty:
              "You've modified the criteria. We'll create a new search row with the updated criteria and run it. The original search and its candidates remain available in the list. This new run is a billable profile search.",
            confirmDirty: "Create & run",
          },
          errors: {
            generic: "Could not run the search.",
            authError: "Profile search authentication failed. Contact your administrator.",
            rateLimit: "Search rate limit reached. Try again in a few minutes.",
            notConfigured: "Profile search is not configured for this environment.",
            notValidated: "Validate the criteria before running the search.",
            billingExhausted: "Your profile credit plan is exhausted. Contact your administrator to top it up.",
          },
          failedReasons: {
            pdl_rate_limit: "Search failed: rate limit reached. Wait a few minutes and try again.",
            pdl_billing_exhausted: "Search failed: profile credits exhausted. Contact your administrator.",
            pdl_auth_error: "Search failed: profile search authentication error.",
            pdl_not_configured: "Search failed: profile search is not configured.",
          },
        },
        profiles: {
          empty: "No matching profiles found. Try loosening the criteria and re-running.",
          emptyV2:
            "We searched the locations and skills you selected and could not find profiles matching these criteria. Try widening the skills or location.",
          modalTitle: "Candidate profiles",
          viewCandidatesEmpty: "View candidates",
          notRunYet: "Run the search to load candidate profiles.",
        },
        batch: {
          legendTitle: "Loading batches:",
          item: "Batch {{number}}",
        },
        profileCard: {
          inDbBadge: "Already in PILOT",
          inDbTooltip: "This person is already a consultant in PILOT — click to open.",
          addToPilot: "Add to PILOT",
          addedToast: "Profile added to PILOT.",
          unknownName: "Unknown candidate",
          linkedin: "LinkedIn",
          errors: {
            addFailed: "Could not add this profile to PILOT.",
            alreadyMatched: "This profile is already linked to a consultant.",
          },
        },
        loadMore: {
          label: "Load 12 more",
          loadedCount: "{{count}} loaded",
          callsRemaining: "{{count}} extra calls left",
          exhausted:
            "All matching profiles loaded ({{count}}). No more profiles match these criteria.",
          successToast: "{{count}} new profiles added.",
          noMoreToast: "No new profiles found at the next page.",
          errorToast: "Could not load more profiles.",
          limitReachedToast:
            "Load-more limit reached for this search ({{cap}}). Start a new search to fetch more.",
          rebillDialog: {
            title: "Run another profile search?",
            body:
              "This will run another billable profile search to fetch the next 12 candidates.",
            cancel: "Cancel",
            confirm: "Continue",
          },
        },
        statsChart: {
          title: "Activity (last 6 months)",
          tooltip: "{{value}} searches",
        },
        export: {
          button: "Export Excel",
          errorTitle: "Export failed",
        },
      },
      documents: {
        title: "Documents",
        noDocuments: "No documents found.",
        table: {
          documentType: "Document Type",
          requestId: "Request ID",
          status: "Status",
          createdAt: "Created at",
          actions: "Actions",
        },
        download: "Download",
        downloading: "Downloading",
        errors: {
          fetchTitle: "Error fetching documents",
          fetchDescription: "Could not load documents.",
          downloadTitle: "Download failed",
          notReady: "The document is not ready for download yet.",
          forbidden: "You don't have permission to access this document.",
          notFound: "Document not found.",
          generic: "Could not download the file.",
        },
      },
      asapCV: {
        pageTitle: "ASAP CV",
        eyebrow: "ASAP CV",
        linkedConsultantBanner: "This CV will be linked to {{name}}",
        documentsTitle: "Documents",
        emptyHint: "Upload a CV from the form to generate documents.",
        searchPlaceholder: "Search by first/last name",
        pagination: {
          previous: "Previous",
          next: "Next",
          pageLabel: "Page {{page}}",
        },
        card: {
          unknownPosition: "Unknown position",
          ageLabel: "{{count}} years",
          ageNA: "N/A",
          updatedAt: "Updated: {{date}}",
          editNameTooltip: "Edit name",
          edit: {
            firstNamePlaceholder: "First name",
            lastNamePlaceholder: "Last name",
            saveAria: "Save",
            cancelAria: "Cancel",
          },
          deliverables: {
            originalCv: "Original formatted CV",
            aiCv: "CV formatted and improved by AI",
            oralGuide: "Oral guide",
            qa: "Questions & Answers",
            aoScoring: "Tender scoring",
          },
        },
        chart: {
          title: "Activity (last 6 months)",
          tooltip: "{{count}} CV",
          tooltip_plural: "{{count}} CVs",
        },
        alerts: {
          businessUnitRequiredTitle: "Business Unit required",
          businessUnitRequiredDescription:
            "Please select a Business Unit before submitting this request.",
          noBusinessUnitTitle: "No Business Unit available",
          noBusinessUnitDescription:
            "Your account must be attached to a Business Unit before you can submit this request. Contact an administrator.",
        },
        form: {
          panelTitle: "New CV request",
          uploadCvLabel: "Add one or more CVs",
          uploadCvDropText: "Drag & drop your CVs here or click to select",
          uploadJobDescLabel: "Tender document (optional)",
          uploadJobDescDropText:
            "Drag & drop the tender document here or click to select",
          acceptedFormats: "Accepted formats: PDF, DOCX",
          languageLabel: "Language",
          languageOptions: {
            fr: "French",
            en: "English",
          },
          jobTypeLabel: "Job type",
          jobTypePlaceholder: "Select",
          jobTypeOptions: {
            moa: "MOA / Business Analyst",
            "chef-de-projet": "Project Manager",
            "product-owner": "Product Owner / Scrum Master",
            "consultant-fonctionnel": "Functional Consultant",
            "consultant-technique": "Technical Consultant / Developer",
            "architecte-si": "IT Architect / Data Consultant",
            "expert-securite": "Security Expert / DevOps",
            pmo: "PMO",
            "finance-comptabilite": "Finance & Accounting",
            "rh-paie": "HR & Payroll",
            "supply-chain": "Supply Chain & Logistics",
            "marketing-commercial": "Marketing & Sales",
            "gestion-risques": "Risk Management & Compliance",
            "juridique-fiscalite": "Legal & Tax",
          },
          templateLabel: "CV template",
          deliverablesLabel: "Requested deliverables",
          deliverables: {
            cvOriginal: "Original formatted CV",
            cvVersionIA: "CV formatted and improved by AI.",
            scoringAppelOffre: "Tender scoring",
            guideOral: "Oral guide",
            questionsEntretiens: "Interview questions",
          },
          candidateEmailsLabel: "Candidate emails (comma-separated)",
          candidateEmailsPlaceholder: "email1@example.com, email2@example.com",
          submitLabel: "Send files",
          submitLoading: "Sending…",
          loadingConsultantCv: "Loading CV from consultant profile...",
        },
        toasts: {
          trialExpiredTitle: "Trial expired",
          trialExpiredDescription:
            "Your trial period has expired. Contact us to renew access.",
          fetchErrorTitle: "Error fetching documents",
          fetchErrorDescription: "Could not load documents.",
          downloadFailedTitle: "Download failed",
          cvAutoLoaded: "CV loaded from consultant profile",
          downloadErrors: {
            notReady: "The document is not ready for download yet.",
            forbidden: "You don't have permission to access this document.",
            notFound: "Document not found.",
            generic: "Could not download the file.",
          },
          nameEdit: {
            missingFields: "Fill in at least one of the fields",
            success: "Name updated",
            errorTitle: "Update failed",
            genericError: "Could not update the name.",
            emptyUpdate: "No field to update.",
            forbidden: "Not authorized for this Business Unit.",
            notFound: "Group not found.",
            trialExpired: "Your trial period has expired.",
          },
          submit: {
            noFiles: "Add at least one CV",
            noDeliverables: "Select at least one deliverable",
            invalidEmails: "Invalid emails: {{list}}",
            invalidFileTypeTitle: "Unsupported file type",
            invalidFileTypeDescription: "Accepted formats: PDF, DOCX.",
            invalidFileTypeDescriptionWithList:
              "Rejected files: {{list}}. Accepted formats: PDF, DOCX.",
            summary: "{{success}}/{{total}} files submitted successfully",
            errorTitle: "Error while sending",
          },
        },
      },
      asapSkills: {
        pageTitle: "ASAP Skills",
        eyebrow: "ASAP Skills",
        linkedConsultantBanner: "This evaluation will be linked to {{name}}",
        documentsTitle: "Assessments",
        searchPlaceholder: "Search by first/last name",
        noAssessments: "No assessments found.",
        emptyHint: "Send an assessment from the form to see it here.",
        pagination: {
          previous: "Previous",
          next: "Next",
          pageLabel: "Page {{page}}",
        },
        card: {
          status: {
            preparing: "Preparing",
            sent: "Sent",
            completed: "Completed",
            started: "Started",
            completedPendingScore: "Completed (scoring)",
            reportReady: "Report ready",
            notStarted: "Not started",
          },
          scoreTag: "Score: {{score}}/100",
          updatedAt: "Updated: {{date}}",
          noFiles: "No files",
          downloads: {
            assessmentJob: "Assessment job",
            assessmentReport: "Assessment report",
          },
        },
        chart: {
          title: "Activity (last 6 months)",
          tooltip: "{{count}} assessment",
          tooltip_plural: "{{count}} assessments",
        },
        alerts: {
          businessUnitRequiredTitle: "Business Unit required",
          businessUnitRequiredDescription:
            "Please select a Business Unit before submitting this request.",
          noBusinessUnitTitle: "No Business Unit available",
          noBusinessUnitDescription:
            "Your account must be attached to a Business Unit before you can submit this request. Contact an administrator.",
          maxSkillsReached:
            "Maximum limit reached. Deselect a skill to choose another one.",
          loadJobMatrixFailed: "Unable to load skills matrix",
        },
        form: {
          panelTitle: "New assessment",
          uploadJobDescLabel: "Job description",
          languageLabel: "Language",
          languageOptions: {
            fr: "French",
            en: "English",
          },
          uploadJobDescDropText:
            "Drag & drop the job description here or click to select",
          acceptedFormats: "Accepted formats: PDF, DOCX",
          evaluationSettingsTitle: "Evaluation settings",
          autoModeLabel: "Automatic mode",
          emptyMatrixHint:
            "Upload a job description to load suggested skills.",
          durationLabel: "Test duration (minutes)",
          durationMin: "Min: {{value}} min",
          durationSelected: "Selected: {{value}} min",
          durationMax: "Max: {{value}} min",
          durationHint:
            "Select skills to define the duration.",
          emailsSectionLabel: "Candidate(s)",
          candidateNamePlaceholder: "First name Last name",
          candidateEmailPlaceholder: "example@domain.com",
          removeCandidateAria: "Remove",
          addCandidateButton: "Add candidate",
          difficulty: {
            junior: "Junior (0–2 years)",
            confirme: "Confirmed (2–4 years)",
            senior: "Senior (4–7 years)",
            seniorConfirme: "Senior confirmed (7–10 years)",
            expert: "Expert (10+ years)",
          },
          importance: {
            label: "Importance",
            primary: "Primary",
            secondary: "Secondary",
            help: "Primary skills are the must-have requirements and are weighted more in the assessment. Secondary skills are nice-to-have and have less impact.",
            helpAria: "What do Primary and Secondary mean?",
          },
          addSkill: {
            button: "Add a skill",
            modalTitle: "Add a skill",
            nameLabel: "Skill name",
            namePlaceholder: "e.g. React, Scrum, Banking…",
            typeLabel: "Type",
            typeOptions: {
              technical: "Technical",
              functional: "Functional",
              business: "Business / Domain",
              role: "Role",
            },
            difficultyLabel: "Difficulty",
            cancel: "Cancel",
            save: "Save",
            missingFields: "Please fill skill name, type, and difficulty.",
            autoModeDisabledTitle: "Automatic mode disabled",
            autoModeDisabledDescription:
              "To add a skill, we switched to manual mode so your changes are sent.",
          },
          codingSkillTooltip: "This skill includes coding questions in the code editor.",
          sections: {
            technical: "Technical assessment",
            functional: "Functional assessment",
            business: "Business assessment",
            role: "Role",
          },
        },
        toasts: {
          fetchErrorTitle: "Error fetching documents",
          fetchErrorDescription: "Could not load documents.",
          trialExpiredTitle: "Trial expired",
          trialExpiredDescription:
            "Your trial period has expired. Contact us to renew access.",
          downloadFailedTitle: "Download failed",
          downloadErrors: {
            notReady: "The document is not ready for download yet.",
            forbidden: "You don't have permission to access this document.",
            notFound: "Document not found.",
            generic: "Could not download the file.",
          },
          loadJobMatrixFailed: "Unable to load skills matrix",
          invalidEmails: "Please enter valid email addresses",
          noCompetences:
            "Please select at least one skill to assess",
          noDetectedSkillsTitle: "No skill detected",
          noDetectedSkillsDescription:
            "Upload a job description to enable automatic mode.",
          invitationsSent: "Invitations sent",
          submitErrorTitle: "Error while sending",
        },
      },
      pilotNeeds: {
        list: {
          pageTitle: "Company Needs",
          eyebrow: "ASAP PILOT",
          emptyHint: "Create a need to start tracking client demand.",
          actions: {
            importFromFile: "Bulk Upload",
            upload: "Upload",
            create: "Create need",
            confirmDelete: "Confirm delete",
          },
          summary: {
            loading: "Loading...",
            total: "{{count}} total",
          },
          filters: {
            searchPlaceholder: "Search by company name or title...",
            clientPlaceholder: "Filter: Company",
            statusPlaceholder: "Filter: Status",
            salesPlaceholder: "Filter: Sales",
            recruiterPlaceholder: "Filter: Recruiter",
            pageSizeSuffix: "/page",
            dateRangeLabel: "Date Range",
            startDateRangeLabel: "Start Date Range",
            endDateRangeLabel: "End Date Range",
            from: "From",
            to: "to",
            categoryPlaceholder: "Filter: Category",
            minSalaryPlaceholder: "Min salary",
            maxSalaryPlaceholder: "Max salary",
            hasMatchesPlaceholder: "Filter: Matches",
            hasMatchesAny: "Any",
            hasMatchesWith: "With matches",
            hasMatchesWithout: "Without matches",
          },
          table: {
            date: "Date",
            title: "Title",
            client: "Company",
            skills: "Skills",
            status: "Status",
            category: "Category",
            acquisitionChannels: "Acquisition channel",
            contractTypes: "Contract type",
            salaryRange: "Salary (min-max)",
            startDate: "Start Date",
            endDate: "End Date",
            sales: "Sales",
            recruiter: "Recruiter",
            daysOpen: "Days Open",
            loading: "Loading...",
            noItems: "No needs found",
          },
          pagination: {
            first: "First",
            prev: "Prev",
            next: "Next",
            last: "Last",
            pageLabel: "Page {{page}} of {{total}}",
          },
        },
        detail: {
          backAria: "Back to needs",
          close: "Close",
          matchesPanel: {
            emptyHint: "Generate matches or add consultants to populate this need.",
          },
          tabs: {
            description: "Description",
            skills: "Skills",
            matches: "Matches",
            pairings: "Consultants",
            comments: "Comments",
          },
          pairings: {
            title: "Paired consultants",
            actions: {
              add: "Add consultants",
            },
            modal: {
              title: "Add consultants to this need",
              searchPlaceholder: "Search consultants...",
              selectedCount: "Selected: {{count}} / {{max}}",
              noConsultants: "No consultants found",
              total: "{{count}} consultants found",
              cancelButton: "Cancel",
              primaryButton: "Add",
              roleColumn: "Role",
            },
            toasts: {
              addSuccess: "{{count}} consultant added",
              addSuccess_plural: "{{count}} consultants added",
              addFailedTitle: "Could not add consultant(s)",
              addFailedDescription: "Please try again",
              loadConsultantsFailedTitle: "Failed to load consultants",
              loadConsultantsFailedDescription: "Please try again",
            },
          },
          toasts: {
            commentAdded: "Comment added",
            commentFailedTitle: "Could not add comment",
            commentFailedDescription: "Please try again",
          },
          notFound: "Need not found",
          loading: "Loading...",
          confirmDelete: "Are you sure you want to delete this need?",
          header: {
            edit: "Edit",
            delete: "Delete",
          },
          sections: {
            title: "Title",
            client: "Company",
            date: "Date",
            category: "Category",
            acquisitionChannels: "Acquisition channel",
            salaryRange: "Salary (min-max)",
            status: "Status",
            contractTypes: "Contract type",
            startDate: "Start Date",
            asap: "ASAP",
            endDate: "End Date",
            sales: "Sales",
            recruiter: "Recruiter(s)",
            contactPerson: "Contact Person",
            daysOpen: "Days Open",
            description: "Description",
            noDescription: "No description provided",
            technicalSkills: "Technical Skills",
            functionalSkills: "Functional Skills",
            businessSkills: "Business Skills",
            createdAt: "Created",
            updatedAt: "Last Updated",
          },
          comments: {
            title: "Comments",
            noComments: "No comments yet for this need.",
            actions: {
              submit: "Add comment",
              reply: "Reply",
              cancel: "Cancel",
            },
            placeholders: {
              new: "Write a comment…",
              reply: "Write a reply…",
            },
          },
        },
        form: {
          modalTitleCreate: "Create company need",
          modalTitleEdit: "Edit company need",
          sections: {
            general: "General information",
            people: "People",
            dates: "Dates",
            compensation: "Compensation",
            channels: "Channels & contract",
            skills: "Skills",
          },
          fields: {
            title: "Title",
            client: "Company",
            status: "Status",
            acquisitionChannels: "Acquisition channel",
            contractTypes: "Contract type",
            minSalary: "Min salary",
            maxSalary: "Max salary",
            startDate: "Start Date",
            asap: "ASAP (start as soon as possible)",
            endDate: "End Date",
            sales: "Sales",
            recruiter: "Recruiter(s)",
            contactPerson: "Contact Person",
            description: "Description",
            technicalSkills: "Technical Skills",
            functionalSkills: "Functional Skills",
            businessSkills: "Business Skills",
          },
          placeholders: {
            title: "e.g., Senior Data Engineer",
            contactPerson: "Select a contact...",
            minSalary: "e.g., 70000",
            maxSalary: "e.g., 90000",
            description: "Detailed description",
            technicalSkill: "Add technical skill",
            functionalSkill: "Add functional skill",
            businessSkill: "Add business skill",
          },
          buttons: {
            cancel: "Cancel",
            addSkill: "Add",
            saveChanges: "Save changes",
            create: "Create",
          },
        },
        importFromFile: {
          modalTitle: "Import needs from CSV/XLSX",
          description:
            "Upload a CSV or XLSX file. Supported columns include client, title, status, dates, salary range and contact information.",
          fields: {
            file: "File",
          },
          statusLabels: {
            total: "Total",
            added: "Added",
            updated: "Updated",
            deleted: "Deleted",
            duplicate: "Duplicates",
          },
          dropzone: {
            text: "Drag & drop CSV/XLSX here or click to select",
            accepted: "Accepted: .csv, .xlsx",
          },
          buttons: {
            cancel: "Cancel",
            import: "Import",
          },
          toasts: {
            importCompleted: "Import completed",
            importFailedTitle: "Import failed",
            importFailedDescription: "Please try again",
            invalidFileTypeTitle: "Invalid file type",
            invalidFileTypeDescription: "Only CSV or XLSX files are accepted",
          },
        },
        upload: {
          modalTitlePdf: "Import need from PDF",
          modalTitleFile: "Import need from file",
          descriptionPdf:
            "Import a PDF file containing job posting information. The system will extract the details and create a company need.",
          descriptionFile:
            "Import a PDF or DOCX file containing job posting information. The system will extract the details and create a company need.",
          clientLabel: "Company",
          clientPlaceholder: "Select company",
          fileLabel: "File",
          statusLabels: {
            total: "Total",
            added: "Added",
            updated: "Updated",
            deleted: "Deleted",
            duplicate: "Duplicates",
          },
          dropzonePdf: "Drag & drop PDF here or click to select",
          dropzoneFile: "Drag & drop PDF or DOCX here or click to select",
          acceptedPdf: "Accepted: .pdf",
          acceptedFile: "Accepted: .pdf, .docx",
          buttons: {
            cancel: "Cancel",
            upload: "Upload",
          },
        },
        createWizard: {
          stepIndicator: {
            document: "Document",
            details: "Details",
          },
          step1Title: "Create company need",
          step2Title: "Complete need details",
          step1Description:
            "You can upload a PDF or DOCX document to extract need details, or continue without uploading and fill in the form manually.",
          buttons: {
            continueWithoutUpload: "Continue without upload",
            continueWithDocument: "Continue with document",
            continueWithUpload: "Continue with upload",
            back: "Back",
          },
        },
        toasts: {
          loadListFailed: "Failed to load needs",
          loadDetailFailed: "Failed to load need",
          loadDetailRefreshFailed: "Failed to refresh need",
          deleteSuccess: "Need deleted",
          deleteFailedTitle: "Delete failed",
          deleteFailedDescription: "Please try again",
          confirmDeleteSuccess: "Need permanently deleted",
          confirmDeleteFailedTitle: "Confirmation failed",
          confirmDeleteFailedDescription: "Please try again",
          operationFailedTitle: "Operation failed",
          loadClientNeedsFailed: "Failed to load company needs",
          created: "Need created",
          updated: "Need updated",
          importCompleted: "Import completed",
          importFailedTitle: "Import failed",
          uploadCompleted: "Need imported successfully",
          uploadFailedTitle: "Import failed",
          invalidFileTypeTitle: "Invalid file type",
          invalidFileTypeDescriptionPdf: "Only PDF files are accepted",
          invalidFileTypeDescriptionFile:
            "Only PDF or DOCX files are accepted",
          commentAdded: "Comment added",
          commentFailedTitle: "Could not add comment",
          commentFailedDescription: "Please try again"
        },
      },
      pilotClients: {
        list: {
          pageTitle: "Companies",
          pageTitleContacts: "Clients",
          pageTitleStatus: "Company Status",
          pageTitleContactStatus: "Contact Status",
          eyebrow: "ASAP PILOT",
          emptyHint: "Create a company or contact to start building your client base.",
          view: {
            companies: "Companies",
            contacts: "Clients",
            companyStatus: "Company Status",
            contactStatus: "Contact Status",
          },
          actions: {
            import: "Bulk Upload",
            create: "Create company",
            createContactNoCompany: "Add contact (no company)",
            confirmDelete: "Confirm delete",
            delete: "Delete",
            approveAllDeletions_one: "Approve deletion ({{count}})",
            approveAllDeletions_other: "Approve all deletions ({{count}})",
          },
          deleteConfirm: {
            title: "Permanently delete this company?",
            intro: "This will permanently delete \"{{name}}\" and everything attached to it. This action cannot be undone.",
            summaryHeading: "The following will be removed:",
            company: "The company itself",
            needs_one: "{{count}} need",
            needs_other: "{{count}} needs",
            matches_one: "{{count}} match",
            matches_other: "{{count}} matches",
            contacts_one: "{{count}} contact",
            contacts_other: "{{count}} contacts",
            comments_one: "{{count}} comment",
            comments_other: "{{count}} comments",
            loadingImpact: "Loading what will be deleted...",
            impactError: "Could not load what will be deleted.",
            phrase: "delete all permanently",
            prompt: "Type \"{{phrase}}\" to confirm.",
            cancel: "Cancel",
            confirm: "Delete all permanently",
          },
          bulkDeleteConfirm: {
            title: "Approve all pending deletions?",
            intro_one: "{{count}} company is awaiting deletion approval. Approving will permanently delete it and everything attached to it. This action cannot be undone.",
            intro_other: "{{count}} companies are awaiting deletion approval. Approving will permanently delete them and everything attached to them. This action cannot be undone.",
            summaryHeading: "The following will be removed:",
            companies_one: "{{count}} company",
            companies_other: "{{count}} companies",
            needs_one: "{{count}} need",
            needs_other: "{{count}} needs",
            matches_one: "{{count}} match",
            matches_other: "{{count}} matches",
            contacts_one: "{{count}} contact",
            contacts_other: "{{count}} contacts",
            comments_one: "{{count}} comment",
            comments_other: "{{count}} comments",
            loadingImpact: "Loading what will be deleted...",
            impactError: "Could not load what will be deleted.",
            empty: "There are no pending deletions to approve.",
            phrase: "delete all permanently",
            prompt: "Type \"{{phrase}}\" to confirm.",
            cancel: "Cancel",
            confirm: "Delete all permanently",
          },
          filters: {
            searchPlaceholder: "Search",
            to: "to",
            sectorPlaceholder: "Filter: Sector",
            statusPlaceholder: "Filter: Status",
            locationPlaceholder: "Filter: Location",
            managerPlaceholder: "Filter: Account Owner / Sales Representative",
            clientCreatedFromPlaceholder: "Filter: Company created from",
            clientCreatedToPlaceholder: "Filter: Company created to",
            contactCreatedFromPlaceholder: "Filter: Contact created from",
            contactCreatedToPlaceholder: "Filter: Contact created to",
            clientCreatedLabel: "Company created",
            contactCreatedLabel: "Contact created",
            fromPlaceholder: "From",
            toPlaceholder: "To",
            pageSizeSuffix: "/page",
          },
          summary: {
            loading: "Loading...",
            total: "{{count}} total",
          },
          table: {
            contacts: "Contacts",
            name: "Company",
            description: "Description",
            sector: "Sector",
            status: "Status",
            location: "Location",
            manager: "Account Owner / Sales Representative",
            actions: "Actions",
            contactName: "Contact",
            contactFunction: "Function",
            contactDepartment: "Department",
            contactStatus: "Status",
            contactEmail: "Email",
            contactPhone: "Phone",
            companyName: "Company",
            companySector: "Sector",
            companyLocation: "Location",
            companyManager: "Account Owner / Sales Representative",
            noItems: "No companies found",
            noContacts: "No contacts found",
          },
          statusView: {
            empty: "No companies in this status.",
          },
          contactStatusView: {
            empty: "No contacts in this status.",
          },
          contactsToggle: {
            show: "Show contacts",
            hide: "Hide contacts",
          },
          contactsTable: {
            name: "Name",
            status: "Status",
            email: "Email",
            phone: "Phone",
            noContacts: "No contacts found for this company.",
          },
          pagination: {
            first: "First",
            prev: "Prev",
            next: "Next",
            last: "Last",
            pageLabel: "Page {{page}} of {{total}}",
          },
          toasts: {
            loadClientsFailed: "Failed to load companies",
            deleteClientSuccess: "Company deleted",
            deleteFailedTitle: "Delete failed",
            deleteFailedDescription: "Please try again",
            loadContactsFailed: "Failed to load contacts",
            confirmDeleteSuccess: "Company permanently deleted",
            confirmDeleteFailedTitle: "Confirmation failed",
            confirmDeleteFailedDescription: "Please try again",
            bulkDeleteSuccess_one: "{{count}} company permanently deleted",
            bulkDeleteSuccess_other: "{{count}} companies permanently deleted",
            bulkDeleteFailedTitle: "Approval failed",
            bulkDeleteFailedDescription: "Please try again",
            updateStatusSuccess: "Company status updated",
            updateStatusFailedTitle: "Status update failed",
            updateStatusFailedDescription: "Please try again",
            updateContactStatusSuccess: "Contact status updated",
            updateContactStatusFailedTitle: "Status update failed",
            updateContactStatusFailedDescription: "Please try again",
          },
        },
        detail: {
          backAria: "Back to companies",
          titleFallback: "Company",
          close: "Close",
          tabs: {
            description: "Description",
            contacts: "Contacts",
            needs: "Company Needs",
            comments: "Comments",
          },
          companyInfo: {
            description: "Description",
            sector: "Sector",
            status: "Status",
            location: "Location",
            manager: "Account Owner / Sales Representative",
            numberOfEmployees: "Number of employees",
            linkedinUrl: "LinkedIn URL",
            notFound: "Company not found.",
            skillsTechnical: "Technical skills",
            skillsFunctional: "Functional skills",
            skillsBusiness: "Business skills",
          },
          actions: {
            blacklist: "Blacklist",
            unblacklist: "Remove Blacklist",
          },
          topSkills: {
            title: "Key skills",
            searchPlaceholder: "Search skills...",
            noResults: "No skills match your search.",
          },
          contacts: {
            title: "Contact persons",
            addButton: "Add contact",
            table: {
              name: "Name",
              title: "Title / Position",
              division: "Division / Department",
              status: "Status",
              email: "Email",
              phone: "Phone",
              linkedin: "LinkedIn",
              actions: "Actions",
            },
            noContacts: "No contacts found for this company.",
            inactiveBadge: "Inactive",
          },
          needs: {
            title: "Company needs",
            filters: {
              searchPlaceholder: "Search needs...",
              statusPlaceholder: "Filter: Status",
              pageSizeSuffix: "/page",
            },
            summary: {
              loading: "Loading...",
              total: "{{count}} total",
            },
            table: {
              title: "Title",
              status: "Status",
              owner: "Owner",
              created: "Created",
              updated: "Updated",
              link: "Link",
              viewLink: "View",
              noNeeds: "No needs found for this company.",
            },
            pagination: {
              first: "First",
              prev: "Prev",
              next: "Next",
              last: "Last",
              pageLabel: "Page {{page}} of {{total}}",
            },
          },
          comments: {
            title: "Comments",
            noComments: "No comments yet for this company.",
            actions: {
              submit: "Add comment",
              reply: "Reply",
              cancel: "Cancel",
            },
            placeholders: {
              new: "Write a comment…",
              reply: "Write a reply…",
            },
          },
          toasts: {
            loadClientFailed: "Failed to load company",
            loadNeedsFailed: "Failed to load company needs",
            loadContactsFailed: "Failed to load contacts",
            contactDeleted: "Contact deleted",
            deleteFailedTitle: "Delete failed",
            deleteFailedDescription: "Please try again",
            commentAdded: "Comment added",
            commentFailedTitle: "Could not add comment",
            commentFailedDescription: "Please try again",
            blacklisted: "Company blacklisted",
            blacklistFailed: "Failed to blacklist company",
            unblacklisted: "Blacklist removed",
            unblacklistFailed: "Failed to remove blacklist",
          },
          confirmDeleteContact: "Are you sure you want to delete this contact?",
          confirmDeleteContactTitle: "Delete contact?",
        },
        form: {
          modalTitleCreate: "Create company",
          modalTitleEdit: "Edit company",
          fields: {
            name: "Name",
            description: "Description",
            sector: "Sector",
            status: "Status",
            location: "Location",
            manager: "Account Owner / Sales Representative",
            numberOfEmployees: "Number of employees",
            linkedinUrl: "LinkedIn URL",
          },
          placeholders: {
            name: "Company name",
            sector: "e.g., Retail",
            location: "e.g., Paris",
            numberOfEmployees: "e.g., 250",
            linkedinUrl: "https://www.linkedin.com/company/...",
            description: "Short description",
          },
          buttons: {
            cancel: "Cancel",
            saveChanges: "Save changes",
            create: "Create",
          },
          toasts: {
            created: "Company created",
            updated: "Company updated",
            operationFailedTitle: "Operation failed",
            operationFailedDescription: "Please try again",
          },
          duplicate: {
            title: "Duplicate company",
            body: 'A company named "{{name}}" already exists.',
            prompt: "Would you like to view the existing company?",
            cancel: "Cancel",
            view: "View existing",
          },
        },
        importModal: {
          title: "Import companies from CSV/XLSX",
          description:
            "Supported columns (case-insensitive): name, description, sector, status, location, contact_name, contact_email, contact_phone, manager_email",
          fields: {
            file: "File",
            defaultManager: "Default account owner / sales rep (optional)",
          },
          statusLabels: {
            total: "Total",
            added: "Added",
            updated: "Updated",
            deleted: "Deleted",
            duplicate: "Duplicates",
          },
          dropzone: {
            text: "Drag & drop CSV/XLSX here or click to select",
            accepted: "Accepted: .csv, .xlsx",
          },
          buttons: {
            cancel: "Cancel",
            import: "Import",
          },
          toasts: {
            importCompleted: "Import completed",
            importFailedTitle: "Import failed",
          },
        },
        contactsWizard: {
          modalTitle: "Add contacts {{clientName}}",
          intro:
            "Your company has been created. You can now add one or multiple contact persons.",
          header: "Contacts",
          addContactButton: "Add contact",
          table: {
            name: "Name",
            email: "Email",
            phone: "Phone",
            noContacts:
              'No contacts yet. Click "Add contact" to create one.',
          },
          buttons: {
            skip: "Skip for now",
            finish: "Finish",
          },
          toasts: {
            loadContactsFailed: "Failed to load contacts",
          },
        },
        contactsForm: {
          modalTitleCreate: "Create contact",
          modalTitleEdit: "Edit contact",
          fields: {
            name: "Name",
            status: "Status",
            email: "Email",
            phone: "Phone",
            linkedinUrl: "LinkedIn URL",
            title: "Title / Position held",
            division: "Division / Department",
            comments: "Comments",
            active: "Active",
          },
          placeholders: {
            name: "Contact name",
            status: "Select status",
            email: "email@example.com",
            phone: "+33 ...",
            linkedinUrl: "https://www.linkedin.com/in/...",
            title: "e.g., Head of Trading",
            division: "e.g., Finance",
            comments: "Notes, context, preferred communication, etc.",
          },
          buttons: {
            cancel: "Cancel",
            saveChanges: "Save changes",
            create: "Create",
          },
          toasts: {
            created: "Contact created",
            updated: "Contact updated",
            operationFailedTitle: "Operation failed",
            operationFailedDescription: "Please try again",
          },
        },
      },
      pilotConsultants: {
        list: {
          pageTitle: "Consultants",
          eyebrow: "ASAP PILOT",
          emptyHint: "Create or import a consultant to start building your pool.",
          actions: {
            create: "Create consultant",
            importFromCv: "Upload",
            importFromFile: "Bulk Upload",
            confirmDelete: "Confirm delete",
            bulkUploadCvs: "Bulk Upload CVs",
          },
          filters: {
            searchPlaceholder: "Search by consultant, company or need...",
            clientPlaceholder: "Filter: Company",
            needPlaceholder: "Filter: Need",
            statusPlaceholder: "Filter: Status",
            staffingStatusPlaceholder: "Filter: Staffing status",
            availabilityPlaceholder: "Filter: Availability",
            locationPlaceholder: "Filter: Location",
            categoryPlaceholder: "Filter: Category",
            minSalaryPlaceholder: "Min salary",
            maxSalaryPlaceholder: "Max salary",
            pageSizeSuffix: "/page",
          },
          viewSwitcher: {
            ariaLabel: "Data view",
            allConsultants: "All Consultants",
            byStatus: "By Status",
          },
          summary: {
            loading: "Loading...",
            total: "{{count}} total",
          },
          table: {
            name: "Name",
            title: "Title",
            category: "Category",
            salaryRange: "Salary (min-max)",
            skills: "Skills",
            client: "Company",
            need: "Need",
            availability: "Availability",
            status: "Status",
            staffingStatus: "Staffing status",
            location: "Location",
            email: "Email",
            portal: "Portal",
            actions: "Actions",
            noItems: "No consultants found",
          },
          pagination: {
            first: "First",
            prev: "Prev",
            next: "Next",
            last: "Last",
            pageLabel: "Page {{page}} of {{total}}",
          },
          confirmDelete: "Are you sure you want to delete this consultant?",
          toasts: {
            loadConsultantsFailed: "Failed to load consultants",
            deleteConsultantSuccess: "Consultant deleted",
            deleteFailedTitle: "Delete failed",
            deleteFailedDescription: "Please try again",
            confirmDeleteSuccess: "Consultant permanently deleted",
            confirmDeleteFailedTitle: "Confirmation failed",
            confirmDeleteFailedDescription: "Please try again",
            invalidFileType: "Some files were skipped (only PDF and DOCX are accepted).",
            bulkCvError: "Failed to upload CVs.",
            updateStaffingStatusSuccess: "Staffing status updated",
            updateStaffingStatusFailed: "Failed to update status",
          },
          bulkCvModal: {
            title: "Bulk Upload CVs",
            description: "Upload up to 20 CV files (PDF or DOCX) to create consultant profiles automatically.",
            dropzone: "Drag and drop CV files here, or click to browse",
            accepted: "PDF, DOCX, max 20 files",
            upload: "Upload",
            createdTitle: "consultant(s) created successfully",
            errorsTitle: "file(s) could not be processed",
            duplicateMessage: "A consultant named \"{{name}}\" already exists",
            viewExisting: "View Existing",
          },
        },
        importModal: {
          title: "Create consultant from CV",
          description:
            "Upload a CV (PDF or DOC/DOCX). The system will extract the details and create a consultant profile.",
          fields: {
            file: "CV file",
          },
          statusLabels: {
            total: "Total",
            added: "Added",
            updated: "Updated",
            deleted: "Deleted",
            duplicate: "Duplicates",
          },
          dropzone: {
            text: "Drag & drop a CV here or click to select",
            accepted: "Accepted: .pdf, .doc, .docx",
          },
          buttons: {
            cancel: "Cancel",
            import: "Upload",
          },
          toasts: {
            importCompleted: "Consultant created from CV",
            importFailedTitle: "Import failed",
            importFailedDescription: "Please try again",
          },
        },
        createWizard: {
          stepLabels: {
            upload: "Upload CV",
            review: "Review details",
          },
          step1Title: "Create consultant",
          step2Title: "Complete consultant details",
          step1Description:
            "You can upload a CV (PDF or DOC/DOCX) to create a consultant profile from the document, or continue without uploading and fill in the form manually.",
          buttons: {
            continueWithoutUpload: "Continue without upload",
            continueWithUpload: "Continue with upload",
            back: "Back",
          },
        },
        detail: {
          backAria: "Back to consultants",
          close: "Close",
          titleFallback: "Consultant",
          notFound: "Consultant not found",
          header: {
            edit: "Edit",
            delete: "Delete",
            downloadCv: "Download CV",
          },
          sections: {
            title: "Title",
            email: "Email",
            phone: "Phone",
            location: "Location",
            availability: "Availability",
            contractType: "Contract type",
            employmentStartDate: "Employment start date",
            status: "Status",
            staffingStatus: "Staffing status",
            category: "Category",
            salaryRange: "Salary (min-max)",
          description: "Description",
          technicalSkills: "Technical Skills",
          functionalSkills: "Functional Skills",
          businessSkills: "Business Skills",
            createdAt: "Created",
            updatedAt: "Last updated",
          },
          cv: {
            title: "CV",
            description: "Upload a CV for this consultant and access generated CV documents.",
            fields: {
              cvFile: "CV file",
              jobDescFile: "Tender / job description (optional)",
              language: "Language",
              deliverables: "Requested deliverables",
            },
            actions: {
              selectCv: "Select CV file",
              selectJobDesc: "Select job description",
              submit: "Send CV",
            },
            documentsTitle: "CV documents",
          },
          skills: {
            title: "Skills assessments",
            description: "Send an assessment request for this consultant and view past assessments.",
            fields: {
              candidateName: "Candidate name",
              candidateEmail: "Candidate email",
              language: "Language",
              jobDescFile: "Job description (optional)",
            },
            actions: {
              selectJobDesc: "Select job description",
              submit: "Send assessment",
            },
            assessmentsTitle: "Assessments",
          },
          comments: {
            title: "Comments",
            noComments: "No comments yet for this consultant.",
            addLabel: "Add a comment",
            actions: {
              submit: "Add comment",
              reply: "Reply",
              cancel: "Cancel",
            },
            placeholders: {
              new: "Write a comment…",
              reply: "Write a reply…",
            },
          },
          tabs: {
            profile: "Profile",
            about: "About",
            conversations: "Conversations",
            cvPortfolio: "CV Portfolio",
            asapSkills: "ASAP SKILLS",
            assignments: "Assignments",
            cv: "CV",
            assessment: "Assessment",
            comments: "Comments",
            matches: "Matches",
            candidateFollowUp: "Candidate Follow-up",
            tracking: "Tracking",
          },
          reverseMatch: {
            title: "Reverse Match",
            description: "Find the best open needs for this consultant",
            search: "Reverse Match",
            lastSearched: "Last searched",
            suggest: "Suggest",
            suggestSuccess: "Suggestion sent to sales rep",
            suggestError: "Failed to send suggestion",
            error: "Reverse match failed",
            actions: "Actions",
          },
          followUp: {
            monthlyRatings: {
              title: "Monthly Ratings",
              noRatings: "No monthly ratings yet.",
              add: "Add rating",
              period: "Period",
              rating: "Rating",
              comment: "Comment",
              save: "Save",
              periodYear: "Year",
              periodMonth: "Month",
              editTitle: "Edit rating",
              selectRating: "Please select a rating",
            },
            annualReviews: {
              title: "Annual Reviews",
              noReviews: "No annual reviews yet.",
              add: "Add review",
              year: "Year",
              type: "Type",
              notes: "Notes",
              reviewer: "Reviewer",
              save: "Save",
              editTitle: "Edit review",
              types: {
                annual: "Annual",
                salary: "Salary",
                client_feedback: "Client Feedback",
              },
            },
            specialRequests: {
              title: "Special Requests",
              comingSoon: "Coming soon",
            },
            activity: {
              title: "Activity reports",
              empty: "No activity reports yet.",
              period: "Period",
              days: "Days",
              status: "Status",
              review: "Review",
            },
            leave: {
              title: "Leave requests",
              empty: "No leave requests yet.",
              type: "Type",
              dates: "Dates",
              status: "Status",
              review: "Review",
            },
            feedback: {
              title: "Assignment feedback",
              empty: "No assignment feedback yet.",
              noPermission: "You do not have permission to review assignment feedback.",
              client: "Client",
              assignment: "Assignment",
              cycle: "Cycle",
              rating: "Rating",
              status: "Status",
              comment: "Comment",
            },
          },
          conversations: {
            title: "Conversations",
            noConversations: "No conversations yet.",
            unknownAuthor: "Unknown",
            actions: {
              add: "Add interaction",
              save: "Save",
              edit: "Edit",
              delete: "Delete",
            },
            placeholders: {
              content: "Describe the interaction...",
            },
            confirmDeleteTitle: "Delete interaction?",
            confirmDeleteBody: "This action cannot be undone.",
            toasts: {
              fetchFailed: "Failed to load conversations",
              created: "Interaction added",
              createFailed: "Could not add interaction",
              updated: "Interaction updated",
              updateFailed: "Could not update interaction",
              deleted: "Interaction deleted",
              deleteFailed: "Could not delete interaction",
            },
          },
          matches: {
            title: "Matches",
            description: "All matches for this consultant.",
            noMatches: "No matches found.",
            filterAll: "All matches",
            filterSubmitted: "Submitted to client",
            generateMatches: "Generate matches",
            generateError: "Failed to generate matches",
            lastGenerated: "Last generated",
          },
          consultantPortal: {
            sectionLabel: "Consultant portal",
            portalAccessHeading: "Portal access",
            lastInvite: "Last invite: {{date}}",
            inviteButton: "Invite",
            resendInviteButton: "Resend invite",
            disableAccessButton: "Disable access",
            statusLabels: {
              active: "Active",
              invited: "Invited",
              disabled: "Disabled",
              inactive: "Inactive",
            },
            toasts: {
              inviteSent: "Consultant invite sent",
              inviteResent: "Consultant invite resent",
              accessDisabled: "Consultant portal access disabled",
              actionFailedTitle: "Consultant portal action failed",
              actionFailedDescription: "Please try again.",
            },
          },
          confirmDeleteTitle: "Delete consultant?",
          asapSkills: {
            noData: "No skills data available.",
            skillName: "Skill",
            skillScore: "Score",
            skillProgress: "Progress",
            evaluationCount: "Evaluations",
            averageScore: "Average Score",
            trend: "Trend",
            goToSkills: "Go to ASAP SKILLS",
            evaluationHistory: "Evaluation History",
            noEvaluations: "No completed evaluations yet.",
            noSkillBreakdown: "No detailed skill breakdown available.",
          },
          assignments: {
            loadError: "Failed to load assignments. Please try again.",
            noData: "No assignment data available.",
            totalExperienceLabel: "Total experience",
            yearsInCompanyLabel: "Within the company",
            yearsValue: "{{years}} years",
            noExperienceData: "Experience data not set.",
            historyTitle: "Assignment History",
            noAssignments: "No assignments found.",
            current: "Current",
            ongoing: "Ongoing",
          },
          review: {
            approve: "Approve",
            reject: "Reject",
            approveTitle: "Approve request",
            rejectTitle: "Reject request",
            commentHint: "Optionally add a comment or note for this review.",
            commentPlaceholder: "Comment (optional)",
            toasts: {
              approved: "Review approved",
              rejected: "Review rejected",
              failedTitle: "Review action failed",
              failedDescription: "Please try again.",
            },
          },
          skillsPortfolio: {
            sourceCvs: "Source CV",
            storedCv: "Stored CV",
            sendCv: "Send a CV",
            downloadSourceCv: "Download source CV",
            noSourceCvs: "No source CV uploaded yet.",
            generatedCvs: "Generated CVs",
            moduleNotEnabled: "CV module not enabled for this business unit.",
            noGeneratedCvs: "No processed CVs yet.",
          },
          confirmDeleteConsultant: "Are you sure you want to delete this consultant?",
          toasts: {
            loadConsultantFailed: "Failed to load consultant",
            deleteConsultantSuccess: "Consultant deleted",
            deleteFailedTitle: "Delete failed",
            deleteFailedDescription: "Please try again",
            commentAdded: "Comment added",
            commentFailedTitle: "Could not add comment",
            commentFailedDescription: "Please try again",
            cvSubmitSuccess: "CV submitted",
            cvSubmitFailedTitle: "CV submission failed",
            cvSubmitFailedDescription: "Please try again",
            assessmentSubmitSuccess: "Assessment request sent",
            assessmentSubmitFailedTitle: "Assessment request failed",
            assessmentSubmitFailedDescription: "Please try again",
          },
        },
        form: {
          modalTitleCreate: "Create consultant",
          modalTitleEdit: "Edit consultant",
          fields: {
            name: "Name",
            title: "Title",
            email: "Email",
            phone: "Phone",
            location: "Location",
            availability: "Availability",
            status: "Status",
            staffingStatus: "Staffing status",
            description: "Description",
            comments: "Comments",
            technicalSkills: "Technical Skills",
            functionalSkills: "Functional Skills",
            businessSkills: "Business Skills",
            minSalary: "Min salary",
            maxSalary: "Max salary",
          },
          placeholders: {
            name: "Consultant name",
            title: "e.g., Senior Consultant",
            email: "email@example.com",
            phone: "Phone number",
            location: "e.g., Paris",
            availability: "e.g., Immediately, 1 month",
            status: "e.g., Active, Bench",
            staffingStatus: "e.g., Internal, Talent pool, Inactive",
            description: "Public description",
            comments: "Internal notes about this consultant",
            technicalSkill: "Add technical skill",
            functionalSkill: "Add functional skill",
            businessSkill: "Add business skill",
            minSalary: "e.g., 70000",
            maxSalary: "e.g., 90000",
          },
          buttons: {
            cancel: "Cancel",
            saveChanges: "Save changes",
            create: "Create",
            addSkill: "Add",
          },
          toasts: {
            created: "Consultant created",
            updated: "Consultant updated",
            operationFailedTitle: "Operation failed",
            operationFailedDescription: "Please try again",
          },
        },
        importFromFile: {
          modalTitle: "Import consultants from CSV/XLSX",
          description:
            "Upload a CSV or XLSX file. Supported columns include name, title, email, availability, status, location, phone and technical/functional/business skills.",
          fields: {
            file: "File",
          },
          statusLabels: {
            total: "Total",
            added: "Added",
            updated: "Updated",
            deleted: "Deleted",
            duplicate: "Duplicates",
          },
          dropzone: {
            text: "Drag & drop CSV/XLSX here or click to select",
            accepted: "Accepted: .csv, .xlsx",
          },
          buttons: {
            cancel: "Cancel",
            import: "Import",
          },
          toasts: {
            importTooLargeTitle: "Import too large",
            importTooLargeDescription:
              "This file contains more than 500 data rows. Please split it and try again.",
          },
        },
      },
      pilotConsultantTracking: {
        list: {
          pageTitle: "Tracking",
          eyebrow: "ASAP Pilot",
          panelTitle: "Consultant Tracking",
          filters: {
            searchPlaceholder: "Search consultant…",
            pageSizeSuffix: "/page",
            toggle: "Filters",
            reset: "Reset filters",
            contractTypeLabel: "Status (contract type)",
            contractTypeAll: "All contract types",
            clientLabel: "Current client",
            clientAll: "All clients",
            nextMissionLabel: "Next mission",
            nextMissionAll: "All",
            nextMissionYes: "Has next mission",
            nextMissionNo: "No next mission",
            missionEndRangeLabel: "Mission end date",
            poEndRangeLabel: "Purchase order end date",
            benchDaysRangeLabel: "Bench days (permanent only)",
            clientScoreRangeLabel: "Avg client score",
            from: "From",
            to: "to",
            missionEndFromLabel: "Mission end date from",
            missionEndToLabel: "Mission end date to",
            poEndFromLabel: "PO end date from",
            poEndToLabel: "PO end date to",
            benchDaysMinLabel: "Min bench days (permanent only)",
            clientScoreMaxLabel: "Max avg client score",
          },
          summary: {
            loading: "Loading...",
            total: "{{count}} total",
          },
          table: {
            name: "Name",
            status: "Status",
            client: "Client",
            assignmentEndDate: "Assignment end date",
            purchaseOrderEndDate: "Purchase order end date",
            cumulativeDaysWithClient: "Days with client",
            numberOfClients: "Clients in company",
            cumulativeDaysWithCompany: "Days in company",
            benchDays: "Bench days",
            nextAssignment: "Next assignment",
            averageClientScore: "Avg. client score",
            noItems: "No consultants found",
            noItemsHint: "Adjust the filters or add consultants to see tracking data.",
          },
          contractTypes: {
            permanent_contract: "Permanent contract",
            freelance: "Freelance",
            subcontracting: "Subcontracting",
            umbrella_company: "Umbrella company"
          },
          badges: {
            available: "Available",
            onMission: "On assignment",
            nextConfirmed: "Yes",
            noNextConfirmed: "No",
            salaryReviewThisMonth: "This month",
            noSalaryReviewThisMonth: "No",
            salaryReviewDay: "Day {{day}}",
          },
          pagination: {
            first: "First",
            prev: "Prev",
            next: "Next",
            last: "Last",
            pageLabel: "Page {{page}} of {{total}}",
          },
        },
        detail: {
          backAria: "Back to tracking",
          titleFallback: "Consultant",
          actions: {
            back: "Back",
          },
          rangePanelTitle: "Tracking range",
          assignmentsPanelTitle: "Assignments",
          emptyHint: "Adjust the date range or check back later for timeline data.",
          kpis: {
            availability: "Availability",
            nextAssignment: "Next assignment",
            salaryReview: "Salary review",
            months: "Months tracked",
          },
          summary: {
            loading: "Loading...",
            range: "Range: {{start}} → {{end}}",
          },
          empty: "No timeline data",
          filters: {
            fromDate: "From",
            toDate: "To",
            refresh: "Refresh",
          },
          chart: {
            title: "Timeline (months)",
            tooltip:
              "{{month}} — {{count}} assignment(s) — {{status}} — Salary review: {{salaryReview}}",
            selectedMonth: "Selected: {{month}}",
          },
          table: {
            month: "Month",
            isAvailable: "Availability",
            assignments: "Assignments",
            noAssignments: "No assignments",
          },
          badges: {
            po: "PO: {{status}}",
            satisfaction: "CSAT: {{score}}",
          },
          fields: {
            dates: "{{start}} → {{end}}",
          },
          actions: {
            editTracking: "Edit tracking",
            viewConsultant: "View consultant",
          },
          modal: {
            title: "Update assignment tracking",
            actions: {
              cancel: "Cancel",
              save: "Save",
            },
            fields: {
              empty: "—",
              assignmentStatus: "Assignment status",
              assignmentStartDate: "Assignment start date",
              assignmentEndDate: "Assignment end date",
              purchaseOrderStatus: "Purchase order status",
              purchaseOrderNumber: "Purchase order number",
              purchaseOrderReceivedAt: "PO received at",
              clientSatisfactionScore: "Company satisfaction score",
              clientSatisfactionComment: "Company satisfaction comment",
            },
          },
          toasts: {
            updateMatchSuccess: "Tracking updated",
            updateMatchFailed: "Update failed",
          },
        },
      },
      pilotMatches: {
        list: {
          pageTitle: "Matches",
          eyebrow: "ASAP PILOT",
          emptyHint: "Generate matches to position consultants against client needs.",
          viewSwitcher: {
            ariaLabel: "Data view",
            byNeed: "By Need",
            byConsultant: "By Consultant",
          },
          deleteDialog: {
            title: "Delete match",
          },
          filters: {
            searchPlaceholder: "Search by need title...",
            searchConsultantPlaceholder: "Search by consultant name...",
            clientPlaceholder: "Filter: Company",
            statusPlaceholder: "Filter: Status",
            categoryPlaceholder: "Filter: Category",
            hasMatchesPlaceholder: "Filter: Matches",
            hasMatchesWith: "With matches",
            hasMatchesWithout: "No matches",
          },
          summary: {
            loading: "Loading...",
            total: "{{count}} total needs",
            consultantCount: "{{count}} consultant(s)",
          },
          table: {
            matchesCount: "Matches",
            openDuration: "Need open duration (days)",
            suggestedProfiles: "Suggested profiles",
            positionedProfiles: "Positioned profiles",
            actions: "Actions",
            noItems: "No needs found",
          },
          matchTable: {
            consultant: "Consultant",
            bestScore: "Best Score",
            won: "won",
            submitted: "Submitted",
          },
          expand: {
            show: "Show matches",
            hide: "Hide matches",
          },
          actions: {
            generateMatches: "Generate matches",
            regenerateMatches: "Regenerate matches",
            selectConsultants: "Select consultants...",
          },
          selectModal: {
            title: "Select consultants for this need",
            searchPlaceholder: "Search consultants...",
            selectedCount: "Selected: {{count}} / {{max}}",
            noConsultants: "No consultants found",
            total: "{{count}} consultants found",
            cancelButton: "Cancel",
            primaryButton: "Generate matches",
            modeAuto: "Use all consultants",
            modeManual: "Select consultants manually",
            roleColumn: "Role",
          },
          pagination: {
            first: "First",
            prev: "Prev",
            next: "Next",
            last: "Last",
            pageLabel: "Page {{page}} of {{total}}",
          },
        },
        detail: {
          pageTitle: "Match #{{id}}",
          back: "Back to matches",
          backAria: "Back to matches",
          scoreBadge: "Score {{score}}",
          notFoundHint: "This match may have been deleted or is unavailable.",
          notFound: "Match not found",
          confirmDelete: "Are you sure you want to delete this match?",
          confirmDeleteTitle: "Delete match?",
          sections: {
            overview: "Overview",
            reasoning: "Reasoning",
            needInfo: "Need",
            consultantInfo: "Consultant",
          },
          workflow: {
            title: "Workflow Pipeline",
            columnTitle: "Workflow",
            clickToRevert: "Click to revert (requires confirmation)",
            clickToAdvance: "Click to advance",
            withdraw: "Withdraw",
            markRejected: "Mark Rejected",
            backwardConfirm: {
              title: "Revert workflow step?",
              body: "Moving backward in the workflow pipeline will reset the match to an earlier stage. Are you sure?",
            },
            toasts: {
              transitionSuccess: "Workflow updated",
              transitionFailedTitle: "Transition failed",
              transitionFailedDescription: "Could not update workflow status.",
            },
          },
          missionTracking: {
            title: "Assignment",
            description:
              "Update assignment status and dates for this match. You can also track purchase order and company satisfaction.",
            fields: {
              empty: "—",
              assignmentStatus: "Assignment status",
              assignmentStartDate: "Assignment start date",
              assignmentEndDate: "Assignment end date",
              purchaseOrderStatus: "Purchase order status",
              purchaseOrderNumber: "Purchase order number",
              purchaseOrderReceivedAt: "PO received at",
              clientSatisfactionScore: "Company satisfaction score",
              clientSatisfactionComment: "Company satisfaction comment",
              dailyRate: "Daily rate (\u20ac)",
              costRate: "Cost rate (\u20ac)",
              billableDaysTarget: "Billable days target",
            },
            actions: {
              open: "Edit assignment",
              close: "Close",
              save: "Save",
            },
            toasts: {
              saveSuccess: "Assignment tracking updated",
              saveFailedTitle: "Update failed",
              saveFailedDescription: "Please try again",
            },
          },
          interviews: {
            title: "Interviews",
            description:
              "Track scheduled and completed interviews for this match.",
            fields: {
              empty: "—",
              status: "Status",
              scheduledAt: "Scheduled at",
              completedAt: "Completed at",
            },
            actions: {
              add: "Add interview",
              edit: "Edit",
              close: "Close",
              save: "Save",
            },
            toasts: {
              loadFailedTitle: "Failed to load interviews",
              saveSuccess: "Interview saved",
              saveFailedTitle: "Save failed",
              saveFailedDescription: "Please try again",
              missingTime: "Provide at least one date/time.",
              nothingToUpdate: "Please update at least one field.",
            },
            emptyState: "No interviews recorded yet.",
          },
          fields: {
            id: "ID",
            needId: "Need ID",
            consultantId: "Consultant ID",
            category: "Category",
            score: "Score",
            source: "Source",
            companyId: "Company ID",
            businessUnitId: "Business Unit ID",
            atsScore: "ATS score",
            skillsScore: "Skills score",
          },
          table: {
            consultant: "Consultant",
            category: "Category",
            score: "Score",
            source: "Source",
            reasoning: "Reasoning",
            positioned: "Positioned",
            actions: "Actions",
            workflowStatus: "Workflow",
          },
          positioned: {
            yes: "Yes",
            no: "No",
          },
          actions: {
            delete: "Delete",
            viewNeed: "View need",
            viewConsultant: "View consultant",
          },
          submission: {
            status: {
              submittedToClient: "Submitted to client",
              rejected: "Rejected by client",
              withdrawn: "Withdrawn",
              draft: "Draft",
            },
            columnTitle: "Submission",
            submitAction: "Submit profile",
            withdrawAction: "Withdraw",
            rejectAction: "Mark rejected",
            modalTitle: "Submit match to client",
            modalDescription:
              "Confirm that Sales has sent this consultant to the client.",
            channelLabel: "Submission channel",
            channelEmail: "Email",
            channelPlatform: "Platform",
            channelOther: "Other",
            submittedAtLabel: "Submitted at (optional)",
            noteLabel: "Note (optional)",
            notePlaceholder: "e.g. sent via email to client contact",
            cancelAction: "Cancel",
            confirmAction: "Confirm submission",
          },
          matchesForNeedTitle: "Matches for need \"{{title}}\"",
          noMatches: "No matches found for this need.",
          noReasoning: "No reasoning stored for this match.",
          noNeedInContext: "No need information available in this context.",
          noConsultantInContext: "No consultant information available in this context.",
        },
        toasts: {
          loadNeedsFailed: "Failed to load needs",
          loadMatchesFailedTitle: "Failed to load matches",
          loadMatchesFailedDescription: "Please try again",
          generateSuccess: "Matches generated",
          generateFailedTitle: "Generation failed",
          generateFailedDescription: "Please try again",
          deleteSuccess: "Match deleted",
          deleteFailedTitle: "Delete failed",
          deleteFailedDescription: "Please try again",
          updateMatchSuccess: "Updated",
          updateMatchFailed: "Update failed",
          maxConsultantsSelected: "You can select at most {{max}} consultants.",
          submission: {
            invalidTransitionTitle: "Invalid transition",
            invalidTransitionDescription:
              "This match changed state. Refresh and try again.",
            invalidChannelTitle: "Invalid channel",
            invalidChannelDescription: "Use one of: email, platform, other.",
            invalidSubmittedAtTitle: "Invalid submission date",
            matchNotFoundTitle: "Match not found",
            genericFailedTitle: "Submission update failed",
            submitSuccess: "Submitted to client",
            withdrawSuccess: "Submission withdrawn",
            rejectSuccess: "Marked as rejected",
          },
        },
      },
      settings: {
        pageTitle: "Settings",
        eyebrow: "Administration",
        loading: "Loading...",
        noPermission: "You don't have permission to access settings.",
        noPermissionTitle: "No access",
        tabs: {
          company: "Company",
          businessUnit: "Business Unit",
          partnersEmail: "Partners Email",
        },
        partnersEmail: {
          description: "Configure the SMTP email settings used by ASAP Partners to send communications from this business unit.",
        },
        company: {
          accessDeniedTitle: "Access denied",
          accessDeniedDescription:
            "Only company administrators can view company settings.",
          sectionTitle: "Company profile",
          sectionDescription: "Your company identity and billing contact details.",
          headerButton: "Edit company info",
          modalTitle: "Edit Company",
          displayCompanyFallback: "Company",
          fields: {
            logo: "Company logo",
            name: "Company name",
            managerName: "Manager's name",
            billingManagerName: "Billing manager's name",
            billingManagerEmail: "Billing manager's email",
            website: "Website (URL)",
          },
          buttons: {
            uploadLogo: "Upload",
            cancel: "Cancel",
            save: "Save",
          },
          toasts: {
            loadFailed: "Failed to load company",
            saveSuccess: "Company settings saved",
            saveFailed: "Failed to save",
          },
        },
        businessUnits: {
          noUnits: "No business units found for this company.",
          noUnitsHint: "Create a business unit to organize your teams.",
          toasts: {
            created: "Business unit created",
            createFailed: "Creation failed",
          },
          newButton: "New Business Unit",
          listTitle: "Business Units",
          table: {
            columns: {
              name: "Name",
              code: "Code",
              manager: "Manager",
              actions: "Actions",
            },
            noUnits: "No business units found.",
            edit: "Edit",
          },
          editModal: {
            title: "Edit Business Unit",
            fields: {
              unitName: "Unit name",
              managerName: "Manager's name",
              billingManagerName: "Billing manager's name",
              billingManagerEmail: "Billing manager's email",
            },
            buttons: {
              cancel: "Cancel",
              save: "Save",
            },
            toasts: {
              loadFailed: "Failed to load business unit",
              saveSuccess: "Business unit settings saved",
              saveFailed: "Failed to save settings",
            },
          },
          createModal: {
            title: "Create Business Unit",
            fields: {
              unitName: "Unit name",
              managerName: "Manager's name",
              billingManagerName: "Billing manager's name",
              billingManagerEmail: "Billing manager's email",
            },
            buttons: {
              cancel: "Cancel",
              create: "Create",
            },
            toasts: {
              createSuccess: "Business unit created",
              createFailed: "Creation failed",
            },
          },
          editButtonForBUAdmin: "Edit Business Unit",
        },
        users: {
          sectionTitle: "Users",
          inviteButton: "Invite User",
          table: {
            columns: {
              name: "Name",
              email: "Email",
              role: "Role",
              businessUnit: "Business Unit",
              actions: "Actions",
            },
            noUsersCompany:
              "No company-level users (not assigned to any business unit).",
            noUsersBU:
              "This business unit has no users yet. Please invite users to get started.",
            noUsersHint: "Invite a user to get started.",
            edit: "Edit",
            remove: "Remove",
          },
          inviteModal: {
            title: "Invite User",
            fields: {
              email: "Email",
              firstName: "First name",
              lastName: "Last name",
              existingUser: "Existing user",
              businessUnitCode: "Business Unit code",
              role: "Role",
              jobTitle: "Job title",
              reportingManager: "Reporting manager",
            },
            labels: {
              companyAdmin: "Company Admin",
            },
            buttons: {
              cancel: "Cancel",
              sendInvite: "Send Invite",
            },
          },
          editModal: {
            title: "Edit User",
            fields: {
              firstName: "First name",
              lastName: "Last name",
              businessUnit: "Business Unit",
              role: "Role",
              jobTitle: "Job title",
              reportingManager: "Reporting manager",
            },
            buttons: {
              cancel: "Cancel",
              removeUser: "Remove User",
              save: "Save",
            },
          },
          removeDialog: {
            title: "Remove User",
            confirmQuestion: "Are you sure you want to remove this user?",
            permanentDeletionTitle: "Permanent Deletion",
            permanentDeletionDescription:
              "This action will permanently delete the user from the company and remove their Firebase account. This cannot be undone.",
            buRemovalTitle: "Business Unit Removal",
            buRemovalDescription:
              "This will remove the user from this business unit only. They will remain in the company if they belong to other business units.",
            buttons: {
              cancel: "Cancel",
              removeFromBU: "Remove from Business Unit",
              deleteUser: "Delete User",
              removing: "Removing...",
            },
            roleBadge: {
              companyAdmin: "Company Admin",
              admin: "Admin",
            },
          },
          toasts: {
            fetchUsersFailed: "Failed to fetch users",
            inviteSuccess: "Invitation sent",
            inviteFailed: "Invite failed",
            updateSuccess: "User updated",
            updateFailed: "Update failed",
            removeSuccessCompany: "User removed from the company",
            removeSuccessBU: "User removed from this business unit",
            removeFailed: "Failed to remove user",
          },
        },
      },
      profile: {
        eyebrow: "Account",
        pageTitle: "Profile Settings",
        sections: {
          profileInfo: {
            title: "Profile Information",
            description: "Update your profile information",
          },
          password: {
            title: "Password",
            description: "Update your password",
          },
          linkedin: {
            title: "LinkedIn",
            description: "Add your profile",
          },
          businessUnits: {
            title: "Business Units",
            description: "Your memberships and roles",
          },
          role: {
            title: "Role",
            description: "Information set by your administrator",
          },
        },
        fields: {
          firstName: "First name",
          lastName: "Last name",
          email: "Email address",
          currentPassword: "Current password",
          newPassword: "New password",
          linkedinUrl: "LinkedIn URL",
          jobTitle: "Job title / Position",
          department: "Department / Team / Business Unit",
          reportingManager: "Reporting manager",
          notSet: "Not set",
          noBusinessUnits: "No business units",
        },
        buttons: {
          uploadPhoto: "Upload photo",
          save: "Save",
          selectBU: "Select",
        },
        passwordModal: {
          title: "Password updated",
          body: "You need to log out and log back in with your new password.",
          ok: "OK",
        },
        toasts: {
          profileUpdated: "Profile updated",
          profileUpdateFailed: "Failed to update profile",
          passwordUpdated: "Password updated",
          passwordUpdateFailed: "Failed to update password",
        },
        notifications: {
          modules: {
            partners: "ASAP Partners",
          },
          eventsApplicationHighMatch: "High-scoring application received",
          eventsCvReceived: "CV / application received",
          eventsNewRequirementFromPartner: "New requirement from a partner",
          eventsIncomingConsultantFromPartner: "Consultant proposed by a partner",
        },
      },
      // ── ASAP Partners status labels (resolved by getStatusLabel) ──
      partnersStatuses: {
        pool: { active: "Active", archived: "Archived" },
        poolMember: { active: "Active", new_to_add: "New", removed: "Removed" },
        mission: {
          draft: "Draft", sent: "Sent", active: "Active",
          filled: "Filled", closed: "Closed", expired: "Expired",
        },
        partnerLink: { active: "Active", suspended: "Suspended", removed: "Removed" },
        incoming: {
          pending: "Pending", accepted: "Accepted",
          declined: "Declined", completed: "Completed",
        },
        submission: {
          submitted: "Submitted", scored: "Scored", shortlisted: "Shortlisted",
          rejected: "Rejected", positioned: "Positioned",
        },
      },
      // ── ASAP Partners ──
      partners: {
        communications: {
          title: "Job Postings",
          history: {
            title: "Communication History",
            table: { date: "Date", action: "Action", details: "Details", mission: "Mission" },
            empty: "No communications yet",
          },
          emailConfig: {
            title: "Email Configuration",
            configureButton: "Configure",
            verifyButton: "Verify",
            notConfigured: "Email sending is not configured yet. Click Configure to set up.",
            notConfiguredTitle: "Not configured",
            senderEmail: "Sender Email",
            smtpHost: "SMTP Host",
            authProtocol: "Auth Protocol",
            verified: "Verified",
            verifiedYes: "Verified",
            verifiedNo: "Not Verified",
            senderMode: "Sender Mode",
            senderModePlatform: "ASAP HUB default sender",
            senderModeOwn: "Own email server",
            senderAddress: "Sender Address",
            senderAddressAuto: "Auto-generated on first send",
            modalTitle: "Configure Email Settings",
            form: {
              usePlatformSender: "Use ASAP HUB default sender",
              usePlatformSenderHelp: "Send emails from the shared no-reply@asap-hub.ai address — no SMTP setup required.",
              fromLocalPart: "Sender address",
              fromLocalPartPlaceholder: "yourcompany",
              fromLocalPartHelp: "The address your mission emails are sent from. Leave blank to auto-generate from your company name.",
              senderEmail: "Sender Email",
              senderPassword: "Password / App Password",
              senderPasswordPlaceholder: "Leave blank to keep current",
              smtpHost: "SMTP Host",
              smtpPort: "SMTP Port",
              authProtocol: "Auth Protocol",
              authStarttls: "STARTTLS",
              authSsl: "SSL",
              authOauth2: "OAuth2",
              cancel: "Cancel",
              save: "Save Configuration",
            },
          },
          newCommunication: {
            title: "New Communication",
            description: "Create a new mission to send communications to your consultant pools. Missions allow you to reach out to partners with opportunities, collect availability, and manage responses.",
            createMissionButton: "Create Mission",
          },
          toasts: {
            configUpdated: "Email configuration updated",
            configFailedTitle: "Update failed",
            configFailedDescription: "Could not update email configuration.",
            verifySuccess: "Test email sent — configuration verified",
            verifyFailed: "Verification failed",
          },
        },
        jobPostings: {
          table: {
            name: "Job Posting",
            recipients: "Recipients",
            publicationDate: "Publication Date",
            cvsReceived: "CVs Received",
            cvsAboveThreshold: "CVs Above Threshold",
            additionalContacts: "Additional Contacts",
          },
          empty: "No job postings yet",
          errorTitle: "Failed to load job postings",
        },
        poolsAndPartners: {
          title: "Pools & Partners",
          eyebrow: "ASAP Partners",
        },
        partnerLinks: {
          fetchError: "Failed to load partner links",
          removed: "Partner link removed",
          removeError: "Failed to remove partner link",
          title: "Partner Companies",
          add: "Add Partner",
          summary: { loading: "Loading...", total: "{{count}} partner(s)" },
          companyName: "Company",
          code: "Code",
          emptyTitle: "No partner companies yet",
          empty: "No partner companies yet. Add one by entering their partner code.",
          remove: "Remove",
          removeTitle: "Remove Partner",
          confirmRemove: "Are you sure you want to remove this partner link? You can always add it back later.",
          removeAction: "Remove",
          codeRequired: "Partner code is required",
          created: "Partner link created",
          codeNotFound: "No company found with this partner code",
          cannotLinkSelf: "Cannot link to your own company",
          alreadyExists: "This partner link already exists",
          addPartner: "Add Partner Company",
          addDescription: "Enter the partner code of the company you want to link with. This code is shared by the partner company.",
          partnerCode: "Partner Code",
          codePlaceholder: "e.g. a1b2-c3d4-e5f6-g7h8",
        },
        myCode: {
          copied: "Partner code copied to clipboard",
          title: "Your Partner Code",
          copy: "Copy",
          notSet: "No unit code configured for this business unit. Contact your administrator.",
          hint: "Share this code with other companies so they can link with you.",
        },
        pools: {
          title: "Consultant Pools",
          createButton: "Create Pool",
          filters: {
            searchPlaceholder: "Search by name or code...",
            perPage: "{{count}}/page",
          },
          summary: { loading: "Loading...", total: "{{count}} pool(s)" },
          table: {
            name: "Name", code: "Code", description: "Description",
            status: "Status", members: "Members", actions: "Actions",
            noPools: "No pools found",
            noPoolsHint: "Create a pool to start grouping your consultants.",
          },
          pagination: {
            pageLabel: "Page {{page}} of {{totalPages}}",
            first: "First", prev: "Prev", next: "Next", last: "Last",
          },
          actions: { editAria: "Edit", archiveAria: "Archive" },
          confirmArchive: "Are you sure you want to archive \"{{name}}\"?",
          archiveConfirmTitle: "Archive pool?",
          archiveConfirmButton: "Archive",
          toasts: {
            loadFailed: "Failed to load pools",
            archived: "Pool archived",
            archiveFailedTitle: "Archive failed",
            archiveFailedDescription: "Could not archive pool.",
          },
          form: {
            modalTitleEdit: "Edit Pool",
            modalTitleCreate: "Create Pool",
            fields: { name: "Name", code: "Code", description: "Description" },
            placeholders: { name: "Pool name", code: "POOL_CODE", description: "Optional description" },
            buttons: { cancel: "Cancel", saveChanges: "Save Changes", create: "Create" },
            toasts: {
              updated: "Pool updated", created: "Pool created",
              operationFailedTitle: "Operation failed",
              operationFailedDescription: "An error occurred. Please try again.",
            },
          },
          detail: {
            titleFallback: "Pool Details",
            tabs: { info: "Info", members: "Members" },
            info: {
              notFound: "Pool not found",
              name: "Name", code: "Code", status: "Status",
              description: "Description", created: "Created", membersCount: "Members",
            },
            members: {
              title: "Pool Members",
              addButton: "Add Member",
              importButton: "Import from file",
              summary: { loading: "Loading...", total: "{{count}} member(s)" },
              table: { name: "Name", email: "Email", status: "Status", actions: "Actions" },
              noMembers: "No members yet",
              pagination: {
                pageLabel: "Page {{page}} of {{totalPages}}",
                perPage: "{{count}}/page", prev: "Prev", next: "Next",
              },
            },
            editMemberAria: "Edit",
            deleteMemberAria: "Delete",
            close: "Close",
            toasts: {
              loadMembersFailed: "Failed to load members",
              memberDeleted: "Member removed",
              deleteFailedTitle: "Delete failed",
              deleteFailedDescription: "Could not remove member.",
            },
            confirmDeleteMember: "Are you sure you want to remove this member?",
          },
          members: {
            form: {
              modalTitleEdit: "Edit Member",
              modalTitleCreate: "Add Member",
              tabs: { manual: "Add manually", bulkCv: "Upload CVs" },
              fields: { firstName: "First Name", lastName: "Last Name", email: "Email", status: "Status" },
              placeholders: { firstName: "First name", lastName: "Last name", email: "email@example.com" },
              buttons: { cancel: "Cancel", saveChanges: "Save Changes", add: "Add Member" },
              toasts: {
                updated: "Member updated", created: "Member added",
                operationFailedTitle: "Operation failed",
                operationFailedDescription: "An error occurred. Please try again.",
              },
            },
            bulkCv: {
              notice: "Upload up to 20 PDF/DOCX CVs. Name and email will be extracted automatically.",
              fileLabel: "CV files (PDF or DOCX)",
              dropzone: "Drag & drop CV files here, or click to browse",
              accepted: "PDF, DOCX — max 20 files",
              selectedCount: "{{count}} file(s) selected",
              added: "Added",
              failed: "Failed",
              uploadButton: "Upload {{count}} CV(s)",
              unsupportedFiltered: "Some files were skipped (only PDF/DOCX accepted)",
              tooManyFiles: "Maximum 20 files at a time",
              createdTitle_one: "member added successfully",
              createdTitle_other: "members added successfully",
              errorsTitle_one: "file could not be processed",
              errorsTitle_other: "files could not be processed",
              errors: {
                duplicateEmail: "Email \"{{email}}\" is already in this pool",
                emailNotFound: "No email could be extracted from this CV",
                unsupportedType: "Unsupported file type (only PDF/DOCX)",
                parseFailed: "Could not read text from this file",
                uploadFailed: "Storage upload failed",
              },
              toasts: {
                uploadFailed: "Bulk upload failed",
              },
            },
            importFromFile: {
              modalTitle: "Import pool members from CSV/XLSX",
              description:
                "Upload a CSV or XLSX file. Supported columns: firstName, lastName (or a single \"name\" column), email, status (active / new_to_add / removed) and optional consultantId. Use the optional \"action\" column with values add, update or delete to update or remove existing members by email.",
              fields: { file: "File" },
              statusLabels: {
                total: "Total",
                added: "Added",
                updated: "Updated",
                deleted: "Deleted",
                duplicate: "Duplicates",
              },
              dropzone: {
                text: "Drag & drop CSV/XLSX here or click to select",
                accepted: "Accepted: .csv, .xlsx",
              },
              buttons: { cancel: "Cancel", import: "Import" },
              toasts: {
                importCompleted: "Import completed",
                importFailedTitle: "Import failed",
                importFailedDescription: "Please try again",
                invalidFileTypeTitle: "Invalid file type",
                invalidFileTypeDescription: "Only CSV or XLSX files are accepted",
                invalidExcelDescription: "The Excel file could not be read. Please check its format.",
                importTooLargeTitle: "Import too large",
                importTooLargeDescription:
                  "This file contains more than 500 data rows. Please split it and try again.",
              },
            },
          },
        },
        missions: {
          title: "Missions",
          createButton: "New Mission",
          searchPlaceholder: "Search by title...",
          allStatuses: "All statuses",
          empty: "No missions yet",
          totalItems: "{{count}} missions",
          purpose: "Purpose",
          location: "Location",
          tjm: "Daily Rate",
          startDate: "Start Date",
          asap: "ASAP",
          closeDialog: {
            title: "Close this mission?",
            body: "The mission will be closed and will no longer accept applications.",
            notifyQuestion: "Would you like to send an automatic notification to all applicants?",
            notifyHint: "The {{count}} applicant(s) to this mission will receive an email informing them that the position has been filled, thanking them for applying and offering to keep their details for future opportunities.",
            notifyExclusions: "The positioned candidate and candidates submitted by partner companies are not notified.",
            noApplicants: "No one has applied to this mission, so no notification will be sent.",
            yes: "Yes, notify applicants",
            no: "No, close without notifying",
            cancel: "Cancel",
          },
          duration: "Duration",
          table: {
            title: "Title", purpose: "Purpose", status: "Status",
            recipients: "Recipients", applications: "Applications", created: "Created",
          },
          purposeLabels: {
            contactPool: "Contact Pool",
            proposeAssignment: "Assignment",
          },
          form: {
            steps: {
              purpose: "Purpose", details: "Details", skills: "Skills",
              conditions: "Conditions", recipients: "Recipients", review: "Review",
            },
            purposeOptions: {
              contactPool: "Share an assignment with a list of candidates",
              proposeAssignment: "Propose an assignment to a partner company",
            },
            purposeDescription: "Select the type of communication you want to send.",
            createTitle: "Create Mission",
            editTitle: "Edit Mission",
            title: "Title",
            titlePlaceholder: "e.g., Full-Stack Developer React/Python",
            description: "Description",
            descriptionPlaceholder: "Describe the mission, context, and expectations...",
            category: "Category",
            categoryPlaceholder: "e.g., IT, Data, Management",
            technicalSkills: "Technical Skills",
            technicalSkillsPlaceholder: "e.g., React, Python, PostgreSQL",
            functionalSkills: "Functional Skills",
            functionalSkillsPlaceholder: "e.g., Agile, Scrum, Project Management",
            businessSkills: "Business Skills",
            businessSkillsPlaceholder: "e.g., Finance, Marketing, Sales",
            requiredSkills: "Required Skills",
            optionalSkills: "Optional Skills",
            addSkillButton: "Add",
            skillsDescription: "Add skills by category, then mark each as required or optional for scoring.",
            importanceTitle: "Skill Importance",
            importanceDescription: "Click to toggle each skill as required, optional, or unclassified. Required skills weigh 80% in scoring, optional 20%.",
            legendRequired: "Required",
            legendOptional: "Optional",
            legendUnclassified: "Unclassified",
            location: "Location",
            locationPlaceholder: "e.g., Paris, Remote",
            startDate: "Start Date",
            startAsap: "ASAP",
            tjm: "TJM",
            tjmMin: "TJM Min (€)",
            tjmMax: "TJM Max (€)",
            durationDays: "Duration (days)",
            durationValue: "Duration",
            durationUnit: "Unit",
            unitDays: "Days",
            unitMonths: "Months",
            days: "days",
            months: "months",
            isRenewable: "Renewable",
            contractType: "Contract Type",
            contractTypePlaceholder: "Select a contract type",
            contractTypeFreelance: "Freelance",
            contractTypeCdi: "CDI",
            contractTypePortage: "Portage salarial",
            contractTypeSousTraitance: "Sous-traitance",
            salary: "Salary",
            salaireMin: "Salary Min (€/year)",
            salaireMax: "Salary Max (€/year)",
            selonProfil: "Based on profile",
            renewable: "(renewable)",
            atsThreshold: "ATS Score Threshold",
            atsNone: "No threshold",
            ats50: "50% minimum",
            ats80: "80% minimum",
            recipientsDescription: "Select which consultant pools will receive this mission.",
            loadingPools: "Loading pools...",
            noPools: "No pools available. Create a pool first.",
            members: "members",
            reviewDescription: "Review your mission before saving.",
            purpose: "Purpose",
            selectedPools: "Selected Pools",
            noPoolsSelected: "No pools selected",
            cancel: "Cancel",
            back: "Back",
            next: "Next",
            save: "Save",
            createDraft: "Create Draft",
            evaluationLanguage: "Evaluation Language",
            poolsSection: "Consultant Pools",
            partnersSection: "Partner Companies",
            createAndSend: "Create & Send",
            autoMode: "Auto mode",
            autoModeDescription: "Auto mode: the system will automatically select the most relevant skills and generate the evaluation. All skills below will be sent for analysis.",
            includeEvaluation: "Include SKILLS evaluation",
            evaluationDescription: "Candidates will receive a separate evaluation email with a technical test. Select which skills to include (max 5).",
            selectedSkillsCount: "Selected: {{count}} / {{max}}",
            tooManySkills: "Maximum {{max}} skills allowed",
            evalSkill: "Skill",
            evalType: "Type",
            evalDifficulty: "Difficulty",
            evalImportance: "Importance",
            evalCoding: "Coding",
            ats60: "60% minimum",
            ats75: "75% minimum",
            ats85: "85% minimum",
            pilotNeed: "Pre-fill from Client Need (PILOT)",
            loadingNeeds: "Loading client needs...",
            noNeeds: "No open client needs available.",
            selectNeed: "-- Select a client need to pre-fill --",
            loadingPools: "Loading pools...",
            loadingPartners: "Loading partner companies...",
            noPartners: "No partner companies linked yet. Add partners from the Partners page.",
            recipientsDescriptionPools: "Select consultant pools that will receive this job posting.",
            recipientsDescriptionPartners: "Select partner companies that will receive this mission.",
            selectedPartners: "Selected Partners",
            noPartnersSelected: "No partner companies selected",
            saveAsDraft: "Save as draft",
            saveAsDraftDescription: "Save this mission as a draft instead of sending it immediately.",
            skillsEvaluation: "SKILLS Evaluation",
            skillsSelected: "skills selected",
            rfpUpload: "Upload RFP (optional)",
            rfpUploadDescription: "Upload a job description document to auto-fill the form fields. Supported formats: PDF, DOCX (max 10 MB).",
            rfpDragDrop: "Drag & drop an RFP file here, or click to browse",
            rfpExtracting: "Extracting data from document...",
            rfpOverwriteTitle: "Overwrite existing data?",
            rfpOverwriteBody: "The form already contains data. Applying the extracted RFP data will replace the current title, description, skills, and location fields.",
            rfpOverwriteCancel: "Cancel",
            rfpOverwriteConfirm: "Apply RFP data",
          },
          sections: {
            title: "Published text",
            help: "This wording is published in the email and on the mission page. Edit it freely, you have full control.",
            generate: "Generate with AI",
            regenerate: "Regenerate with AI",
            generating: "Generating...",
            generateError: "Could not generate the text. Please try again.",
            responsibilities: "Missions, Roles & Responsibilities",
            requiredSkills: "Required / Expected Skills",
            candidateProfile: "Candidate Profile",
            responsibilitiesPlaceholder: "Intro sentence followed by '- ' bullet points...",
            requiredSkillsPlaceholder: "Intro sentence followed by '- ' bullet points...",
            candidateProfilePlaceholder: "Intro sentence followed by '- ' bullet points...",
            regenerateTitle: "Regenerate the text?",
            regenerateBody: "You have edited the published text. Regenerating with AI will replace your changes in all three sections.",
            regenerateCancel: "Keep my text",
            regenerateConfirm: "Regenerate",
            descriptionLabel: "Description (Context)",
            descriptionHelp: "Shown as the Contexte section of the email. Edit it here before sending.",
            descriptionPlaceholder: "Describe the mission, context, and expectations...",
            previewTitle: "Email preview",
            previewHelp: "This is the email consultants will receive. Edit the content on the left to update it.",
            previewLoading: "Updating preview...",
            previewError: "Could not load the preview. Edit a field to retry.",
            subjectLabel: "Email subject",
            subjectHelp: "The subject line consultants will see. Edit it freely (for example, to flag an update).",
            resetToGenerated: "Reset to generated",
            customizeTitle: "Customize the full email",
            customizeHelp: "Turn this on to rewrite the entire email freely. The generated content is the starting point. [Prénom] and the apply link are filled in automatically per consultant.",
            emailBodyLabel: "Email content",
            revertToGenerated: "Revert to generated content",
          },
          detail: {
            loading: "Loading...",
            notFound: "Mission not found",
            tabs: { info: "Info", recipients: "Recipients", applications: "Applications" },
            purpose: "Purpose",
            category: "Category",
            location: "Location",
            tjm: "TJM",
            startDate: "Start Date",
            duration: "Duration",
            contractType: "Contract Type",
            atsThreshold: "ATS Threshold",
            created: "Created",
            description: "Description",
            technicalSkills: "Technical Skills",
            functionalSkills: "Functional Skills",
            businessSkills: "Business Skills",
            requiredSkills: "Required Skills",
            optionalSkills: "Optional Skills",
            landingPage: "Landing Page",
            copyLinkAria: "Copy link",
            deliverability: {
              title: "Email deliverability",
              sent: "Sent",
              delivered: "Delivered",
              bounced: "Bounced",
              complained: "Complaints",
              deliveryRate: "Delivery rate",
              bounceRate: "Bounce rate",
              complaintRate: "Complaint rate",
            },
            noRecipients: "No recipients added",
            recipientType: "Type",
            recipientName: "Name",
            emailSentAt: "Email Sent",
            recipientTypePool: "Pool",
            recipientTypePartner: "Partner",
            noApplications: "No applications received yet",
            applicant: "Applicant",
            email: "Email",
            score: "Score",
            appStatus: "Status",
            appDate: "Date",
            close: "Close",
            copyLink: "Copy Link",
            copyLinkedIn: "Copy LinkedIn Post",
            candidate: "Candidate",
            submissionFallback: "Submission #{{id}}",
            profile: "Profile",
            experience: "Experience",
            years: "years",
            edit: "Edit",
            send: "Send",
            closeMission: "Close Mission",
            phone: "Phone",
            referral: "Referral",
            referredBy: "Referred by",
            skills: "Skills",
            downloadCv: "Download CV",
            downloadingCv: "Downloading...",
            downloadError: "Failed to download CV",
            pool: "Pool",
            selectPool: "Select pool",
            addToPool: "Add to pool",
            addedToPool: "Candidate added to pool",
            addToPoolError: "Failed to add to pool",
            alreadyInPool: "This candidate is already in the selected pool",
            inPool: "Already in pool",
            consultantLinked: "Consultant #{{id}}",
            createConsultant: "Create Consultant",
            consultantCreated: "Consultant created successfully",
            consultantCreatedDesc: "Consultant #{{id}} has been added to PILOT.",
            consultantError: "Failed to create consultant",
            alreadyConsultant: "This application already has an associated consultant.",
            loadError: "Failed to load mission",
            skillsEvaluation: "SKILLS Evaluation",
            evaluationAttached: "Evaluation attached",
            noSubmissions: "No partner submissions received yet",
            submitter: "Submitted By",
            cost: "Cost",
            availability: "Availability",
            tabs: { info: "Info", recipients: "Recipients", applications: "Applications", submissions: "Partner Submissions" },
          },
          toasts: {
            loadError: "Failed to load missions",
            created: "Mission created",
            updated: "Mission updated",
            errorTitle: "Error",
            errorDescription: "Could not save mission.",
            sent: "Mission sent successfully",
            sendError: "Failed to send mission",
            closed: "Mission closed",
            closedNotified: "Mission closed. Applicants are being notified by email.",
            closeError: "Failed to close mission",
            linkCopied: "Landing page link copied to clipboard",
            linkError: "Failed to copy link",
            linkedInCopied: "LinkedIn post copied to clipboard",
            linkedInCopyError: "Failed to copy LinkedIn post",
            rfpUnsupported: "Only PDF and DOCX files are supported",
            rfpTooLarge: "File too large (max 10 MB)",
            rfpError: "Failed to extract RFP data",
            rfpExtracted: "RFP data extracted and pre-filled",
          },
          linkedIn: {
            notSpecified: "To be specified",
            durationMonths: "{{count}} months",
            durationDays: "{{count}} days",
            renewable: "(renewable)",
            headline: "📢 New mission opportunity 📢",
            intro: "I am currently looking for a consultant to support my client on a {{title}} mission.",
            infoSection: "📍 Key information",
            locationLine: "• Location: {{value}}",
            startDateLine: "• Start date: {{value}}",
            durationLine: "• Duration: {{value}}",
            contractLine: "• Contract: {{value}}",
            profilesSection: "👤 Profile sought",
            experienceLine: "• Strong experience in {{title}}",
            skillsLine: "• Proficient in {{skills}}",
            skillsFallback: "• Mastery of the field's tools and methods",
            relationalLine: "• Strong interpersonal skills and client service mindset",
            applySection: "📬 Available or open to new opportunities?",
            applyDirect: "You can apply directly through the following link:",
            closingLine1: "Your application will be reviewed promptly, and we will reach out if your profile matches our clients' needs.",
            closingLine2: "Feel free to like, comment, or share this post to give it visibility with the right people.",
            closingLine3: "Thank you in advance for your help and recommendations.",
            hashtags: "#Consultants #Mission #Recruitment",
          },
        },
        incoming: {
          title: "Requirements",
          empty: "No incoming missions yet.",
          totalItems: "{{count}} incoming missions",
          searchPlaceholder: "Search by title...",
          allStatuses: "All statuses",
          fetchError: "Failed to load incoming missions",
          missionTitle: "Mission",
          sender: "From",
          purpose: "Purpose",
          missionStatus: "Mission Status",
          submissionsCount: "Submissions",
          accepted: "Mission accepted",
          declined: "Mission declined",
          table: {
            mission: "Mission", sender: "Sender", purpose: "Purpose",
            status: "Status", submissions: "Submissions", created: "Created",
          },
          score: "Score",
          detail: "Incoming Mission Detail",
          accept: "Accept",
          decline: "Decline",
          submissions: "Submissions",
          submitCandidate: "Submit Candidate",
          noSubmissions: "No candidates submitted yet.",
          candidate: "Candidate",
          nameRequired: "First and last name are required",
          sourceCvRequired: "Please upload a CV",
          candidateSubmitted: "Candidate submitted successfully",
          sourceCv: "CV",
          uploadCv: "Click to upload CV",
          fileTooLarge: "File too large (max 10 MB)",
          costAmount: "Cost",
          costType: "Cost Type",
          selectCostType: "Select type",
          salary: "Salary",
          dailyRate: "Daily Rate",
          availabilityDate: "Availability Date",
          submit: "Submit",
          toasts: {
            loadError: "Failed to load incoming missions",
            accepted: "Mission accepted", declined: "Mission declined",
            updateError: "Failed to update status",
          },
        },
        companyLinks: {
          title: "Partner Companies",
          addButton: "Add Partner",
          empty: "No partner companies linked",
          totalItems: "{{count}} partner(s)",
          table: {
            companyName: "Company", partnerCode: "Partner Code",
            status: "Status", created: "Created", actions: "Actions",
          },
          form: {
            modalTitle: "Add Partner Company",
            partnerCode: "Partner Code",
            partnerCodePlaceholder: "e.g. a1b2-c3d4-e5f6-g7h8",
            cancel: "Cancel", add: "Add Partner",
          },
          toasts: {
            created: "Partner company linked",
            createError: "Failed to link partner",
            removed: "Partner link removed",
            removeError: "Failed to remove partner link",
          },
          confirmRemove: "Remove this partner link?",
        },
        dashboard: {
          title: "ASAP PARTNERS — Dashboard",
          fetchError: "Failed to load dashboard data",
          poolSection: "Consultant Pools",
          missionSection: "Missions",
          applicationSection: "Applications",
          partnerSection: "Partner Companies",
          communicationSection: "Communications",
          activePools: "Active Pools",
          totalPoolMembers: "Total Pool Members",
          totalMissions: "Total Missions",
          missionsByPurpose: "Missions by Purpose",
          totalApplications: "Total Applications",
          avgScore: "Avg. Score: {{score}}",
          activePartnerLinks: "Active Partner Links",
          totalIncoming: "Incoming Missions",
          totalSubmissions: "Partner Submissions",
          totalCommunications: "Total Communications",
          sections: {
            pools: "Pools", missions: "Missions", applications: "Applications",
            partners: "Partners", communications: "Communications",
          },
          stats: {
            activePools: "Active Pools", totalMembers: "Total Members",
            totalMissions: "Total Missions", missionsByStatus: "By Status",
            missionsByPurpose: "By Purpose",
            totalApplications: "Total Applications", averageScore: "Average Score",
            applicationsByStatus: "By Status",
            activePartnerLinks: "Partner Links", totalIncoming: "Incoming Missions",
            incomingByStatus: "By Status",
            totalSubmissions: "Submissions", submissionsByStatus: "By Status",
            totalCommunications: "Communications",
          },
        },
        sentRequests: {
          title: "Sent Requests",
          tabs: { missions: "Missions", consultants: "Consultants" },
          consultantsAllStatuses: "All statuses",
          toasts: {
            loadMissionsError: "Failed to load missions",
            loadProposalsError: "Failed to load consultant proposals",
          },
        },
        receivedRequests: {
          title: "Received Requests",
          tabs: { missions: "Missions", consultants: "Consultants" },
          fetchError: "Failed to load received requests",
          fetchProposalsError: "Failed to load consultant proposals",
        },
        stats: {
          chartTitle: "Monthly Activity",
          total: "Total: {{count}}",
          barLabel: "Count",
          noData: "No activity yet for this period",
        },
        consultantProposals: {
          createButton: "Propose Consultant",
          empty: "No consultant proposals sent yet",
          emptyReceived: "No consultant proposals received yet.",
          partnerNumber: "Partner",
          statusLabels: {
            pending: "Pending",
            accepted: "Accepted",
            declined: "Declined",
          },
          table: {
            recipient: "Recipient",
            from: "From",
            role: "Role",
            dailyRate: "Daily rate",
            availability: "Available",
            status: "Status",
            created: "Sent",
          },
          form: {
            title: "Propose a Consultant",
            anonymisedNotice:
              "The receiving partner will see only the anonymised profile (no name, email, or phone) plus your daily rate and availability.",
            source: "Source",
            sourcePilot: "Pick from Pilot",
            sourceCv: "Upload CV",
            pilotDisabled:
              "Pilot mode is unavailable — your business unit doesn't have ASAP Pilot enabled.",
            consultant: "Consultant",
            selectConsultant: "Select a consultant",
            cvFile: "CV file (PDF or DOCX)",
            cvDropzone: "Drag & drop a CV here, or click to browse",
            cvAccepted: "PDF or DOCX, one file",
            dailyRate: "Daily rate",
            currency: "Currency",
            availabilityDate: "Availability date",
            recipients: "Send to partner companies",
            noPartners: "No active partner links yet — add a partner first.",
            submit: "Send proposal",
          },
          detail: {
            title: "Consultant Proposal",
            anonymisedNotice:
              "This is an anonymised view — name, email, and phone are not shared.",
            sender: "From",
            role: "Role",
            description: "Profile",
            dailyRate: "Daily rate",
            availability: "Available from",
            location: "Location",
            skillsTechnical: "Technical skills",
            skillsFunctional: "Functional skills",
            skillsBusiness: "Business skills",
            note: "Note (optional)",
            notePlaceholder: "Anything to share with the sender...",
            matchTitle: "Match against my open needs",
            runMatch: "Run scoring",
            noMatches: "No matching needs above the threshold.",
            matchesFound_one: "{{count}} matching open need found",
            matchesFound_other: "{{count}} matching open needs found",
            viewMatches: "View matches",
            accept: "Accept",
            decline: "Decline",
          },
          matches: {
            title: "Matching open needs",
            empty: "No matching needs above the threshold.",
            table: {
              need: "Need",
              client: "Client",
              score: "Score",
              reasoning: "Reasoning",
            },
          },
          toasts: {
            created: "Consultant proposal sent",
            accepted: "Proposal accepted",
            declined: "Proposal declined",
          },
          errors: {
            loadPartners: "Failed to load partner companies",
            loadConsultants: "Failed to load consultants",
            unsupportedFile: "Only PDF and DOCX files are supported",
            noRecipients: "Select at least one partner company",
            noConsultant: "Pick a consultant from your pool",
            noCv: "Upload a CV file",
            createFailed: "Failed to send proposal",
            decisionFailed: "Failed to record decision",
            matchFailed: "Failed to score matches",
          },
        },
      },
      superAdmin: {
        deleteCompany: {
          action: "Delete",
          tooltip: "Delete company and its environments",
          title: "Delete company",
          body: "This will remove \"{{name}}\" and all of its environments from the dashboards, and immediately revoke access for all of its users. This cannot be undone from the portal. Continue?",
          confirm: "Delete company",
          success: "Company deleted",
          error: "Failed to delete company",
        },
        deleteBusinessUnit: {
          title: "Delete environment",
          body: "This will remove the environment \"{{name}}\" and revoke its members' access to it. This cannot be undone from the portal. Continue?",
          confirm: "Delete environment",
          success: "Environment deleted",
          error: "Failed to delete environment",
        },
      },
      common: {
        pagination: {
          pageLabel: "Page {{page}} of {{total}}",
          page: "page",
          first: "First", prev: "Prev", next: "Next", last: "Last",
        },
        name: "Name", email: "Email", status: "Status", createdAt: "Date",
        firstName: "First Name", lastName: "Last Name",
        description: "Description", cancel: "Cancel",
        close: "Close",
        days: "days", delete: "Remove",
        comingSoon: "Coming soon",
        allStatuses: "All statuses",
        error: "Error",
        unknownError: "An error occurred",
        confirmDelete: "Confirm Delete",
        confirmDeleteMessage: "Are you sure you want to delete this item?",
      },
      evaluation: {
        soundOn: "Sound on",
        soundOff: "Sound off",
        timeRunningLow: "Time running low",
        micCheck: {
          title: "Microphone check",
          instructions:
            "We'll record a short test to make sure your microphone and speech-to-text service are working. Your voice is not stored.",
          testButton: "Test microphone",
          testing: "Testing…",
          successTitle: "Microphone ready",
          successMessage: "Great — your microphone and dictation service are working.",
          failureTitle: "Microphone check failed",
          failureMessage:
            "We couldn't verify your microphone. Make sure your browser has microphone permission and try again.",
          troubleshooting: "Troubleshooting tips",
          tipPermission: "Allow microphone access when your browser asks.",
          tipDevice: "Check that the right microphone is selected in your system settings.",
          tipNetwork: "A stable internet connection is required for live transcription.",
          retry: "Retry",
          continue: "Continue",
          continueWithout: "Continue without microphone",
          skipExplanation:
            "You can still complete the test using the keyboard. Dictation will be disabled.",
        },
        sampleQuestion: {
          pageTitle: "Practice question",
          banner:
            "Practice question — this does not count toward your score. Use it to get familiar with the interface.",
          yourAnswer: "Your answer",
          submit: "Submit practice answer",
          submitting: "Evaluating…",
          resultTitle: "Practice result",
          score: "Score",
          comment: "Feedback",
          startTest: "Start the real test",
          skip: "Skip practice",
          fetchError: "We couldn't load the practice question. You can continue to the real test.",
          previewError: "We couldn't evaluate your practice answer. You can continue anyway.",
        },
        practice: {
          heading: "Practice question",
          banner:
            "Practice question — it does not count toward your final score. Use it to get familiar with the interface.",
          fetchError:
            "We couldn't load the practice question. You can start the real test directly.",
          startTest: "Start the real test",
          yourAnswer: "Your answer",
          answerPlaceholder: "Type your answer or use the microphone...",
          startMic: "Dictate via microphone",
          stopMic: "Stop dictation",
          verify: "Verify my answer",
          previewError: "We couldn't evaluate your practice answer. Please try again.",
          resultTitle: "Practice result",
          score: "Score",
          comment: "Feedback",
          footer:
            "When you're ready, click 'Start the real test' to begin. Practice answers are not saved.",
          sectionContext: "Context",
          sectionQuestion: "Question",
          sectionScoring: "Scoring rules",
          questionTypes: {
            OPEN: "Open question",
            CODING: "Coding question",
            QCM: "Single-choice question",
            "MULTI-CHOICE": "Multiple-choice question",
            KATA_OPEN: "Short open exercise",
            KATA_CODING: "Short coding exercise",
            CODE_COMPLETION: "Code completion",
          },
          tour: {
            timerTitle: "Timer",
            timer:
              "Each question has a countdown. The bar turns red when time is running low. The practice question doesn't auto-submit — take as long as you need.",
            questionTextTitle: "Question content",
            questionText:
              "Real questions show context, the question itself, and any scoring rules in this area. Read carefully before answering.",
            verifyTitle: "Verify your answer",
            verify:
              "Click 'Verify my answer' to get a quick check on the practice question. During the real test, this gives you a completeness score to refine your answer.",
            microphoneTitle: "Microphone",
            microphone:
              "Use the microphone to dictate your answer instead of typing. Speak in short sentences and pause between each — the transcript appears as you talk.",
            progress: "{{current}} / {{total}}",
            skip: "Skip",
            next: "Next",
            done: "Got it",
          },
        },
      },
      // Additional portal sections will be added here (settings, asapCV, asapSkills, cvModule)
    },
    public: {
      landing: {
        applyingFor: "Applying for",
        footer: { poweredBy: "Powered by" },
        missionClosed: "This mission is no longer accepting applications.",
        missionNotFound: "This mission does not exist or the link is invalid.",
        loadFailed: "Failed to load mission. Please try again later.",
        loading: "Loading...",
        cannotApply: "Cannot Apply",
        skillsForMission: "Skills for this mission:",
        required: "required",
        niceToHave: "nice to have",
        firstName: "First Name",
        lastName: "Last Name",
        email: "Email",
        phone: "Phone",
        cvResume: "CV / Resume",
        extractingSkills: "Extracting skills from your CV...",
        dragDropCv: "Drag & drop your CV here, or click to browse",
        cvFormats: "PDF, DOC, or DOCX (max 10 MB)",
        fileTooLarge: "File too large (max 10 MB)",
        fillRequired: "Please fill in all required fields",
        submitApplication: "Submit Application",
        submitting: "Submitting...",
        submissionFailed: "Submission failed",
        error: "Error",
        referral: "I was referred",
        referrerFirstName: "Referrer's First Name",
        referrerLastName: "Referrer's Last Name",
        tac: {
          label: "I accept the",
          linkText: "Terms & Conditions",
        },
        renewable: "(renewable)",
        submitNow: "Apply now",
        sections: {
          contexte: "Context",
          missionsRoles: "Missions, Roles and responsibilities",
          competencesRequises: "Required skills",
          profilRecherche: "Sought profile",
          informationsPratiques: "Practical information",
          locationLabel: "Location",
          startDateLabel: "Start date",
          asap: "ASAP",
          durationLabel: "Duration",
          contractLabel: "Contract",
          contractTypes: {
            freelance: "Freelance",
            cdi: "Permanent",
            portage: "Umbrella company",
            sous_traitance: "Subcontracting",
          },
          budgetLabel: "Budget",
          tjmLabel: "Daily rate",
          salaryLabel: "Salary",
          selonProfil: "based on profile",
          days: "days",
          months: "months",
          year: "year",
          day: "day",
          renewable: "(renewable)",
        },
        success: {
          title: "Application Submitted!",
          message: "Your application has been received and is being reviewed. You will be contacted if your profile matches the requirements.",
          closePage: "You can close this page now.",
        },
        welcome: {
          loading: "Loading mission details...",
          missionNotFound: "This mission does not exist or the link is invalid.",
          loadFailed: "Failed to load mission information. Please try again later.",
          errorTitle: "Mission Not Found",
          closedTitle: "Mission Closed",
          closedMessage: "This mission is no longer accepting applications.",
          purposePool: "Consultant Pool",
          purposeAssignment: "Assignment",
          purposeBench: "Bench Consultant",
          location: "Location",
          dailyRate: "Daily Rate (TJM)",
          from: "From",
          upTo: "Up to",
          startDate: "Start Date",
          duration: "Duration",
          days: "days",
          months: "months",
          requiredSkills: "Required Skills",
          niceToHave: "Nice to Have",
          applyNow: "Apply Now",
        },
        terms: {
          title: "Terms & Conditions",
          placeholder: "Content coming soon.",
        },
      },
      unsubscribe: {
        title: "Confirm unsubscribe",
        confirmPrompt: "You are about to unsubscribe ({{email}}). Confirm?",
        confirmCta: "Unsubscribe me",
        successTitle: "You are unsubscribed",
        successBody: "You will no longer receive emails from this list.",
        invalidToken: "This unsubscribe link is invalid or has expired.",
        loadFailed: "An error occurred. Please try again.",
        cannotProcess: "Cannot unsubscribe",
        poolName: "List: {{name}}",
        error: "The unsubscribe attempt failed.",
      },
    },
  },
  fr: {
    portal: {
      header: {
        searchPlaceholder: "Rechercher",
        notifications: "Notifications",
        trialDays: "{{count}} jour restant",
        trialDays_plural: "{{count}} jours restants",
        profile: "Profil",
        logout: "Se déconnecter",
        superAdmin: "Super Admin",
        language: "Langue",
        language_en: "Anglais",
        language_fr: "Français",
      },
      sidebar: {
        dashboard: "Tableau de bord",
        asapProfile: "ASAP Profil",
        asapCV: "ASAP CV",
        asapSkills: "ASAP Skills",
        asapPilot: "ASAP Pilot",
        asapPilotOpen: "ASAP Pilot ▾",
        asapPilotClosed: "ASAP Pilot ▸",
        pilotClients: "Entreprises",
        pilotNeeds: "Besoins",
        pilotConsultants: "Consultants",
        pilotConsultantTracking: "Suivi",
        pilotPerformance: "Mon activité",
        pilotTasks: "Tâches",
        pilotMatches: "Matchs",
        asapPartners: "ASAP Partners",
        asapPartnersOpen: "ASAP Partners ▾",
        asapPartnersClosed: "ASAP Partners ▸",
        partnersPools: "Pools",
        partnersSentRequests: "Demandes envoyées",
        partnersReceivedRequests: "Demandes reçues",
        partnersPartners: "Partenaires",
        partnersPoolsAndPartners: "Pools & Partenaires",
        partnersDashboard: "Tableau de bord",
        settings: "Paramètres",
      },
      trialBanner: {
        title: "Essai gratuit",
        message:
          "Il vous reste <b>{{count}} jour</b> d'essai. Renouvelez maintenant pour continuer à utiliser la plateforme.",
        message_plural:
          "Il vous reste <b>{{count}} jours</b> d'essai. Renouvelez maintenant pour continuer à utiliser la plateforme.",
        endsAt: " (fin le {{date}})",
        contactUs: "Nous contacter",
      },
      expiredModal: {
        title: "Essai expiré",
        body:
          "Votre période d'essai a expiré. Veuillez contacter votre administrateur ou notre équipe pour renouveler votre abonnement et retrouver l'accès à la plateforme.",
        ok: "OK",
        contactUs: "Nous contacter",
      },
      sessionExpiredModal: {
        title: "Session expirée",
        bodyExpired: "Votre session a expiré. Veuillez vous reconnecter pour continuer.",
        bodyInvalid: "Votre session est invalide. Veuillez vous reconnecter pour continuer.",
        action: "Se reconnecter",
      },
      dashboard: {
        eyebrow: "ASAP Hub",
        title: "Tableau de bord",
        errors: {
          fetchStats: "Erreur de chargement des statistiques",
        },
        filters: {
          businessUnits: {
            label: "Business units",
            all: "Toutes les Business Units",
            none: "Aucune BU sélectionnée",
            singleFallback: "1 BU",
            count: "{{count}} BUs",
            showSingle: "Afficher uniquement la BU actuelle",
            selectAll: "Sélectionner toutes les BUs",
            menuTitle: "Business Units",
          },
          person: {
            label: "Collaborateur",
            all: "Tous les collaborateurs",
            groups: {
              sales: "Commerciaux",
              recruiter: "Recruteurs",
            },
          },
          granularity: {
            label: "Granularité",
          },
        },
        noData: "Pas de données disponibles.",
        rangeLabel: "Période : {{start}} au {{end}}",
        totalLabel: "Total : {{value}}",
        cards: {
          cv: {
            eyebrow: "ASAP CV",
            subtitle: "CVs traités",
            empty: "Aucune activité CV pour la sélection actuelle.",
          },
          skills: {
            eyebrow: "ASAP SKILLS",
            subtitle: "Assessments envoyés",
            empty: "Aucune activité skills pour la sélection actuelle.",
          },
          partners: {
            eyebrow: "ASAP Partners",
            jobPostingsToPool: "Offres d'emploi aux candidats",
            jobPostingsToPartners: "Offres d'emploi aux partenaires",
            consultantsProposed: "Consultants proposés aux partenaires",
            signedRate: "Taux de signature : {{rate}}%",
            noData: "Aucune donnée Partners disponible.",
            period: {
              week: "Semaine",
              month: "Mois",
              all: "Tout",
            },
          },
        },
        pilotChart: {
          eyebrow: "ASAP PILOT",
          title: "Pipeline mensuel",
          subtitle: "Progression des besoins et des matchs par mois",
          legend: {
            needsCreated: "Besoins créés",
            needsCovered: "Besoins couverts",
            matchesProposed: "Matchs proposés",
            matchesConfirmed: "Signés",
          },
          empty: "Aucune activité pilot pour la sélection actuelle.",
        },
        detailTable: {
          eyebrow: "ASAP PILOT",
          title: "Détail des positionnements",
          subtitle: "Affectations en cours et suivi client",
          empty: "Aucun positionnement ouvert ne correspond aux filtres actuels.",
          prevWeek: "Semaine précédente",
          nextWeek: "Semaine suivante",
          allMatches: "Tous les matchs",
          thisWeek: "Cette semaine",
          showAll: "Tout afficher",
          columns: {
            consultant: "Consultant",
            client: "Client",
            jobTitle: "Intitulé du poste",
            status: "Statut du positionnement",
            sales: "Commercial",
            recruiter: "Recruteur",
          },
          statuses: {
            positioned: "Positionné",
            awaitingClientFeedback: "En attente du retour client",
            signed: "Signé",
          },
        },
        podiums: {
          recruiterTitle: "Podium recruteurs",
          salesTitle: "Podium commerciaux",
          recruiterMetric: "{{count}} placement",
          recruiterMetric_plural: "{{count}} placements",
          salesMetric: "{{count}} conversion",
          salesMetric_plural: "{{count}} conversions",
          empty: "Aucune donnée de classement disponible.",
        },
        drilldown: {
          monthLabel: "Mois du drill-down",
          button: "Ouvrir la vue hebdomadaire",
          help: "Ouvrir le mois sélectionné dans la performance hebdomadaire ASAP PILOT.",
          disabledMultiBu:
            "Sélectionnez une seule Business Unit pour ouvrir le drill-down hebdomadaire.",
        },
      },
      consultantPortal: {
        sidebar: {
          dashboard: "Tableau de bord",
          activityReports: "Activités / rapports",
          leaveRequests: "Demandes d'absence",
          assignmentFeedback: "Retour mission",
          calendar: "Calendrier",
        },
        common: {
          eyebrow: "Portail Consultant",
          loading: "Chargement...",
          monthNames: ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"],
        },
        dashboard: {
          pageTitle: "Tableau de bord",
          welcomeMessage: "Bienvenue, {{name}}",
          currentMonthTimesheet: "Feuille de temps du mois",
          recentTimesheets: "Feuilles de temps récentes",
          notSubmitted: "Non soumis",
          pendingLeaveRequests: "Demandes d'absence en attente",
          pendingLeaveDescription: "{{count}} demande(s) en attente de validation",
          noPendingLeave: "Aucune demande en attente",
          currentAssignment: "Mission en cours",
          noAssignment: "Aucune mission active",
          quickActions: "Actions rapides",
          fillTimesheet: "Saisir la feuille de temps",
          requestLeave: "Demander un congé",
          viewCalendar: "Voir le calendrier",
          billable: "facturables",
          nonBillable: "non facturables",
          statusLabels: {
            draft: "Brouillon",
            submitted: "Soumis",
            approved: "Approuvé",
            rejected: "Refusé",
          },
          toasts: {
            loadFailedTitle: "Échec du chargement du tableau de bord",
            loadFailedDescription: "Veuillez réessayer.",
          },
        },
        calendar: {
          pageTitle: "Calendrier",
          pageDescription: "Consultez votre planning, vos congés et les jours fériés.",
          legend: "Légende",
          workDay: "Jour travaillé",
          leaveDay: "Congé",
          publicHoliday: "Jour férié",
          weekend: "Week-end",
          today: "Aujourd'hui",
          dayNames: ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"],
          toasts: {
            loadFailedTitle: "Échec du chargement du calendrier",
            loadFailedDescription: "Veuillez réessayer.",
          },
        },
        activityReports: {
          pageTitle: "Rapports d'activité",
          pageDescription: "Saisissez vos jours facturables et non facturables par mois, puis soumettez pour validation.",
          createNewReport: "Créer un nouveau rapport",
          formTitleEdit: "Modifier le brouillon",
          formTitleNew: "Nouveau rapport",
          fields: {
            year: "Année",
            month: "Mois",
            billableDays: "Jours facturables",
            nonBillableDays: "Jours non facturables",
            notes: "Notes",
          },
          placeholders: {
            notes: "Contexte pour Sales ou RH",
          },
          buttons: {
            saveDraft: "Enregistrer le brouillon",
            submitReport: "Soumettre le rapport",
          },
          historyTitle: "Historique",
          empty: "Aucun rapport pour l'instant.",
          emptyHint: "Remplissez le formulaire pour enregistrer votre premier rapport mensuel.",
          table: {
            period: "Période",
            days: "Jours",
            status: "Statut",
            review: "Revue",
            edit: "Modifier",
          },
          statusLabels: {
            draft: "Brouillon",
            submitted: "Soumis",
            approved: "Approuvé",
            rejected: "Refusé",
          },
          toasts: {
            loadFailedTitle: "Échec du chargement des rapports d'activité",
            loadFailedDescription: "Veuillez réessayer.",
            submitted: "Rapport d'activité soumis",
            saved: "Rapport d'activité enregistré",
            saveFailedTitle: "Impossible d'enregistrer le rapport d'activité",
            saveFailedDescription: "Vérifiez le formulaire et réessayez.",
          },
        },
        assignmentFeedback: {
          pageTitle: "Retour mission",
          pageDescription: "Partagez votre retour trimestriel sur vos missions avec Sales et la RH.",
          empty: "Aucune mission éligible pour l'instant.",
          emptyHint: "Les cycles de feedback apparaîtront ici une fois que vous serez en mission.",
          clientAssignment: "Mission client",
          untitledNeed: "Besoin sans titre",
          ongoing: "en cours",
          ratingLabel: "Note",
          rateStarsAria: "Noter {{count}} étoiles",
          commentPlaceholder: "Décrivez le déroulement de la mission",
          submittedAt: "Soumis le {{date}}",
          buttons: {
            saveDraft: "Enregistrer le brouillon",
            submitFeedback: "Soumettre le retour",
          },
          feedbackSubmitted: "Retour soumis",
          availableEndOfCycle: "Le retour sera disponible en fin de période.",
          noCyclesYet: "Aucune période de retour n'a été créée pour cette mission. Les retours apparaîtront ici lorsque des périodes seront disponibles.",
          statusLabels: {
            draft: "Brouillon",
            submitted: "Soumis",
          },
          toasts: {
            loadFailedTitle: "Échec du chargement des retours mission",
            loadFailedDescription: "Veuillez réessayer.",
            submitted: "Retour mission soumis",
            saved: "Retour mission enregistré",
            saveFailedTitle: "Impossible d'enregistrer le retour mission",
            saveFailedDescription: "Vérifiez le formulaire et réessayez.",
          },
        },
        leaveRequests: {
          pageTitle: "Demandes d'absence",
          pageDescription: "Créez, soumettez et suivez vos demandes de congés.",
          newRequest: "Nouvelle demande",
          formTitleEdit: "Modifier la demande",
          formTitleNew: "Nouvelle demande",
          listTitle: "Demandes",
          fields: {
            leaveType: "Type d'absence",
            startDate: "Date de début",
            endDate: "Date de fin",
            notes: "Notes",
          },
          placeholders: {
            notes: "Contexte complémentaire",
          },
          buttons: {
            saveDraft: "Enregistrer le brouillon",
            submitRequest: "Soumettre la demande",
            edit: "Modifier",
            cancel: "Annuler",
          },
          leaveTypes: {
            annual_leave: "Congés annuels",
            sick_leave: "Arrêt maladie",
            unpaid_leave: "Congé sans solde",
            rtt: "RTT",
            other: "Autre",
          },
          empty: "Aucune demande d'absence pour l'instant.",
          emptyHint: "Soumettez une demande pour poser des congés.",
          table: {
            type: "Type",
            dates: "Dates",
            status: "Statut",
            review: "Revue",
          },
          statusLabels: {
            draft: "Brouillon",
            submitted: "Soumis",
            approved: "Approuvé",
            rejected: "Refusé",
            cancelled: "Annulé",
          },
          validation: {
            invalidDatesTitle: "Dates invalides",
            endBeforeStart: "La date de fin ne peut pas être avant la date de début.",
          },
          toasts: {
            loadFailedTitle: "Échec du chargement des demandes d'absence",
            loadFailedDescription: "Veuillez réessayer.",
            submitted: "Demande d'absence soumise",
            saved: "Demande d'absence enregistrée",
            saveFailedTitle: "Impossible d'enregistrer la demande d'absence",
            saveFailedDescription: "Vérifiez le formulaire et réessayez.",
            cancelled: "Demande d'absence annulée",
            cancelFailedTitle: "Impossible d'annuler la demande",
            cancelFailedDescription: "Veuillez réessayer.",
          },
        },
      },
      pilot: {
        unknownCompany: {
          label: "Inconnue",
        },
        import: {
          downloadTemplate: "Télécharger le modèle",
          uploadWorkbook: "Importer un classeur",
          uploadWorkbookTitle: "Importer un classeur",
          workbookDescription:
            "Téléversez un classeur à 5 onglets (Summary / Company / Client / Need / Consultant). L'onglet Summary est informatif et n'est pas traité.",
          workbookFileLabel: "Classeur (.xlsx)",
          dropzoneText: "Déposez le fichier .xlsx ici ou cliquez pour parcourir",
          dropzoneAccepted: "Seuls les fichiers .xlsx sont acceptés.",
          defaultManagerEmailLabel: "Email du responsable par défaut",
          defaultManagerEmailPlaceholder: "responsable@entreprise.com",
          summaryTitle: "Résumé de l'import",
          added: "Ajoutés",
          updated: "Mis à jour",
          deleted: "Supprimés",
          duplicates: "Doublons",
          skipped: "Ignorés",
          skippedRows: "Ignorés",
          errors: "Erreurs",
          errorRow: "Ligne",
          summary: {
            company: "Entreprises",
            client: "Contacts",
            need: "Besoins",
            consultant: "Consultants",
          },
          buttons: {
            close: "Fermer",
            import: "Importer",
            done: "Terminé",
          },
          toasts: {
            importCompleted: "Classeur importé",
            importFailedTitle: "Échec de l'import",
          },
          templateError: "Impossible de télécharger le modèle.",
        },
      },
      pilotStatuses: {
        client: {
          inactive_dormant: "Inactif / Dormant",
          active_account: "Compte actif",
          strategic_key_account: "Compte stratégique / clé",
          blacklisted_excluded: "Sur liste noire / exclu",
        },
        contact: {
          new_lead_pool: "Pool nouveaux prospects",
          lead_to_qualify: "Prospect à qualifier",
          cold_prospect: "Prospect froid",
          warm_prospect_opportunity: "Prospect chaud / Opportunité",
          active_client: "Contact actif",
          lost_dropped: "Perdu / Abandonné",
        },
        need: {
          open_early_stage: "Ouvert – phase initiale",
          open_rfp_received: "Ouvert – RFP reçu",
          closed_need_closed: "Fermé – besoin clôturé",
          abandoned: "Abandonné",
          postponed: "Reporté",
          reopened: "Réouvert",
        },
        consultant: {
          active: "Actif",
          bench: "Intercontrat",
          prospect: "Prospect",
          inactive: "Inactif",
        },
        consultantStaffing: {
          active: "Interne (actif)",
          recruiting: "Vivier (recrutement)",
          unavailable: "Indisponible",
          talentPool: "Vivier de talents",
          betweenAssignments: "Intercontrat",
          matchInProgress: "Matching en cours",
          presentedToClient: "Présenté au client",
          signed: "Signé",
          rejected: "Rejeté",
          tenderAbandoned: "Appel d'offres abandonné",
          departure: "Inactif (départ / indisponible)",
        },
        matchAssignment: {
          potential: "Potentiel",
          proposed: "Proposé",
          confirmed: "Confirmé",
          active: "Actif",
          completed: "Terminé",
          cancelled: "Annulé",
          lost: "Perdu",
        },
        matchPurchaseOrder: {
          pending: "En attente",
          received: "Reçu",
          not_required: "Non requis",
          cancelled: "Annulé",
        },
        interview: {
          scheduled: "Planifié",
          completed: "Terminé",
          cancelled: "Annulé",
        },
      },
      pilotPerformance: {
        common: {
          bucket: "Période",
          totals: "Total",
          empty: "Aucune donnée disponible.",
        },
        sections: {
          sales: "Activité commerciale",
          recruiter: "Activité recrutement",
        },
        metrics: {
          offMarketOpportunities: "Opportunités hors marché",
          referralOpportunities: "Opportunités recommandation / listing",
          recruiterProfilesReceived: "Profils reçus des recruteurs",
          avgAsapSkillsScoreProposed: "Score ASAP Skills moyen (proposés)",
          candidatesSubmitted: "Candidats soumis",
          clientInterviews: "Entretiens client",
          wins: "Missions gagnées",
          conversionOppToInterview: "Conversion : opportunités → entretiens",
          conversionOppToWin: "Conversion : opportunités → missions",
          staffingNeedsToCover: "Besoins à couvrir",
          profilesProposed: "Profils proposés",
          avgAtsScore: "Score ATS moyen",
          leadTimeOppToFirstProfile: "Délai jusqu'au premier profil (jours)",
          opportunitiesTotal: "Total opportunités",
          totalOpenRequirements: "Total des besoins ouverts",
          newOpenRequirements: "Nouveaux besoins ouverts",
          coveredRequirements: "Besoins couverts",
          profilesPresented: "Profils présentés",
          wonRequirements: "Besoins gagnés",
        },
        list: {
          pageTitle: "Mon activité",
          eyebrow: "ASAP PILOT",
          emptyHint: "Ajustez la période ou les filtres pour voir les indicateurs d'activité.",
          backToDashboard: "Retour au dashboard",
          clearDashboardFilter: "Retirer le filtre dashboard",
          dashboardFilterNotice: "Filtre dashboard actif pour {{count}} utilisateur.",
          dashboardFilterNotice_plural:
            "Filtre dashboard actif pour {{count}} utilisateurs.",
          filters: {
            bucket: "Regroupement",
            bucketWeek: "Semaine",
            bucketMonth: "Mois",
            bucketYear: "Année",
            fromDate: "Du",
            toDate: "Au",
            metricsAs: "Voir en tant que",
            metricsAsSales: "Commercial",
            metricsAsRecruiter: "Recruteur",
            roleScope: "Périmètre",
            roleScopeAll: "Tous",
            roleScopeSales: "Commercial",
            roleScopeRecruiter: "Recruteur",
            search: "Recherche",
            searchPlaceholder: "Rechercher un utilisateur…",
            refresh: "Rafraîchir",
          },
          summary: {
            loading: "Chargement…",
            totalUsers: "{{count}} utilisateurs ({{roleScope}})",
            myRange: "Période : {{start}} → {{end}}",
          },
          table: {
            user: "Utilisateur",
            role: "Rôle",
            opportunities: "Opportunités",
            recruitmentWorkload: "Charge de recrutement",
            matches: "Matchs",
            missionsStarted: "Missions démarrées",
            noItems: "Aucun utilisateur trouvé.",
          },
        },
        detail: {
          backAria: "Retour",
          backToList: "Retour",
          titleFallback: "Utilisateur",
          meTitle: "Mon activité",
          chart: {
            title: "Activité dans le temps",
            metric: "Indicateur",
            selectedPeriod: "Sélection : {{period}}",
            tooltip:
              "{{period}} — {{metric}} : {{value}}\nOpportunités : {{opportunities}}\nCharge de recrutement : {{workload}}\nMatchs : {{matches}}\nMissions démarrées : {{missions}}",
          },
          summary: {
            loading: "Chargement…",
            range: "Période : {{start}} → {{end}}",
          },
        },
      },
      tasks: {
        pageTitle: "Tâches",
        eyebrow: "ASAP Pilot",
        create: "Nouvelle tâche",
        createTask: "Nouvelle tâche",
        editTask: "Modifier la tâche",
        loadError: "Échec du chargement des tâches",
        saveError: "Échec de l'enregistrement de la tâche",
        deleteError: "Échec de la suppression de la tâche",
        created: "Tâche créée",
        updated: "Tâche mise à jour",
        deleted: "Tâche supprimée",
        titleRequired: "Le titre est requis",
        viewMine: "Mes tâches",
        viewAll: "Toutes les tâches",
        empty: "Aucune tâche trouvée.",
        emptyHint: "Créez une tâche pour suivre vos relances.",
        totalTasks: "Total",
        openTasks: "Ouvertes",
        inProgressTasks: "En cours",
        doneTasks: "Terminées",
        summary: {
          loading: "Chargement...",
          total: "{{count}} tâche(s)",
        },
        allStatuses: "Tous les statuts",
        allPriorities: "Toutes les priorités",
        titleCol: "Titre",
        statusCol: "Statut",
        priorityCol: "Priorité",
        dueDateCol: "Échéance",
        ownerCol: "Propriétaire",
        assigneeCol: "Assigné à",
        entityCol: "Lié à",
        actionsCol: "Actions",
        edit: "Modifier",
        delete: "Supprimer",
        titleLabel: "Titre",
        descriptionLabel: "Description",
        dueDateLabel: "Échéance",
        priorityLabel: "Priorité",
        statusLabel: "Statut",
        assigneeLabel: "Assigner à",
        assigneePlaceholder: "Non assigné",
        entityTypeLabel: "Lier à un type d'entité",
        entityTypePlaceholder: "Aucun",
        entityIdLabel: "Entité liée",
        entityLoading: "Chargement...",
        entityPlaceholder: "Sélectionner une entité",
        entityTypeFirst: "Sélectionnez d'abord un type",
        deleteConfirmTitle: "Supprimer la tâche",
        deleteConfirmBody: "Êtes-vous sûr de vouloir supprimer cette tâche ? Cette action est irréversible.",
        status: {
          open: "Ouverte",
          in_progress: "En cours",
          done: "Terminée",
          cancelled: "Annulée",
        },
        priority: {
          low: "Basse",
          normal: "Normale",
          high: "Haute",
        },
        entityType: {
          consultant: "Consultant",
          client: "Entreprise",
          need: "Besoin",
          match: "Match",
        },
        widget: {
          heading: "Mes tâches",
          subtitle: "Tâches ouvertes qui vous sont assignées ou que vous possédez",
          viewAll: "Voir toutes les tâches",
          empty: "Vous n'avez aucune tâche ouverte.",
          emptyHint: "Vous êtes à jour.",
          openCount_one: "{{count}} tâche ouverte",
          openCount_other: "{{count}} tâches ouvertes",
          dueLabel: "Échéance {{date}}",
          noDueDate: "Sans échéance",
        },
      },
      financialKpis: {
        title: "KPI financiers",
        loading: "Chargement des KPI financiers…",
        empty: "Aucune mission remportée avec des données financières.",
        consultant: "Consultant",
        client: "Client",
        need: "Besoin",
        assignmentStatus: "Statut",
        dailyRate: "TJM",
        costRate: "Coût journalier",
        billableDaysTarget: "Jours cibles",
        billableDaysActual: "Jours réels",
        revenue: "CA",
        cost: "Coût",
        grossMargin: "Marge",
        grossMarginPercent: "Marge %",
        summary: {
          totalRevenue: "CA total",
          totalCost: "Coût total",
          totalMargin: "Marge totale",
          avgMarginPercent: "Marge moyenne %",
          totalBillableDays: "Jours facturables totaux",
          assignmentCount: "Missions",
        },
      },
      portfolioAnalysis: {
        title: "Analyse de portefeuille",
        loading: "Chargement de l'analyse de portefeuille…",
        empty: "Aucune donnée de portefeuille disponible.",
        user: "Commercial",
        consultants: "Consultants",
        activeAssignments: "Missions actives",
        totalRevenue: "CA",
        totalMargin: "Marge",
        avgMarginPercent: "Marge moyenne %",
        utilization: "Taux d'utilisation %",
        companyAvg: "Moyenne entreprise",
        companyMargin: "Marge moyenne entreprise %",
        companyUtilization: "Taux d'utilisation moyen entreprise %",
      },
      comingSoon: {
        defaultTitle: "Bientôt disponible",
        defaultDescription:
          "Cette fonctionnalité est en cours de développement. Revenez bientôt !",
        goBack: "Retour",
      },
      asapProfile: {
        eyebrow: "ASAP Profile",
        pageTitle: "ASAP Profil",
        emptyList:
          "Aucune recherche. Sélectionnez un besoin PILOT ou téléversez un AO pour commencer.",
        list: {
          title: "Recherches",
          loading: "Chargement des recherches…",
          newSearch: "Nouvelle recherche",
          searchPlaceholder: "Rechercher par titre ou client",
          noMatch: "Aucune recherche ne correspond à votre filtre.",
          untitled: "Recherche sans titre",
          cardViewCandidates: "Voir les candidats",
          forkBadge: "depuis #{{parent}}",
          forkBadgeTooltip:
            "Re-lancée à partir de la recherche #{{parent}} avec des critères modifiés. L'original reste dans la liste.",
        },
        detail: {
          title: "Détails de la recherche",
          newSearchTitle: "Nouvelle recherche",
          disabledMessage:
            "Cliquez sur « + Nouvelle recherche » pour sélectionner un besoin PILOT ou téléverser un AO.",
          extractingMessage: "Analyse de l'AO… extraction des critères.",
          extractFailedMessage:
            "L'extraction automatique des critères a échoué. Vous pouvez éditer les champs manuellement ci-dessous.",
          inProgressMessage:
            "Recherche des candidats en cours… cela peut prendre jusqu'à une minute.",
        },
        seniority: {
          junior: "Junior",
          mid: "Confirmé",
          senior: "Senior",
          expert: "Expert",
        },
        status: {
          draft: "Brouillon",
          extracted: "Extrait",
          validated: "Validé",
          queued: "Recherche en cours",
          running: "Recherche en cours",
          completed: "Terminé",
          failed: "Échec",
        },
        errors: {
          loadFailed: "Impossible de charger les recherches.",
          detailLoadFailed: "Impossible de charger cette recherche.",
          profilesLoadFailed: "Impossible de charger les profils candidats.",
        },
        create: {
          title: "Créer une nouvelle recherche",
          orLabel: "ou",
          creating: "Création de la recherche…",
          successToast: "Recherche créée — extraction des critères…",
          tabs: {
            pilot: "Depuis un besoin PILOT",
          },
          upload: {
            dropText: "Glissez-déposez un PDF ou DOCX, ou cliquez pour parcourir.",
            acceptedFormats: "Formats acceptés : .pdf, .docx (max 10 Mo)",
          },
          pilot: {
            searchPlaceholder: "Rechercher par titre ou client",
          },
          errors: {
            createFailed: "Impossible de créer la recherche.",
            extractFailed:
              "Recherche créée mais l'extraction des critères a échoué. Éditez les champs manuellement avant de valider.",
            fileTooLarge: "Le fichier est trop volumineux (max 10 Mo).",
            unsupportedType: "Seuls les fichiers PDF et DOCX sont acceptés.",
            needNotFound: "Ce besoin est introuvable.",
            needsLoadFailed: "Impossible de charger les besoins PILOT.",
            searchBudgetExceededTitle: "Budget mensuel de recherches atteint",
            searchBudgetExceededBody:
              "Votre entreprise a utilisé {{used}} recherches sur {{budget}} ce mois-ci. Contactez votre administrateur pour augmenter le budget.",
          },
        },
        criteria: {
          addSkillAria: "Ajouter une compétence",
          skillsPlaceholder: "Ajoutez une compétence et appuyez sur Entrée",
          fields: {
            rfpTitle: "Titre de l'AO",
            client: "Client",
            seniority: "Séniorité",
            seniorityPlaceholder: "— sélectionner —",
            experienceYears: "Années d'expérience",
            geoScope: "Périmètre géographique",
            geoScopePlaceholder: "— sélectionner —",
            geoCountry: "Pays",
            geoCountryPlaceholder: "ex. France",
            geoRegion: "Région",
            geoRegionPlaceholder: "— sélectionner une région —",
            geoCity: "Ville",
            geoCityPlaceholder: "ex. Paris",
            geoSection: "Localisation",
            summary: "Résumé",
            skills: "Compétences",
          },
          experience: {
            any: "Indifférent",
            "0_2": "0 à 2 ans",
            "2_4": "2 à 4 ans",
            "4_6": "4 à 6 ans",
            "6_10": "6 à 10 ans",
            "10_plus": "10 ans et plus",
          },
          experienceSlider: {
            indifferent: "Indifférent",
            anyValue: "Indifférent",
            range: "{{min}} à {{max}} ans",
          },
          geoScope: {
            city: "Même ville",
            region: "Même région",
            country: "Même pays",
            remote: "Aucune limite (télétravail)",
          },
          skillsTiers: {
            required: "Obligatoires",
            optional: "Optionnelles",
          },
          skillTierToggle: {
            markOptional: "Marquer comme optionnelle",
            markRequired: "Marquer comme obligatoire",
          },
          skillReorderHint:
            "Glissez les étiquettes pour les classer par importance.",
          skillReorderAria: "Glisser pour réordonner {{label}}",
          skillTypes: {
            technical: "Technique",
            functional: "Fonctionnelle",
            business: "Métier",
          },
          errors: {
            requiredSkillsEmpty:
              "Au moins une compétence obligatoire est requise.",
            invalidSeniority: "Valeur de séniorité invalide.",
            invalidExperienceYears: "Plage d'années d'expérience invalide.",
            invalidGeoScope: "Périmètre géographique invalide.",
            geoCityRequired:
              "Une ville est requise lorsque le périmètre est « Même ville ».",
            geoRegionRequired:
              "Une région est requise lorsque le périmètre est « Même région ».",
            geoCountryRequired:
              "Un pays est requis lorsque le périmètre est « Même pays ».",
            validateFailed: "Impossible d'enregistrer les critères.",
            regionsLoadFailed:
              "Impossible de charger les régions pour ce pays. Vous pouvez réessayer ci-dessous.",
          },
          regionsRetry: "Réessayer le chargement des régions",
        },
        run: {
          runSearch: "Lancer la recherche",
          startSearch: "Lancer la recherche",
          runAgain: "Relancer",
          successToast: "Recherche terminée.",
          startedToast:
            "Recherche lancée. Nous vous préviendrons quand les résultats seront prêts.",
          completedToast:
            "Recherche terminée. Consultez les candidats depuis la liste.",
          failedToast:
            "Échec de la recherche. Consultez le détail pour la raison.",
          rerunDialog: {
            title: "Relancer cette recherche ?",
            body:
              "Cette recherche est déjà terminée. Relancer ne lancera pas de nouvelle recherche de profils, seulement un ré-affichage des profils existants.",
            keep: "Annuler",
            confirm: "Ré-afficher",
            titleDirty: "Créer une nouvelle recherche avec ces critères ?",
            bodyDirty:
              "Vous avez modifié les critères. Une nouvelle recherche sera créée avec les critères mis à jour et lancée. La recherche d'origine et ses candidats restent disponibles dans la liste. Cette nouvelle exécution est une recherche de profils facturable.",
            confirmDirty: "Créer et lancer",
          },
          errors: {
            generic: "Impossible de lancer la recherche.",
            authError: "Échec d'authentification de la recherche de profils. Contactez votre administrateur.",
            rateLimit:
              "Limite de débit atteinte. Réessayez dans quelques minutes.",
            notConfigured: "La recherche de profils n'est pas configurée pour cet environnement.",
            notValidated:
              "Validez d'abord les critères avant de lancer la recherche.",
            billingExhausted: "Votre forfait de crédits de profils est épuisé. Contactez votre administrateur pour le recharger.",
          },
          failedReasons: {
            pdl_rate_limit: "Recherche échouée : limite de débit atteinte. Patientez quelques minutes et réessayez.",
            pdl_billing_exhausted: "Recherche échouée : crédits de profils épuisés. Contactez votre administrateur.",
            pdl_auth_error: "Recherche échouée : erreur d'authentification de la recherche de profils.",
            pdl_not_configured: "Recherche échouée : la recherche de profils n'est pas configurée.",
          },
        },
        profiles: {
          empty:
            "Aucun profil correspondant trouvé. Essayez d'élargir les critères et relancez.",
          emptyV2:
            "Nous avons cherché parmi les lieux et compétences que vous avez sélectionnés et n'avons trouvé aucun profil correspondant à ces critères. Essayez d'élargir les compétences ou la zone géographique.",
          modalTitle: "Profils candidats",
          viewCandidatesEmpty: "Voir les candidats",
          notRunYet:
            "Lancez la recherche pour charger les profils candidats.",
        },
        batch: {
          legendTitle: "Lots de chargement :",
          item: "Lot {{number}}",
        },
        profileCard: {
          inDbBadge: "Déjà dans PILOT",
          inDbTooltip:
            "Cette personne est déjà un consultant dans PILOT — cliquez pour ouvrir.",
          addToPilot: "Ajouter à PILOT",
          addedToast: "Profil ajouté à PILOT.",
          unknownName: "Candidat inconnu",
          linkedin: "LinkedIn",
          errors: {
            addFailed: "Impossible d'ajouter ce profil à PILOT.",
            alreadyMatched: "Ce profil est déjà lié à un consultant.",
          },
        },
        loadMore: {
          label: "Charger 12 de plus",
          loadedCount: "{{count}} chargés",
          callsRemaining: "{{count}} appels restants",
          exhausted:
            "Tous les profils correspondants sont chargés ({{count}}). Aucun autre profil ne correspond à ces critères.",
          successToast: "{{count}} nouveaux profils ajoutés.",
          noMoreToast: "Aucun nouveau profil sur la page suivante.",
          errorToast: "Impossible de charger plus de profils.",
          limitReachedToast:
            "Limite de chargement atteinte pour cette recherche ({{cap}}). Lancez une nouvelle recherche pour en récupérer davantage.",
          rebillDialog: {
            title: "Lancer une nouvelle recherche de profils ?",
            body:
              "Ceci lancera une nouvelle recherche de profils facturée pour récupérer les 12 candidats suivants.",
            cancel: "Annuler",
            confirm: "Continuer",
          },
        },
        statsChart: {
          title: "Activité (6 derniers mois)",
          tooltip: "{{value}} recherches",
        },
        export: {
          button: "Exporter Excel",
          errorTitle: "Export impossible",
        },
      },
      documents: {
        title: "Documents",
        noDocuments: "Aucun document trouvé.",
        table: {
          documentType: "Type de document",
          requestId: "ID de requête",
          status: "Statut",
          createdAt: "Créé le",
          actions: "Actions",
        },
        download: "Télécharger",
        downloading: "Téléchargement",
        errors: {
          fetchTitle: "Erreur lors du chargement des documents",
          fetchDescription: "Impossible de charger les documents.",
          downloadTitle: "Échec du téléchargement",
          notReady: "Le document n'est pas encore prêt à être téléchargé.",
          forbidden: "Vous n'avez pas l'autorisation d'accéder à ce document.",
          notFound: "Document introuvable.",
          generic: "Impossible de télécharger le fichier.",
        },
      },
      asapCV: {
        pageTitle: "ASAP CV",
        eyebrow: "ASAP CV",
        linkedConsultantBanner: "Ce CV sera rattaché à {{name}}",
        documentsTitle: "Documents",
        emptyHint: "Importez un CV depuis le formulaire pour générer des documents.",
        searchPlaceholder: "Rechercher par prénom/nom",
        pagination: {
          previous: "Précédent",
          next: "Suivant",
          pageLabel: "Page {{page}}",
        },
        card: {
          unknownPosition: "Poste inconnu",
          ageLabel: "{{count}} ans",
          ageNA: "N/A",
          updatedAt: "Mis à jour : {{date}}",
          editNameTooltip: "Modifier le nom",
          edit: {
            firstNamePlaceholder: "Prénom",
            lastNamePlaceholder: "Nom",
            saveAria: "Enregistrer",
            cancelAria: "Annuler",
          },
          deliverables: {
            originalCv: "CV original mis en forme",
            aiCv: "CV mis en forme et amélioré par l'IA",
            oralGuide: "Guide Oral",
            qa: "Questions & Réponses",
            aoScoring: "Scoring Appel d'Offre",
          },
        },
        chart: {
          title: "Activité (6 derniers mois)",
          tooltip: "{{count}} CV",
          tooltip_plural: "{{count}} CVs",
        },
        alerts: {
          businessUnitRequiredTitle: "Business Unit requise",
          businessUnitRequiredDescription:
            "Veuillez sélectionner une Business Unit avant de soumettre cette demande.",
          noBusinessUnitTitle: "Aucune Business Unit disponible",
          noBusinessUnitDescription:
            "Votre compte doit être rattaché à une Business Unit avant de pouvoir soumettre cette demande. Contactez un administrateur.",
        },
        form: {
          panelTitle: "Nouvelle demande de CV",
          uploadCvLabel: "Ajouter un ou plusieurs CV",
          uploadCvDropText:
            "Glissez-déposez vos CV ici ou cliquez pour sélectionner",
          uploadJobDescLabel: "Appel d’offre (optionnel)",
          uploadJobDescDropText:
            "Glissez-déposez l’appel d’offre ici ou cliquez pour sélectionner",
          acceptedFormats: "Formats acceptés : PDF, DOCX",
          languageLabel: "Langue",
          languageOptions: {
            fr: "Français",
            en: "Anglais",
          },
          jobTypeLabel: "Type de poste",
          jobTypePlaceholder: "Sélectionner",
          jobTypeOptions: {
            moa: "MOA / Business Analyst",
            "chef-de-projet": "Chef de Projet",
            "product-owner": "Product Owner / Scrum Master",
            "consultant-fonctionnel": "Consultant Fonctionnel",
            "consultant-technique": "Consultant Technique / Développeur",
            "architecte-si": "Architecte SI / Consultant Data",
            "expert-securite": "Expert Sécurité / DevOps",
            pmo: "PMO",
            "finance-comptabilite": "Finance & Comptabilité",
            "rh-paie": "RH & Paie",
            "supply-chain": "Supply Chain & Logistique",
            "marketing-commercial": "Marketing & Commercial",
            "gestion-risques": "Gestion des Risques & Conformité",
            "juridique-fiscalite": "Juridique & Fiscalité",
          },
          templateLabel: "Modèle de CV",
          deliverablesLabel: "Livrables souhaités",
          deliverables: {
            cvOriginal: "CV original mis en forme",
            cvVersionIA: "CV mis en forme et amélioré par l'IA.",
            scoringAppelOffre: "Scoring appel d’offre",
            guideOral: "Guide oral",
            questionsEntretiens: "Questions d’entretien",
          },
          candidateEmailsLabel:
            "Candidat(s) (séparés par des virgules)",
          candidateEmailsPlaceholder: "email1@example.com, email2@example.com",
          submitLabel: "Envoyer les fichiers",
          submitLoading: "Envoi en cours",
          loadingConsultantCv: "Chargement du CV depuis le profil consultant...",
        },
        toasts: {
          trialExpiredTitle: "Essai expiré",
          trialExpiredDescription:
            "Votre période d'essai a expiré. Contactez-nous pour renouveler l'accès.",
          fetchErrorTitle: "Erreur lors du chargement des documents",
          fetchErrorDescription: "Impossible de charger les documents.",
          downloadFailedTitle: "Échec du téléchargement",
          cvAutoLoaded: "CV chargé depuis le profil consultant",
          downloadErrors: {
            notReady: "Le document n'est pas encore prêt à être téléchargé.",
            forbidden:
              "Vous n'avez pas l'autorisation d'accéder à ce document.",
            notFound: "Document introuvable.",
            generic: "Impossible de télécharger le fichier.",
          },
          nameEdit: {
            missingFields: "Renseignez au moins un des champs",
            success: "Nom mis à jour",
            errorTitle: "Échec de la mise à jour",
            genericError: "Impossible de mettre à jour le nom.",
            emptyUpdate: "Aucun champ à mettre à jour.",
            forbidden: "Non autorisé pour cette Business Unit.",
            notFound: "Groupe introuvable.",
            trialExpired: "Votre période d'essai a expiré.",
          },
          submit: {
            noFiles: "Ajoutez au moins un CV",
            noDeliverables: "Sélectionnez au moins un livrable",
            invalidEmails: "Emails invalides : {{list}}",
            invalidFileTypeTitle: "Type de fichier non supporté",
            invalidFileTypeDescription: "Formats acceptés : PDF, DOCX.",
            invalidFileTypeDescriptionWithList:
              "Fichiers refusés : {{list}}. Formats acceptés : PDF, DOCX.",
            summary: "{{success}}/{{total}} fichiers soumis avec succès",
            errorTitle: "Erreur lors de l'envoi",
          },
        },
      },
      asapSkills: {
        pageTitle: "ASAP Skills",
        eyebrow: "ASAP Skills",
        linkedConsultantBanner: "Cette évaluation sera rattachée à {{name}}",
        documentsTitle: "Assessments",
        searchPlaceholder: "Rechercher par prénom/nom",
        noAssessments: "Aucun assessment trouvé.",
        emptyHint: "Envoyez une évaluation depuis le formulaire pour la voir ici.",
        pagination: {
          previous: "Précédent",
          next: "Suivant",
          pageLabel: "Page {{page}}",
        },
        card: {
          status: {
            preparing: "Préparation",
            sent: "Envoyé",
            completed: "Terminé",
            started: "Démarré",
            completedPendingScore: "Terminé (scoring)",
            reportReady: "Rapport prêt",
            notStarted: "Non démarré",
          },
          scoreTag: "Score : {{score}}/100",
          updatedAt: "Mis à jour : {{date}}",
          noFiles: "Aucun fichier",
          downloads: {
            assessmentJob: "Fiche de poste",
            assessmentReport: "Rapport d'assessment",
          },
        },
        chart: {
          title: "Activité (6 derniers mois)",
          tooltip: "{{count}} assessment",
          tooltip_plural: "{{count}} assessments",
        },
        alerts: {
          businessUnitRequiredTitle: "Business Unit requise",
          businessUnitRequiredDescription:
            "Veuillez sélectionner une Business Unit avant de soumettre cette demande.",
          noBusinessUnitTitle: "Aucune Business Unit disponible",
          noBusinessUnitDescription:
            "Votre compte doit être rattaché à une Business Unit avant de pouvoir soumettre cette demande. Contactez un administrateur.",
          maxSkillsReached:
            "Limite maximale atteinte. Désélectionnez une compétence pour en choisir une autre.",
          loadJobMatrixFailed: "Impossible de charger la matrice de compétences",
        },
        form: {
          panelTitle: "Nouvelle évaluation",
          uploadJobDescLabel: "Fiche de poste",
          languageLabel: "Langue",
          languageOptions: {
            fr: "Français",
            en: "Anglais",
          },
          uploadJobDescDropText:
            "Glissez-déposez la description de poste ici ou cliquez pour sélectionner",
          acceptedFormats: "Formats acceptés : PDF, DOCX",
          evaluationSettingsTitle: "Paramètres d'évaluation",
          autoModeLabel: "Mode automatique",
          emptyMatrixHint:
            "Téléchargez une description de poste pour charger des compétences suggérées.",
          durationLabel: "Durée du test (minutes)",
          durationMin: "Min : {{value}} min",
          durationSelected: "Sélectionné : {{value}} min",
          durationMax: "Max : {{value}} min",
          durationHint:
            "Sélectionnez des compétences pour définir la durée.",
          emailsSectionLabel: "Candidat(s)",
          candidateNamePlaceholder: "Prénom Nom",
          candidateEmailPlaceholder: "exemple@domaine.com",
          removeCandidateAria: "Supprimer",
          addCandidateButton: "Ajouter un candidat",
          difficulty: {
            junior: "Junior (0–2 ans)",
            confirme: "Confirmé (2–4 ans)",
            senior: "Senior (4–7 ans)",
            seniorConfirme: "Senior confirmé (7–10 ans)",
            expert: "Expert (10 ans +)",
          },
          importance: {
            label: "Importance",
            primary: "Principale",
            secondary: "Secondaire",
            help: "Les compétences principales sont indispensables et pèsent davantage dans l’évaluation. Les compétences secondaires sont souhaitables et ont un poids moindre.",
            helpAria: "Que signifient Principale et Secondaire ?",
          },
          addSkill: {
            button: "Ajouter une compétence",
            modalTitle: "Ajouter une compétence",
            nameLabel: "Nom de la compétence",
            namePlaceholder: "Ex. React, Scrum, Banque…",
            typeLabel: "Type",
            typeOptions: {
              technical: "Technique",
              functional: "Fonctionnel",
              business: "Métier / Domaine",
              role: "Rôle",
            },
            difficultyLabel: "Niveau",
            cancel: "Annuler",
            save: "Enregistrer",
            missingFields: "Veuillez renseigner le nom, le type et le niveau.",
            autoModeDisabledTitle: "Mode automatique désactivé",
            autoModeDisabledDescription:
              "Pour ajouter une compétence, nous sommes passés en mode manuel afin d’envoyer vos modifications.",
          },
          codingSkillTooltip: "Cette compétence inclut des questions de code dans l'éditeur de code.",
          sections: {
            technical: "Évaluation technique",
            functional: "Évaluation fonctionnelle",
            business: "Évaluation métier",
            role: "Rôle",
          },
          submitLabel: "Envoyer les invitations",
          submitLoading: "Envoi en cours",
        },
        toasts: {
          fetchErrorTitle: "Erreur lors du chargement des documents",
          fetchErrorDescription: "Impossible de charger les documents.",
          trialExpiredTitle: "Essai expiré",
          trialExpiredDescription:
            "Votre période d'essai a expiré. Contactez-nous pour renouveler l'accès.",
          downloadFailedTitle: "Échec du téléchargement",
          downloadErrors: {
            notReady: "Le document n'est pas encore prêt à être téléchargé.",
            forbidden:
              "Vous n'avez pas l'autorisation d'accéder à ce document.",
            notFound: "Document introuvable.",
            generic: "Impossible de télécharger le fichier.",
          },
          loadJobMatrixFailed: "Impossible de charger la matrice de compétences",
          invalidEmails: "Veuillez entrer des adresses email valides",
          noCompetences:
            "Veuillez sélectionner au moins une compétence à évaluer",
          noDetectedSkillsTitle: "Aucune compétence détectée",
          noDetectedSkillsDescription:
            "Téléchargez une description de poste pour activer le mode automatique.",
          invitationsSent: "Invitations envoyées",
          submitErrorTitle: "Erreur lors de l'envoi",
        },
      },
      pilotNeeds: {
        list: {
          pageTitle: "Besoins entreprises",
          eyebrow: "ASAP PILOT",
          emptyHint: "Créez un besoin pour commencer à suivre la demande client.",
          actions: {
            importFromFile: "Bulk Upload",
            upload: "Uploader",
            create: "Créer un besoin",
            confirmDelete: "Confirmer la suppression",
          },
          summary: {
            loading: "Chargement...",
            total: "{{count}} au total",
          },
          filters: {
            searchPlaceholder: "Rechercher par entreprise ou titre...",
            clientPlaceholder: "Filtre : Entreprise",
            statusPlaceholder: "Filtre : Statut",
            salesPlaceholder: "Filtre : Commercial",
            recruiterPlaceholder: "Filtre : Recruteur",
            pageSizeSuffix: "/page",
            dateRangeLabel: "Plage de dates",
            startDateRangeLabel: "Plage de dates de début",
            endDateRangeLabel: "Plage de dates de fin",
            from: "Du",
            to: "au",
            categoryPlaceholder: "Filtre : Catégorie",
            minSalaryPlaceholder: "Salaire min.",
            maxSalaryPlaceholder: "Salaire max.",
            hasMatchesPlaceholder: "Filtre : Matchs",
            hasMatchesAny: "Tous",
            hasMatchesWith: "Avec matchs",
            hasMatchesWithout: "Sans matchs",
          },
          table: {
            date: "Date",
            title: "Titre",
            client: "Entreprise",
            skills: "Compétences",
            status: "Statut",
            category: "Catégorie",
            acquisitionChannels: "Canal d'acquisition",
            contractTypes: "Type de contrat",
            salaryRange: "Salaire (min-max)",
            startDate: "Date de début",
            endDate: "Date de fin",
            sales: "Commercial",
            recruiter: "Recruteur",
            daysOpen: "Jours ouverts",
            loading: "Chargement...",
            noItems: "Aucun besoin trouvé",
          },
          pagination: {
            first: "Première",
            prev: "Précédente",
            next: "Suivante",
            last: "Dernière",
            pageLabel: "Page {{page}} sur {{total}}",
          },
        },
        detail: {
          backAria: "Retour aux besoins",
          close: "Fermer",
          matchesPanel: {
            emptyHint: "Générez des matchs ou ajoutez des consultants pour alimenter ce besoin.",
          },
          tabs: {
            description: "Description",
            skills: "Compétences",
            matches: "Matchs",
            pairings: "Consultants",
            comments: "Commentaires",
          },
          pairings: {
            title: "Consultants associés",
            actions: {
              add: "Ajouter des consultants",
            },
            modal: {
              title: "Ajouter des consultants à ce besoin",
              searchPlaceholder: "Rechercher des consultants...",
              selectedCount: "Sélectionnés : {{count}} / {{max}}",
              noConsultants: "Aucun consultant trouvé",
              total: "{{count}} consultants trouvés",
              cancelButton: "Annuler",
              primaryButton: "Ajouter",
              roleColumn: "Rôle",
            },
            toasts: {
              addSuccess: "{{count}} consultant ajouté",
              addSuccess_plural: "{{count}} consultants ajoutés",
              addFailedTitle: "Impossible d'ajouter le(s) consultant(s)",
              addFailedDescription: "Veuillez réessayer",
              loadConsultantsFailedTitle: "Impossible de charger les consultants",
              loadConsultantsFailedDescription: "Veuillez réessayer",
            },
          },
          toasts: {
            commentAdded: "Commentaire ajouté",
            commentFailedTitle: "Impossible d'ajouter le commentaire",
            commentFailedDescription: "Veuillez réessayer",
          },
          notFound: "Besoin introuvable",
          loading: "Chargement...",
          confirmDelete: "Êtes-vous sûr de vouloir supprimer ce besoin ?",
          header: {
            edit: "Modifier",
            delete: "Supprimer",
          },
          sections: {
            title: "Titre",
            client: "Entreprise",
            date: "Date",
            category: "Catégorie",
            acquisitionChannels: "Canal d'acquisition",
            salaryRange: "Salaire (min-max)",
            status: "Statut",
            contractTypes: "Type de contrat",
            startDate: "Date de début",
            asap: "ASAP",
            endDate: "Date de fin",
            sales: "Commercial",
            recruiter: "Recruteur(s)",
            contactPerson: "Personne de contact",
            daysOpen: "Jours ouverts",
            description: "Description",
            noDescription: "Aucune description fournie",
            technicalSkills: "Compétences techniques",
            functionalSkills: "Compétences fonctionnelles",
            businessSkills: "Compétences métier",
            createdAt: "Créé le",
            updatedAt: "Dernière mise à jour",
          },
          comments: {
            title: "Commentaires",
            noComments: "Aucun commentaire pour ce besoin pour le moment.",
            actions: {
              submit: "Ajouter un commentaire",
              reply: "Répondre",
              cancel: "Annuler",
            },
            placeholders: {
              new: "Écrire un commentaire…",
              reply: "Écrire une réponse…",
            },
          },
        },
        form: {
          modalTitleCreate: "Créer un besoin entreprise",
          modalTitleEdit: "Modifier un besoin entreprise",
          sections: {
            general: "Informations générales",
            people: "Interlocuteurs",
            dates: "Dates",
            compensation: "Rémunération",
            channels: "Canaux & contrat",
            skills: "Compétences",
          },
          fields: {
            title: "Titre",
            client: "Entreprise",
            status: "Statut",
            acquisitionChannels: "Canal d'acquisition",
            contractTypes: "Type de contrat",
            minSalary: "Salaire min.",
            maxSalary: "Salaire max.",
            startDate: "Date de début",
            asap: "ASAP (démarrage dès que possible)",
            endDate: "Date de fin",
            sales: "Commercial",
            recruiter: "Recruteur(s)",
            contactPerson: "Personne de contact",
            description: "Description",
            technicalSkills: "Compétences techniques",
            functionalSkills: "Compétences fonctionnelles",
            businessSkills: "Compétences métier",
          },
          placeholders: {
            title: "Ex : Senior Data Engineer",
            contactPerson: "Sélectionner un contact...",
            minSalary: "Ex : 70000",
            maxSalary: "Ex : 90000",
            description: "Description détaillée",
            technicalSkill: "Ajouter une compétence technique",
            functionalSkill: "Ajouter une compétence fonctionnelle",
            businessSkill: "Ajouter une compétence métier",
          },
          buttons: {
            cancel: "Annuler",
            addSkill: "Ajouter",
            saveChanges: "Enregistrer les modifications",
            create: "Créer",
          },
        },
        importFromFile: {
          modalTitle: "Importer des besoins depuis un CSV/XLSX",
          description:
            "Téléversez un fichier CSV ou XLSX. Colonnes supportées : entreprise, titre, statut, dates, salaires et informations de contact.",
          fields: {
            file: "Fichier",
          },
          statusLabels: {
            total: "Total",
            added: "Ajoutés",
            updated: "Mis à jour",
            deleted: "Supprimés",
            duplicate: "Doublons",
          },
          dropzone: {
            text:
              "Glissez-déposez un fichier CSV/XLSX ici ou cliquez pour sélectionner",
            accepted: "Acceptés : .csv, .xlsx",
          },
          buttons: {
            cancel: "Annuler",
            import: "Importer",
          },
          toasts: {
            importCompleted: "Import terminé",
            importFailedTitle: "Échec de l'import",
            importFailedDescription: "Veuillez réessayer",
            invalidFileTypeTitle: "Type de fichier invalide",
            invalidFileTypeDescription: "Seuls les fichiers CSV ou XLSX sont acceptés",
          },
        },
        upload: {
          modalTitlePdf: "Importer un besoin depuis un PDF",
          modalTitleFile: "Importer un besoin depuis un fichier",
          descriptionPdf:
            "Téléversez un fichier PDF contenant l'offre de mission. Le système extraira les informations et créera un besoin entreprise.",
          descriptionFile:
            "Téléversez un fichier PDF ou DOCX contenant l'offre de mission. Le système extraira les informations et créera un besoin entreprise.",
          clientLabel: "Entreprise",
          clientPlaceholder: "Sélectionner une entreprise",
          fileLabel: "Fichier",
          statusLabels: {
            total: "Total",
            added: "Ajoutés",
            updated: "Mis à jour",
            deleted: "Supprimés",
            duplicate: "Doublons",
          },
          dropzonePdf:
            "Glissez-déposez un PDF ici ou cliquez pour sélectionner",
          dropzoneFile:
            "Glissez-déposez un PDF ou DOCX ici ou cliquez pour sélectionner",
          acceptedPdf: "Accepté : .pdf",
          acceptedFile: "Acceptés : .pdf, .docx",
          buttons: {
            cancel: "Annuler",
            upload: "Uploader",
          },
        },
        createWizard: {
          stepIndicator: {
            document: "Document",
            details: "Détails",
          },
          step1Title: "Créer un besoin entreprise",
          step2Title: "Compléter les détails du besoin",
          step1Description:
            "Vous pouvez téléverser un document PDF ou DOCX pour extraire les informations du besoin, ou continuer sans téléverser et remplir le formulaire manuellement.",
          buttons: {
            continueWithoutUpload: "Continuer sans téléverser",
            continueWithDocument: "Continuer avec le document",
            continueWithUpload: "Continuer avec le téléversement",
            back: "Retour",
          },
        },
        toasts: {
          loadListFailed: "Échec du chargement des besoins",
          loadDetailFailed: "Échec du chargement du besoin",
          loadDetailRefreshFailed: "Échec de l'actualisation du besoin",
          deleteSuccess: "Besoin supprimé",
          deleteFailedTitle: "Échec de la suppression",
          deleteFailedDescription: "Veuillez réessayer",
          confirmDeleteSuccess: "Besoin supprimé définitivement",
          confirmDeleteFailedTitle: "Échec de la confirmation",
          confirmDeleteFailedDescription: "Veuillez réessayer",
          operationFailedTitle: "Échec de l'opération",
          loadClientNeedsFailed: "Échec du chargement des besoins de l'entreprise",
          created: "Besoin créé",
          updated: "Besoin mis à jour",
          importCompleted: "Import terminé",
          importFailedTitle: "Échec de l'import",
          uploadCompleted: "Besoin importé avec succès",
          uploadFailedTitle: "Échec de l'import",
          invalidFileTypeTitle: "Type de fichier invalide",
          invalidFileTypeDescriptionPdf:
            "Seuls les fichiers PDF sont acceptés",
          invalidFileTypeDescriptionFile:
            "Seuls les fichiers PDF ou DOCX sont acceptés",
          commentAdded: "Commentaire ajouté",
          commentFailedTitle: "Impossible d'ajouter le commentaire",
          commentFailedDescription: "Veuillez réessayer"
        },
      },
      pilotClients: {
        list: {
          pageTitle: "Entreprises",
          pageTitleContacts: "Clients",
          pageTitleStatus: "Statut entreprise",
          pageTitleContactStatus: "Statut des contacts",
          eyebrow: "ASAP PILOT",
          emptyHint: "Créez une entreprise ou un contact pour commencer à constituer votre base clients.",
          view: {
            companies: "Entreprises",
            contacts: "Clients",
            companyStatus: "Statut entreprise",
            contactStatus: "Statut des contacts",
          },
          actions: {
            import: "Bulk Upload",
            create: "Créer une entreprise",
            createContactNoCompany: "Ajouter un contact (sans entreprise)",
            confirmDelete: "Confirmer la suppression",
            delete: "Supprimer",
            approveAllDeletions_one: "Approuver la suppression ({{count}})",
            approveAllDeletions_other: "Approuver toutes les suppressions ({{count}})",
          },
          deleteConfirm: {
            title: "Supprimer définitivement cette entreprise ?",
            intro: "Cette action supprimera définitivement « {{name}} » et tout ce qui y est rattaché. Cette action est irréversible.",
            summaryHeading: "Les éléments suivants seront supprimés :",
            company: "L'entreprise elle-même",
            needs_one: "{{count}} besoin",
            needs_other: "{{count}} besoins",
            matches_one: "{{count}} match",
            matches_other: "{{count}} matchs",
            contacts_one: "{{count}} contact",
            contacts_other: "{{count}} contacts",
            comments_one: "{{count}} commentaire",
            comments_other: "{{count}} commentaires",
            loadingImpact: "Chargement des éléments à supprimer...",
            impactError: "Impossible de charger les éléments à supprimer.",
            phrase: "tout supprimer définitivement",
            prompt: "Tapez « {{phrase}} » pour confirmer.",
            cancel: "Annuler",
            confirm: "Tout supprimer définitivement",
          },
          bulkDeleteConfirm: {
            title: "Approuver toutes les suppressions en attente ?",
            intro_one: "{{count}} entreprise est en attente d'approbation de suppression. L'approbation la supprimera définitivement ainsi que tout ce qui y est rattaché. Cette action est irréversible.",
            intro_other: "{{count}} entreprises sont en attente d'approbation de suppression. L'approbation les supprimera définitivement ainsi que tout ce qui y est rattaché. Cette action est irréversible.",
            summaryHeading: "Les éléments suivants seront supprimés :",
            companies_one: "{{count}} entreprise",
            companies_other: "{{count}} entreprises",
            needs_one: "{{count}} besoin",
            needs_other: "{{count}} besoins",
            matches_one: "{{count}} match",
            matches_other: "{{count}} matchs",
            contacts_one: "{{count}} contact",
            contacts_other: "{{count}} contacts",
            comments_one: "{{count}} commentaire",
            comments_other: "{{count}} commentaires",
            loadingImpact: "Chargement des éléments à supprimer...",
            impactError: "Impossible de charger les éléments à supprimer.",
            empty: "Aucune suppression en attente à approuver.",
            phrase: "tout supprimer définitivement",
            prompt: "Tapez « {{phrase}} » pour confirmer.",
            cancel: "Annuler",
            confirm: "Tout supprimer définitivement",
          },
          filters: {
            searchPlaceholder: "Rechercher",
            to: "à",
            sectorPlaceholder: "Filtre : Secteur",
            statusPlaceholder: "Filtre : Statut",
            locationPlaceholder: "Filtre : Localisation",
            managerPlaceholder: "Filtre : Responsable de compte / Commercial",
            clientCreatedFromPlaceholder: "Filtre : Entreprise créée à partir de",
            clientCreatedToPlaceholder: "Filtre : Entreprise créée jusqu'à",
            contactCreatedFromPlaceholder: "Filtre : Contact créé à partir de",
            contactCreatedToPlaceholder: "Filtre : Contact créé jusqu'à",
            clientCreatedLabel: "Entreprise créée",
            contactCreatedLabel: "Contact créé",
            fromPlaceholder: "Du",
            toPlaceholder: "Au",
            pageSizeSuffix: "/page",
          },
          summary: {
            loading: "Chargement...",
            total: "{{count}} au total",
          },
          table: {
            contacts: "Contacts",
            name: "Entreprise",
            description: "Description",
            sector: "Secteur",
            status: "Statut",
            location: "Localisation",
            manager: "Responsable de compte / Commercial",
            actions: "Actions",
            contactName: "Contact",
            contactFunction: "Fonction",
            contactDepartment: "Département",
            contactStatus: "Statut",
            contactEmail: "Email",
            contactPhone: "Téléphone",
            companyName: "Entreprise",
            companySector: "Secteur",
            companyLocation: "Localisation",
            companyManager: "Responsable de compte / Commercial",
            noItems: "Aucune entreprise trouvée",
            noContacts: "Aucun contact trouvé",
          },
          statusView: {
            empty: "Aucune entreprise dans ce statut.",
          },
          contactStatusView: {
            empty: "Aucun contact dans ce statut.",
          },
          contactsToggle: {
            show: "Afficher les contacts",
            hide: "Masquer les contacts",
          },
          contactsTable: {
            name: "Nom",
            status: "Statut",
            email: "Email",
            phone: "Téléphone",
            noContacts: "Aucun contact trouvé pour cette entreprise.",
          },
          pagination: {
            first: "Première",
            prev: "Précédente",
            next: "Suivante",
            last: "Dernière",
            pageLabel: "Page {{page}} sur {{total}}",
          },
          toasts: {
            loadClientsFailed: "Échec du chargement des entreprises",
            deleteClientSuccess: "Entreprise supprimée",
            deleteFailedTitle: "Échec de la suppression",
            deleteFailedDescription: "Veuillez réessayer",
            loadContactsFailed: "Échec du chargement des contacts",
            confirmDeleteSuccess: "Entreprise supprimée définitivement",
            confirmDeleteFailedTitle: "Échec de la confirmation",
            confirmDeleteFailedDescription: "Veuillez réessayer",
            bulkDeleteSuccess_one: "{{count}} entreprise supprimée définitivement",
            bulkDeleteSuccess_other: "{{count}} entreprises supprimées définitivement",
            bulkDeleteFailedTitle: "Échec de l'approbation",
            bulkDeleteFailedDescription: "Veuillez réessayer",
            updateStatusSuccess: "Statut de l'entreprise mis à jour",
            updateStatusFailedTitle: "Échec de la mise à jour du statut",
            updateStatusFailedDescription: "Veuillez réessayer",
            updateContactStatusSuccess: "Statut du contact mis à jour",
            updateContactStatusFailedTitle: "Échec de la mise à jour du statut",
            updateContactStatusFailedDescription: "Veuillez réessayer",
          },
        },
        detail: {
          backAria: "Retour à la liste des entreprises",
          titleFallback: "Entreprise",
          close: "Fermer",
          tabs: {
            description: "Description",
            contacts: "Contacts",
            needs: "Besoins de l'entreprise",
            comments: "Commentaires",
          },
          companyInfo: {
            description: "Description",
            sector: "Secteur",
            status: "Statut",
            location: "Localisation",
            manager: "Responsable de compte / Commercial",
            numberOfEmployees: "Nombre d'employés",
            linkedinUrl: "URL LinkedIn",
            notFound: "Entreprise introuvable.",
            skillsTechnical: "Compétences techniques",
            skillsFunctional: "Compétences fonctionnelles",
            skillsBusiness: "Compétences métier",
          },
          actions: {
            blacklist: "Mettre sur liste noire",
            unblacklist: "Retirer de la liste noire",
          },
          topSkills: {
            title: "Compétences principales",
            searchPlaceholder: "Rechercher une compétence...",
            noResults: "Aucune compétence ne correspond à votre recherche.",
          },
          contacts: {
            title: "Contacts",
            addButton: "Ajouter un contact",
            table: {
              name: "Nom",
              title: "Poste / Fonction",
              division: "Division / Département",
              status: "Statut",
              email: "Email",
              phone: "Téléphone",
              linkedin: "LinkedIn",
              actions: "Actions",
            },
            noContacts: "Aucun contact trouvé pour cette entreprise.",
            inactiveBadge: "Inactif",
          },
          needs: {
            title: "Besoins de l'entreprise",
            filters: {
              searchPlaceholder: "Rechercher un besoin...",
              statusPlaceholder: "Filtre : Statut",
              pageSizeSuffix: "/page",
            },
            summary: {
              loading: "Chargement...",
              total: "{{count}} au total",
            },
            table: {
              title: "Titre",
              status: "Statut",
              owner: "Propriétaire",
              created: "Créé",
              updated: "Mis à jour",
              link: "Lien",
              viewLink: "Voir",
              noNeeds: "Aucun besoin trouvé pour cette entreprise.",
            },
            pagination: {
              first: "Première",
              prev: "Précédente",
              next: "Suivante",
              last: "Dernière",
              pageLabel: "Page {{page}} sur {{total}}",
            },
          },
          comments: {
            title: "Commentaires",
            noComments: "Aucun commentaire pour cette entreprise pour le moment.",
            actions: {
              submit: "Ajouter un commentaire",
              reply: "Répondre",
              cancel: "Annuler",
            },
            placeholders: {
              new: "Écrire un commentaire…",
              reply: "Écrire une réponse…",
            },
          },
          toasts: {
            loadClientFailed: "Échec du chargement de l'entreprise",
            loadNeedsFailed: "Échec du chargement des besoins de l'entreprise",
            loadContactsFailed: "Échec du chargement des contacts",
            contactDeleted: "Contact supprimé",
            deleteFailedTitle: "Échec de la suppression",
            deleteFailedDescription: "Veuillez réessayer",
            commentAdded: "Commentaire ajouté",
            commentFailedTitle: "Impossible d'ajouter le commentaire",
            commentFailedDescription: "Veuillez réessayer",
            blacklisted: "Entreprise mise sur liste noire",
            blacklistFailed: "Échec de la mise sur liste noire de l'entreprise",
            unblacklisted: "Liste noire retirée",
            unblacklistFailed: "Échec du retrait de la liste noire",
          },
          confirmDeleteContact:
            "Êtes-vous sûr de vouloir supprimer ce contact ?",
          confirmDeleteContactTitle: "Supprimer le contact ?",
        },
        form: {
          modalTitleCreate: "Créer une entreprise",
          modalTitleEdit: "Modifier une entreprise",
          fields: {
            name: "Nom",
            description: "Description",
            sector: "Secteur",
            status: "Statut",
            location: "Localisation",
            manager: "Responsable de compte / Commercial",
            numberOfEmployees: "Nombre d'employés",
            linkedinUrl: "URL LinkedIn",
          },
          placeholders: {
            name: "Nom de l'entreprise",
            sector: "Ex : Retail",
            location: "Ex : Paris",
            numberOfEmployees: "Ex : 250",
            linkedinUrl: "https://www.linkedin.com/company/...",
            description: "Brève description",
          },
          buttons: {
            cancel: "Annuler",
            saveChanges: "Enregistrer les modifications",
            create: "Créer",
          },
          toasts: {
            created: "Entreprise créée",
            updated: "Entreprise mise à jour",
            operationFailedTitle: "Échec de l'opération",
            operationFailedDescription: "Veuillez réessayer",
          },
          duplicate: {
            title: "Entreprise en double",
            body: 'Une entreprise nommée « {{name}} » existe déjà.',
            prompt: "Souhaitez-vous consulter l'entreprise existante ?",
            cancel: "Annuler",
            view: "Voir l'existante",
          },
        },
        importModal: {
          title: "Importer des entreprises depuis un CSV/XLSX",
          description:
            "Colonnes supportées (insensibles à la casse) : name, description, sector, status, location, contact_name, contact_email, contact_phone, manager_email",
          fields: {
            file: "Fichier",
            defaultManager: "Responsable de compte par défaut (optionnel)",
          },
          statusLabels: {
            total: "Total",
            added: "Ajoutées",
            updated: "Mises à jour",
            deleted: "Supprimées",
            duplicate: "Doublons",
          },
          dropzone: {
            text:
              "Glissez-déposez un fichier CSV/XLSX ici ou cliquez pour sélectionner",
            accepted: "Acceptés : .csv, .xlsx",
          },
          buttons: {
            cancel: "Annuler",
            import: "Importer",
          },
          toasts: {
            importCompleted: "Import terminé",
            importFailedTitle: "Échec de l'import",
          },
        },
        contactsWizard: {
          modalTitle: "Ajouter des contacts {{clientName}}",
          intro:
            "Votre entreprise a été créée. Vous pouvez maintenant ajouter un ou plusieurs contacts.",
          header: "Contacts",
          addContactButton: "Ajouter un contact",
          table: {
            name: "Nom",
            email: "Email",
            phone: "Téléphone",
            noContacts:
              'Aucun contact pour le moment. Cliquez sur "Ajouter un contact" pour en créer un.',
          },
          buttons: {
            skip: "Passer pour le moment",
            finish: "Terminer",
          },
          toasts: {
            loadContactsFailed: "Échec du chargement des contacts",
          },
        },
        contactsForm: {
          modalTitleCreate: "Créer un contact",
          modalTitleEdit: "Modifier un contact",
          fields: {
            name: "Nom",
            status: "Statut",
            email: "Email",
            phone: "Téléphone",
            linkedinUrl: "URL LinkedIn",
            title: "Poste / Fonction",
            division: "Division / Département",
            comments: "Commentaires",
            active: "Actif",
          },
          placeholders: {
            name: "Nom du contact",
            status: "Sélectionner un statut",
            email: "email@example.com",
            phone: "+33 ...",
            linkedinUrl: "https://www.linkedin.com/in/...",
            title: "Ex : Responsable Trading",
            division: "Ex : Finance",
            comments: "Notes, contexte, préférences, etc.",
          },
          buttons: {
            cancel: "Annuler",
            saveChanges: "Enregistrer les modifications",
            create: "Créer",
          },
          toasts: {
            created: "Contact créé",
            updated: "Contact mis à jour",
            operationFailedTitle: "Échec de l'opération",
            operationFailedDescription: "Veuillez réessayer",
          },
        },
      },
      pilotConsultants: {
        list: {
          pageTitle: "Consultants",
          eyebrow: "ASAP PILOT",
          emptyHint: "Créez ou importez un consultant pour constituer votre vivier.",
          actions: {
            create: "Créer un consultant",
            importFromCv: "Uploader",
            importFromFile: "Bulk Upload",
            confirmDelete: "Confirmer la suppression",
            bulkUploadCvs: "Import groupé de CV",
          },
          filters: {
            searchPlaceholder: "Rechercher par consultant, entreprise ou besoin...",
            clientPlaceholder: "Filtre : Entreprise",
            needPlaceholder: "Filtre : Besoin",
            statusPlaceholder: "Filtre : Statut",
            staffingStatusPlaceholder: "Filtre : Statut staffing",
            availabilityPlaceholder: "Filtre : Disponibilité",
            locationPlaceholder: "Filtre : Localisation",
            categoryPlaceholder: "Filtre : Catégorie",
            minSalaryPlaceholder: "Salaire min.",
            maxSalaryPlaceholder: "Salaire max.",
            pageSizeSuffix: "/page",
          },
          viewSwitcher: {
            ariaLabel: "Vue des données",
            allConsultants: "Tous les consultants",
            byStatus: "Par statut",
          },
          summary: {
            loading: "Chargement...",
            total: "{{count}} au total",
          },
          table: {
            name: "Nom",
            title: "Titre",
            category: "Catégorie",
            salaryRange: "Salaire (min-max)",
            skills: "Compétences",
            client: "Entreprise",
            need: "Besoin",
            availability: "Disponibilité",
            status: "Statut",
            staffingStatus: "Statut staffing",
            location: "Localisation",
            email: "Email",
            portal: "Portail",
            actions: "Actions",
            noItems: "Aucun consultant trouvé",
          },
          pagination: {
            first: "Première",
            prev: "Précédente",
            next: "Suivante",
            last: "Dernière",
            pageLabel: "Page {{page}} sur {{total}}",
          },
          confirmDelete: "Êtes-vous sûr de vouloir supprimer ce consultant ?",
          toasts: {
            loadConsultantsFailed: "Échec du chargement des consultants",
            deleteConsultantSuccess: "Consultant supprimé",
            deleteFailedTitle: "Échec de la suppression",
            deleteFailedDescription: "Veuillez réessayer",
            confirmDeleteSuccess: "Consultant supprimé définitivement",
            confirmDeleteFailedTitle: "Échec de la confirmation",
            confirmDeleteFailedDescription: "Veuillez réessayer",
            invalidFileType: "Certains fichiers ont été ignorés (seuls les PDF et DOCX sont acceptés).",
            bulkCvError: "Échec de l'import des CV.",
            updateStaffingStatusSuccess: "Statut de staffing mis à jour",
            updateStaffingStatusFailed: "Échec de la mise à jour du statut",
          },
          bulkCvModal: {
            title: "Import groupé de CV",
            description: "Importez jusqu'à 20 fichiers de CV (PDF ou DOCX) pour créer automatiquement des profils de consultants.",
            dropzone: "Glissez-déposez des fichiers de CV ici, ou cliquez pour parcourir",
            accepted: "PDF, DOCX, 20 fichiers maximum",
            upload: "Importer",
            createdTitle: "consultant(s) créé(s) avec succès",
            errorsTitle: "fichier(s) n'ont pas pu être traités",
            duplicateMessage: "Un consultant nommé « {{name}} » existe déjà",
            viewExisting: "Voir l'existant",
          },
        },
        importModal: {
          title: "Créer un consultant depuis un CV",
          description:
            "Téléversez un CV (PDF ou DOC/DOCX). Le système extraira les informations et créera une fiche consultant.",
          fields: {
            file: "Fichier CV",
          },
          statusLabels: {
            total: "Total",
            added: "Ajoutés",
            updated: "Mis à jour",
            deleted: "Supprimés",
            duplicate: "Doublons",
          },
          dropzone: {
            text: "Glissez-déposez un CV ici ou cliquez pour sélectionner",
            accepted: "Acceptés : .pdf, .doc, .docx",
          },
          buttons: {
            cancel: "Annuler",
            import: "Uploader",
          },
          toasts: {
            importCompleted: "Consultant créé depuis un CV",
            importFailedTitle: "Échec de l'import",
            importFailedDescription: "Veuillez réessayer",
          },
        },
        createWizard: {
          stepLabels: {
            upload: "Téléverser le CV",
            review: "Vérifier les informations",
          },
          step1Title: "Créer un consultant",
          step2Title: "Compléter les détails du consultant",
          step1Description:
            "Vous pouvez téléverser un CV (PDF ou DOC/DOCX) pour créer une fiche consultant à partir du document, ou continuer sans téléverser et remplir le formulaire manuellement.",
          buttons: {
            continueWithoutUpload: "Continuer sans téléverser",
            continueWithUpload: "Continuer avec le téléversement",
            back: "Retour",
          },
        },
        detail: {
          backAria: "Retour à la liste des consultants",
          close: "Fermer",
          titleFallback: "Consultant",
          notFound: "Consultant introuvable",
          header: {
            edit: "Modifier",
            delete: "Supprimer",
            downloadCv: "Télécharger le CV",
          },
          sections: {
            title: "Titre",
            email: "Email",
            phone: "Téléphone",
            location: "Localisation",
            availability: "Disponibilité",
            contractType: "Type de contrat",
            employmentStartDate: "Date de début",
            status: "Statut",
            staffingStatus: "Statut staffing",
            category: "Catégorie",
            salaryRange: "Salaire (min-max)",
          description: "Description",
          technicalSkills: "Compétences techniques",
          functionalSkills: "Compétences fonctionnelles",
          businessSkills: "Compétences métier",
            createdAt: "Créé le",
            updatedAt: "Dernière mise à jour",
          },
          cv: {
            title: "CV",
            description: "Téléversez un CV pour ce consultant et accédez aux documents générés.",
            fields: {
              cvFile: "Fichier CV",
              jobDescFile: "Appel d’offre / description de poste (optionnel)",
              language: "Langue",
              deliverables: "Livrables souhaités",
            },
            actions: {
              selectCv: "Sélectionner un CV",
              selectJobDesc: "Sélectionner une description de poste",
              submit: "Envoyer le CV",
            },
            documentsTitle: "Documents CV",
          },
          skills: {
            title: "Assessments de compétences",
            description:
              "Envoyez une demande d'assessment pour ce consultant et consultez les assessments passés.",
            fields: {
              candidateName: "Nom du candidat",
              candidateEmail: "Email du candidat",
              language: "Langue",
              jobDescFile: "Description de poste (optionnel)",
            },
            actions: {
              selectJobDesc: "Sélectionner une description de poste",
              submit: "Envoyer l'assessment",
            },
            assessmentsTitle: "Assessments",
          },
          comments: {
            title: "Commentaires",
            noComments: "Aucun commentaire pour ce consultant.",
            addLabel: "Ajouter un commentaire",
            actions: {
              submit: "Ajouter le commentaire",
              reply: "Répondre",
              cancel: "Annuler",
            },
            placeholders: {
              new: "Écrire un commentaire…",
              reply: "Écrire une réponse…",
            },
          },
          tabs: {
            profile: "Profil",
            about: "À propos",
            conversations: "Conversations",
            cvPortfolio: "Portfolio CV",
            asapSkills: "ASAP SKILLS",
            assignments: "Missions",
            cv: "CV",
            assessment: "Assessment",
            comments: "Commentaires",
            matches: "Matches",
            candidateFollowUp: "Suivi candidat",
            tracking: "Suivi",
          },
          reverseMatch: {
            title: "Match inversé",
            description: "Trouver les meilleurs besoins ouverts pour ce consultant",
            search: "Match inversé",
            lastSearched: "Dernière recherche",
            suggest: "Suggérer",
            suggestSuccess: "Suggestion envoyée au commercial",
            suggestError: "Échec de l'envoi de la suggestion",
            error: "Échec du match inversé",
            actions: "Actions",
          },
          followUp: {
            monthlyRatings: {
              title: "Évaluations mensuelles",
              noRatings: "Aucune évaluation mensuelle.",
              add: "Ajouter une évaluation",
              period: "Période",
              rating: "Note",
              comment: "Commentaire",
              save: "Enregistrer",
              periodYear: "Année",
              periodMonth: "Mois",
              editTitle: "Modifier l'évaluation",
              selectRating: "Veuillez sélectionner une note",
            },
            annualReviews: {
              title: "Revues annuelles",
              noReviews: "Aucune revue annuelle.",
              add: "Ajouter une revue",
              year: "Année",
              type: "Type",
              notes: "Notes",
              reviewer: "Évaluateur",
              save: "Enregistrer",
              editTitle: "Modifier la revue",
              types: {
                annual: "Annuel",
                salary: "Salaire",
                client_feedback: "Retour client",
              },
            },
            specialRequests: {
              title: "Demandes spéciales",
              comingSoon: "Bientôt disponible",
            },
            activity: {
              title: "Rapports d'activité",
              empty: "Aucun rapport d'activité pour le moment.",
              period: "Période",
              days: "Jours",
              status: "Statut",
              review: "Validation",
            },
            leave: {
              title: "Demandes de congé",
              empty: "Aucune demande de congé pour le moment.",
              type: "Type",
              dates: "Dates",
              status: "Statut",
              review: "Validation",
            },
            feedback: {
              title: "Retour de mission",
              empty: "Aucun retour de mission pour le moment.",
              noPermission: "Vous n'avez pas l'autorisation de valider les retours de mission.",
              client: "Client",
              assignment: "Mission",
              cycle: "Cycle",
              rating: "Note",
              status: "Statut",
              comment: "Commentaire",
            },
          },
          conversations: {
            title: "Conversations",
            noConversations: "Aucune conversation pour le moment.",
            unknownAuthor: "Inconnu",
            actions: {
              add: "Ajouter une interaction",
              save: "Enregistrer",
              edit: "Modifier",
              delete: "Supprimer",
            },
            placeholders: {
              content: "Décrivez l'interaction...",
            },
            confirmDeleteTitle: "Supprimer l'interaction ?",
            confirmDeleteBody: "Cette action est irréversible.",
            toasts: {
              fetchFailed: "Échec du chargement des conversations",
              created: "Interaction ajoutée",
              createFailed: "Impossible d'ajouter l'interaction",
              updated: "Interaction mise à jour",
              updateFailed: "Impossible de mettre à jour l'interaction",
              deleted: "Interaction supprimée",
              deleteFailed: "Impossible de supprimer l'interaction",
            },
          },
          matches: {
            title: "Matches",
            description: "Tous les matches pour ce consultant.",
            noMatches: "Aucun match trouvé.",
            filterAll: "Tous les matches",
            filterSubmitted: "Soumis au client",
            generateMatches: "Générer les matches",
            generateError: "Échec de la génération des matches",
            lastGenerated: "Dernière génération",
          },
          consultantPortal: {
            sectionLabel: "Portail consultant",
            portalAccessHeading: "Accès portail",
            lastInvite: "Dernière invitation : {{date}}",
            inviteButton: "Inviter",
            resendInviteButton: "Renvoyer l'invitation",
            disableAccessButton: "Désactiver l'accès",
            statusLabels: {
              active: "Actif",
              invited: "Invité",
              disabled: "Désactivé",
              inactive: "Inactif",
            },
            toasts: {
              inviteSent: "Invitation consultant envoyée",
              inviteResent: "Invitation consultant renvoyée",
              accessDisabled: "Accès au portail consultant désactivé",
              actionFailedTitle: "Action portail consultant échouée",
              actionFailedDescription: "Veuillez réessayer.",
            },
          },
          confirmDeleteTitle: "Supprimer le consultant ?",
          asapSkills: {
            noData: "Aucune donnée de compétences disponible.",
            skillName: "Compétence",
            skillScore: "Score",
            skillProgress: "Progression",
            evaluationCount: "Évaluations",
            averageScore: "Score moyen",
            trend: "Tendance",
            goToSkills: "Aller vers ASAP SKILLS",
            evaluationHistory: "Historique des évaluations",
            noEvaluations: "Aucune évaluation terminée pour le moment.",
            noSkillBreakdown: "Aucun détail de compétences disponible.",
          },
          assignments: {
            loadError: "Échec du chargement des missions. Veuillez réessayer.",
            noData: "Aucune donnée de mission disponible.",
            totalExperienceLabel: "Expérience totale",
            yearsInCompanyLabel: "Au sein de l'entreprise",
            yearsValue: "{{years}} ans",
            noExperienceData: "Données d'expérience non renseignées.",
            historyTitle: "Historique des missions",
            noAssignments: "Aucune mission trouvée.",
            current: "En cours",
            ongoing: "En cours",
          },
          review: {
            approve: "Approuver",
            reject: "Refuser",
            approveTitle: "Approuver la demande",
            rejectTitle: "Refuser la demande",
            commentHint: "Ajoutez éventuellement un commentaire ou une note pour cette validation.",
            commentPlaceholder: "Commentaire (facultatif)",
            toasts: {
              approved: "Validation approuvée",
              rejected: "Validation refusée",
              failedTitle: "Échec de l'action de validation",
              failedDescription: "Veuillez réessayer.",
            },
          },
          skillsPortfolio: {
            sourceCvs: "CV source",
            storedCv: "CV enregistré",
            sendCv: "Envoyer un CV",
            downloadSourceCv: "Télécharger le CV source",
            noSourceCvs: "Aucun CV source téléversé pour le moment.",
            generatedCvs: "CV générés",
            moduleNotEnabled: "Module CV non activé pour cette business unit.",
            noGeneratedCvs: "Aucun CV traité pour le moment.",
          },
          confirmDeleteConsultant:
            "Êtes-vous sûr de vouloir supprimer ce consultant ?",
          toasts: {
            loadConsultantFailed: "Échec du chargement du consultant",
            deleteConsultantSuccess: "Consultant supprimé",
            deleteFailedTitle: "Échec de la suppression",
            deleteFailedDescription: "Veuillez réessayer",
            commentAdded: "Commentaire ajouté",
            commentFailedTitle: "Impossible d'ajouter le commentaire",
            commentFailedDescription: "Veuillez réessayer",
            cvSubmitSuccess: "CV soumis",
            cvSubmitFailedTitle: "Échec de l'envoi du CV",
            cvSubmitFailedDescription: "Veuillez réessayer",
            assessmentSubmitSuccess: "Demande d'assessment envoyée",
            assessmentSubmitFailedTitle: "Échec de la demande d'assessment",
            assessmentSubmitFailedDescription: "Veuillez réessayer",
          },
        },
        form: {
          modalTitleCreate: "Créer un consultant",
          modalTitleEdit: "Modifier un consultant",
          fields: {
            name: "Nom",
            title: "Titre",
            email: "Email",
            phone: "Téléphone",
            location: "Localisation",
            availability: "Disponibilité",
            status: "Statut",
            staffingStatus: "Statut staffing",
            description: "Description",
            comments: "Commentaires",
            minSalary: "Salaire min.",
            maxSalary: "Salaire max.",
          },
          placeholders: {
            name: "Nom du consultant",
            title: "Ex : Senior Consultant",
            email: "email@example.com",
            phone: "Numéro de téléphone",
            location: "Ex : Paris",
            availability: "Ex : Immédiatement, 1 mois",
            status: "Ex : Actif, Bench",
            staffingStatus: "Ex : Interne, Talent pool, Inactif",
            description: "Description publique",
            comments: "Notes internes sur ce consultant",
            minSalary: "Ex : 70000",
            maxSalary: "Ex : 90000",
          },
          buttons: {
            cancel: "Annuler",
            saveChanges: "Enregistrer les modifications",
            create: "Créer",
          },
          toasts: {
            created: "Consultant créé",
            updated: "Consultant mis à jour",
            operationFailedTitle: "Échec de l'opération",
            operationFailedDescription: "Veuillez réessayer",
          },
        },
        importFromFile: {
          modalTitle: "Importer des consultants depuis un CSV/XLSX",
          description:
            "Téléversez un fichier CSV ou XLSX. Colonnes supportées : nom, titre, email, disponibilité, statut, localisation, téléphone et compétences techniques/fonctionnelles/métier.",
          fields: {
            file: "Fichier",
          },
          statusLabels: {
            total: "Total",
            added: "Ajoutés",
            updated: "Mis à jour",
            deleted: "Supprimés",
            duplicate: "Doublons",
          },
          dropzone: {
            text:
              "Glissez-déposez un fichier CSV/XLSX ici ou cliquez pour sélectionner",
            accepted: "Acceptés : .csv, .xlsx",
          },
          buttons: {
            cancel: "Annuler",
            import: "Importer",
          },
          toasts: {
            importTooLargeTitle: "Import trop volumineux",
            importTooLargeDescription:
              "Ce fichier contient plus de 500 lignes de données. Scindez-le et réessayez.",
          },
        },
      },
      pilotConsultantTracking: {
        list: {
          pageTitle: "Suivi",
          eyebrow: "ASAP Pilot",
          panelTitle: "Suivi des consultants",
          filters: {
            searchPlaceholder: "Rechercher un consultant…",
            pageSizeSuffix: "/page",
            toggle: "Filtres",
            reset: "Réinitialiser",
            contractTypeLabel: "Statut (type de contrat)",
            contractTypeAll: "Tous les types de contrat",
            clientLabel: "Client actuel",
            clientAll: "Tous les clients",
            nextMissionLabel: "Prochaine mission",
            nextMissionAll: "Tous",
            nextMissionYes: "Avec prochaine mission",
            nextMissionNo: "Sans prochaine mission",
            missionEndRangeLabel: "Fin de mission",
            poEndRangeLabel: "Fin de bon de commande",
            benchDaysRangeLabel: "Jours d'intermission (CDI uniquement)",
            clientScoreRangeLabel: "Score client moyen",
            from: "Du",
            to: "au",
            missionEndFromLabel: "Fin de mission à partir du",
            missionEndToLabel: "Fin de mission jusqu'au",
            poEndFromLabel: "Fin de BC à partir du",
            poEndToLabel: "Fin de BC jusqu'au",
            benchDaysMinLabel: "Jours d'intermission min. (CDI uniquement)",
            clientScoreMaxLabel: "Score client moyen max.",
          },
          summary: {
            loading: "Chargement...",
            total: "{{count}} au total",
          },
          table: {
            name: "Nom",
            status: "Statut",
            client: "Client",
            assignmentEndDate: "Fin de mission",
            purchaseOrderEndDate: "Fin du BC",
            cumulativeDaysWithClient: "Jours chez le client",
            numberOfClients: "Clients dans l'entreprise",
            cumulativeDaysWithCompany: "Jours dans l'entreprise",
            benchDays: "Jours d'intermission",
            nextAssignment: "Prochaine mission",
            averageClientScore: "Score moyen client",
            noItems: "Aucun consultant trouvé",
            noItemsHint: "Ajustez les filtres ou ajoutez des consultants pour voir les données de suivi.",
          },
          contractTypes: {
            permanent_contract: "CDI",
            freelance: "Freelance",
            subcontracting: "Sous-traitance",
            umbrella_company: "Portage"
          },
          badges: {
            available: "Disponible",
            onMission: "En mission",
            nextConfirmed: "Oui",
            noNextConfirmed: "Non",
            salaryReviewThisMonth: "Ce mois-ci",
            noSalaryReviewThisMonth: "Non",
            salaryReviewDay: "Jour {{day}}",
          },
          pagination: {
            first: "Première",
            prev: "Préc.",
            next: "Suiv.",
            last: "Dernière",
            pageLabel: "Page {{page}} sur {{total}}",
          },
        },
        detail: {
          backAria: "Retour au suivi",
          titleFallback: "Consultant",
          actions: {
            back: "Retour",
          },
          rangePanelTitle: "Période de suivi",
          assignmentsPanelTitle: "Affectations",
          emptyHint: "Ajustez la période ou revenez plus tard pour les données de suivi.",
          kpis: {
            availability: "Disponibilité",
            nextAssignment: "Prochaine affectation",
            salaryReview: "Révision salariale",
            months: "Mois suivis",
          },
          summary: {
            loading: "Chargement...",
            range: "Période : {{start}} → {{end}}",
          },
          empty: "Aucune donnée de timeline",
          filters: {
            fromDate: "Du",
            toDate: "Au",
            refresh: "Actualiser",
          },
          chart: {
            title: "Timeline (mois)",
            tooltip:
              "{{month}} — {{count}} mission(s) — {{status}} — Rev. salariale : {{salaryReview}}",
            selectedMonth: "Sélectionné : {{month}}",
          },
          table: {
            month: "Mois",
            isAvailable: "Disponibilité",
            assignments: "Missions",
            noAssignments: "Aucune mission",
          },
          badges: {
            po: "BC : {{status}}",
            satisfaction: "CSAT : {{score}}",
          },
          fields: {
            dates: "{{start}} → {{end}}",
          },
          actions: {
            editTracking: "Modifier le suivi",
            viewConsultant: "Voir le consultant",
          },
          modal: {
            title: "Mettre à jour le suivi de mission",
            actions: {
              cancel: "Annuler",
              save: "Enregistrer",
            },
            fields: {
              empty: "—",
              assignmentStatus: "Statut de mission",
              assignmentStartDate: "Date de début",
              assignmentEndDate: "Date de fin",
              purchaseOrderStatus: "Statut du bon de commande",
              purchaseOrderNumber: "Numéro de BC",
              purchaseOrderReceivedAt: "BC reçu le",
              clientSatisfactionScore: "Score de satisfaction",
              clientSatisfactionComment: "Commentaire satisfaction",
            },
          },
          toasts: {
            updateMatchSuccess: "Suivi mis à jour",
            updateMatchFailed: "Échec de la mise à jour",
          },
        },
      },
      pilotMatches: {
        list: {
          pageTitle: "Matchs",
          eyebrow: "ASAP PILOT",
          emptyHint: "Générez des matchs pour positionner les consultants sur les besoins clients.",
          viewSwitcher: {
            ariaLabel: "Vue des données",
            byNeed: "Par besoin",
            byConsultant: "Par consultant",
          },
          deleteDialog: {
            title: "Supprimer le match",
          },
          filters: {
            searchPlaceholder: "Rechercher par titre de besoin...",
            searchConsultantPlaceholder: "Rechercher par nom de consultant...",
            clientPlaceholder: "Filtre : Entreprise",
            statusPlaceholder: "Filtre : Statut",
            categoryPlaceholder: "Filtre : Catégorie",
            hasMatchesPlaceholder: "Filtre : Matchs",
            hasMatchesWith: "Avec matchs",
            hasMatchesWithout: "Sans match",
          },
          summary: {
            loading: "Chargement...",
            total: "{{count}} besoins au total",
            consultantCount: "{{count}} consultant(s)",
          },
          table: {
            matchesCount: "Matchs",
            openDuration: "Durée d'ouverture (jours)",
            suggestedProfiles: "Profils suggérés",
            positionedProfiles: "Profils positionnés",
            actions: "Actions",
            noItems: "Aucun besoin trouvé",
          },
          matchTable: {
            consultant: "Consultant",
            bestScore: "Meilleur score",
            won: "gagné(s)",
            submitted: "Soumis",
          },
          expand: {
            show: "Afficher les matchs",
            hide: "Masquer les matchs",
          },
          actions: {
            generateMatches: "Générer des matchs",
            regenerateMatches: "Régénérer les matchs",
            selectConsultants: "Sélectionner des consultants...",
          },
          selectModal: {
            title: "Sélectionner des consultants pour ce besoin",
            searchPlaceholder: "Rechercher des consultants...",
            selectedCount: "Sélectionnés : {{count}} / {{max}}",
            noConsultants: "Aucun consultant trouvé",
            total: "{{count}} consultants trouvés",
            cancelButton: "Annuler",
            primaryButton: "Générer des matchs",
            modeAuto: "Utiliser tous les consultants (sélection automatique)",
            modeManual: "Sélectionner manuellement jusqu'à {{max}} consultants",
            roleColumn: "Rôle",
          },
          pagination: {
            first: "Première",
            prev: "Précédente",
            next: "Suivante",
            last: "Dernière",
            pageLabel: "Page {{page}} sur {{total}}",
          },
        },
        detail: {
          pageTitle: "Match #{{id}}",
          back: "Retour aux matchs",
          backAria: "Retour aux matchs",
          scoreBadge: "Score {{score}}",
          notFoundHint: "Ce match a peut-être été supprimé ou n'est pas disponible.",
          notFound: "Match introuvable",
          confirmDelete: "Êtes-vous sûr de vouloir supprimer ce match ?",
          confirmDeleteTitle: "Supprimer le match ?",
          sections: {
            overview: "Vue d'ensemble",
            reasoning: "Raisonnement",
            needInfo: "Besoin",
            consultantInfo: "Consultant",
          },
          workflow: {
            title: "Flux de travail",
            columnTitle: "Flux de travail",
            clickToRevert: "Cliquez pour revenir en arrière (confirmation requise)",
            clickToAdvance: "Cliquez pour avancer",
            withdraw: "Retirer",
            markRejected: "Marquer refusé",
            backwardConfirm: {
              title: "Revenir à l'étape précédente du flux ?",
              body: "Revenir en arrière dans le flux de travail réinitialisera le match à une étape antérieure. Êtes-vous sûr ?",
            },
            toasts: {
              transitionSuccess: "Flux de travail mis à jour",
              transitionFailedTitle: "Échec de la transition",
              transitionFailedDescription: "Impossible de mettre à jour le statut du flux de travail.",
            },
          },
          missionTracking: {
            title: "Affectation",
            description:
              "Mettez à jour le statut de mission et les dates pour ce match. Vous pouvez aussi suivre le bon de commande et la satisfaction entreprise.",
            fields: {
              empty: "—",
              assignmentStatus: "Statut de mission",
              assignmentStartDate: "Date de début",
              assignmentEndDate: "Date de fin",
              purchaseOrderStatus: "Statut du bon de commande",
              purchaseOrderNumber: "Numéro de BC",
              purchaseOrderReceivedAt: "BC reçu le",
              clientSatisfactionScore: "Score de satisfaction",
              clientSatisfactionComment: "Commentaire satisfaction",
              dailyRate: "TJM (\u20ac)",
              costRate: "Coût journalier (\u20ac)",
              billableDaysTarget: "Jours facturables cibles",
            },
            actions: {
              open: "Modifier l'affectation",
              close: "Fermer",
              save: "Enregistrer",
            },
            toasts: {
              saveSuccess: "Suivi de mission mis à jour",
              saveFailedTitle: "Échec de la mise à jour",
              saveFailedDescription: "Veuillez réessayer",
            },
          },
          interviews: {
            title: "Entretiens",
            description:
              "Suivez les entretiens planifiés et terminés pour ce match.",
            fields: {
              empty: "—",
              status: "Statut",
              scheduledAt: "Planifié le",
              completedAt: "Terminé le",
            },
            actions: {
              add: "Ajouter un entretien",
              edit: "Modifier",
              close: "Fermer",
              save: "Enregistrer",
            },
            toasts: {
              loadFailedTitle: "Échec du chargement des entretiens",
              saveSuccess: "Entretien enregistré",
              saveFailedTitle: "Échec de l'enregistrement",
              saveFailedDescription: "Veuillez réessayer",
              missingTime: "Veuillez fournir au moins une date/heure.",
              nothingToUpdate: "Veuillez modifier au moins un champ.",
            },
            emptyState: "Aucun entretien enregistré pour le moment.",
          },
          fields: {
            id: "ID",
            needId: "ID du besoin",
            consultantId: "ID du consultant",
            category: "Catégorie",
            score: "Score",
            source: "Source",
            companyId: "ID de l'entreprise",
            businessUnitId: "ID de la Business Unit",
            atsScore: "Score ATS",
            skillsScore: "Score de compétences",
          },
          table: {
            consultant: "Consultant",
            category: "Catégorie",
            score: "Score",
            source: "Source",
            reasoning: "Raisonnement",
            positioned: "Positionné",
            actions: "Actions",
            workflowStatus: "Flux de travail",
          },
          positioned: {
            yes: "Oui",
            no: "Non",
          },
          actions: {
            delete: "Supprimer",
            viewNeed: "Voir le besoin",
            viewConsultant: "Voir le consultant",
          },
          submission: {
            status: {
              submittedToClient: "Soumis au client",
              rejected: "Refusé par le client",
              withdrawn: "Retiré",
              draft: "Brouillon",
            },
            columnTitle: "Soumission",
            submitAction: "Soumettre le profil",
            withdrawAction: "Retirer",
            rejectAction: "Marquer refusé",
            modalTitle: "Soumettre le match au client",
            modalDescription:
              "Confirmez que les ventes ont transmis ce consultant au client.",
            channelLabel: "Canal de soumission",
            channelEmail: "E-mail",
            channelPlatform: "Plateforme",
            channelOther: "Autre",
            submittedAtLabel: "Soumis le (optionnel)",
            noteLabel: "Note (optionnelle)",
            notePlaceholder: "ex. envoyé par e-mail au contact client",
            cancelAction: "Annuler",
            confirmAction: "Confirmer la soumission",
          },
          matchesForNeedTitle: 'Matchs pour le besoin "{{title}}"',
          noMatches: "Aucun match trouvé pour ce besoin.",
          noReasoning: "Aucun raisonnement enregistré pour ce match.",
          noNeedInContext: "Aucune information de besoin disponible dans ce contexte.",
          noConsultantInContext: "Aucune information de consultant disponible dans ce contexte.",
        },
        toasts: {
          loadNeedsFailed: "Échec du chargement des besoins",
          loadMatchesFailedTitle: "Échec du chargement des matchs",
          loadMatchesFailedDescription: "Veuillez réessayer",
          generateSuccess: "Matchs générés",
          generateFailedTitle: "Échec de la génération",
          generateFailedDescription: "Veuillez réessayer",
          deleteSuccess: "Match supprimé",
          deleteFailedTitle: "Échec de la suppression",
          deleteFailedDescription: "Veuillez réessayer",
          updateMatchSuccess: "Mis à jour",
          updateMatchFailed: "Échec de la mise à jour",
          maxConsultantsSelected: "Vous pouvez sélectionner au maximum {{max}} consultants.",
          submission: {
            invalidTransitionTitle: "Transition invalide",
            invalidTransitionDescription:
              "Ce match a changé d'état. Actualisez et réessayez.",
            invalidChannelTitle: "Canal invalide",
            invalidChannelDescription:
              "Utilisez l'un des canaux : e-mail, plateforme, autre.",
            invalidSubmittedAtTitle: "Date de soumission invalide",
            matchNotFoundTitle: "Match introuvable",
            genericFailedTitle: "Échec de la mise à jour de la soumission",
            submitSuccess: "Soumis au client",
            withdrawSuccess: "Soumission retirée",
            rejectSuccess: "Marqué comme refusé",
          },
        },
      },
      settings: {
        pageTitle: "Paramètres",
        eyebrow: "Administration",
        loading: "Chargement...",
        noPermission: "Vous n'avez pas l'autorisation d'accéder aux paramètres.",
        noPermissionTitle: "Accès refusé",
        tabs: {
          company: "Entreprise",
          businessUnit: "Business Unit",
          partnersEmail: "Email Partenaires",
        },
        partnersEmail: {
          description: "Configurez les paramètres SMTP utilisés par ASAP Partners pour envoyer des communications depuis cette unité.",
        },
        company: {
          accessDeniedTitle: "Accès refusé",
          accessDeniedDescription:
            "Seuls les administrateurs de l'entreprise peuvent consulter les paramètres de l'entreprise.",
          sectionTitle: "Profil de l'entreprise",
          sectionDescription: "Identité de votre entreprise et coordonnées de facturation.",
          headerButton: "Modifier les informations de l'entreprise",
          modalTitle: "Modifier l'entreprise",
          displayCompanyFallback: "Entreprise",
          fields: {
            logo: "Logo de l'entreprise",
            name: "Nom de l'entreprise",
            managerName: "Nom du manager",
            billingManagerName: "Nom du responsable facturation",
            billingManagerEmail: "Email du responsable facturation",
            website: "Site web (URL)",
          },
          buttons: {
            uploadLogo: "Téléverser",
            cancel: "Annuler",
            save: "Enregistrer",
          },
          toasts: {
            loadFailed: "Échec du chargement de l'entreprise",
            saveSuccess: "Paramètres de l'entreprise enregistrés",
            saveFailed: "Échec de l'enregistrement",
          },
        },
        businessUnits: {
          noUnits:
            "Aucune business unit trouvée pour cette entreprise.",
          noUnitsHint: "Créez une unité opérationnelle pour organiser vos équipes.",
          toasts: {
            created: "Unité opérationnelle créée",
            createFailed: "Échec de la création",
          },
          newButton: "Nouvelle Business Unit",
          listTitle: "Business Units",
          table: {
            columns: {
              name: "Nom",
              code: "Code",
              manager: "Manager",
              actions: "Actions",
            },
            noUnits: "Aucune business unit trouvée.",
            edit: "Modifier",
          },
          editModal: {
            title: "Modifier la Business Unit",
            fields: {
              unitName: "Nom de l'unité",
              managerName: "Nom du manager",
              billingManagerName: "Nom du responsable facturation",
              billingManagerEmail: "Email du responsable facturation",
            },
            buttons: {
              cancel: "Annuler",
              save: "Enregistrer",
            },
            toasts: {
              loadFailed:
                "Échec du chargement des paramètres de la Business Unit",
              saveSuccess:
                "Paramètres de la Business Unit enregistrés",
              saveFailed:
                "Échec de l'enregistrement des paramètres",
            },
          },
          createModal: {
            title: "Créer une Business Unit",
            fields: {
              unitName: "Nom de l'unité",
              managerName: "Nom du manager",
              billingManagerName: "Nom du responsable facturation",
              billingManagerEmail: "Email du responsable facturation",
            },
            buttons: {
              cancel: "Annuler",
              create: "Créer",
            },
            toasts: {
              createSuccess: "Business Unit créée",
              createFailed: "Échec de la création",
            },
          },
          editButtonForBUAdmin: "Modifier la Business Unit",
        },
        users: {
          sectionTitle: "Utilisateurs",
          inviteButton: "Inviter un utilisateur",
          table: {
            columns: {
              name: "Nom",
              email: "Email",
              role: "Rôle",
              businessUnit: "Business Unit",
              actions: "Actions",
            },
            noUsersCompany:
              "Aucun utilisateur au niveau de l'entreprise (non rattaché à une business unit).",
            noUsersBU:
              "Cette business unit n'a pas encore d'utilisateurs. Veuillez inviter des utilisateurs pour commencer.",
            noUsersHint: "Invitez un utilisateur pour commencer.",
            edit: "Modifier",
            remove: "Retirer",
          },
          inviteModal: {
            title: "Inviter un utilisateur",
            fields: {
              email: "Email",
              firstName: "Prénom",
              lastName: "Nom",
              existingUser: "Utilisateur existant",
              businessUnitCode: "Code de la Business Unit",
              role: "Rôle",
              jobTitle: "Intitulé de poste",
              reportingManager: "Manager hiérarchique",
            },
            labels: {
              companyAdmin: "Company Admin",
            },
            buttons: {
              cancel: "Annuler",
              sendInvite: "Envoyer l'invitation",
            },
          },
          editModal: {
            title: "Modifier l'utilisateur",
            fields: {
              firstName: "Prénom",
              lastName: "Nom",
              businessUnit: "Business Unit",
              role: "Rôle",
              jobTitle: "Intitulé de poste",
              reportingManager: "Manager hiérarchique",
            },
            buttons: {
              cancel: "Annuler",
              removeUser: "Retirer l'utilisateur",
              save: "Enregistrer",
            },
          },
          removeDialog: {
            title: "Retirer l'utilisateur",
            confirmQuestion:
              "Êtes-vous sûr de vouloir retirer cet utilisateur ?",
            permanentDeletionTitle: "Suppression définitive",
            permanentDeletionDescription:
              "Cette action supprimera définitivement l'utilisateur de l'entreprise et supprimera son compte Firebase. Cette action est irréversible.",
            buRemovalTitle: "Retrait de la Business Unit",
            buRemovalDescription:
              "Cela retirera l'utilisateur uniquement de cette business unit. Il restera dans l'entreprise s'il appartient à d'autres business units.",
            buttons: {
              cancel: "Annuler",
              removeFromBU: "Retirer de la Business Unit",
              deleteUser: "Supprimer l'utilisateur",
              removing: "Suppression...",
            },
            roleBadge: {
              companyAdmin: "Company Admin",
              admin: "Admin",
            },
          },
          toasts: {
            fetchUsersFailed:
              "Échec du chargement des utilisateurs",
            inviteSuccess: "Invitation envoyée",
            inviteFailed: "Échec de l'invitation",
            updateSuccess: "Utilisateur mis à jour",
            updateFailed: "Échec de la mise à jour",
            removeSuccessCompany:
              "Utilisateur supprimé de l'entreprise",
            removeSuccessBU:
              "Utilisateur retiré de cette Business Unit",
            removeFailed: "Échec de la suppression de l'utilisateur",
          },
        },
      },
      profile: {
        eyebrow: "Compte",
        pageTitle: "Paramètres du profil",
        sections: {
          profileInfo: {
            title: "Informations du profil",
            description: "Mettez à jour vos informations personnelles",
          },
          password: {
            title: "Mot de passe",
            description: "Mettez à jour votre mot de passe",
          },
          linkedin: {
            title: "LinkedIn",
            description: "Ajoutez votre profil",
          },
          businessUnits: {
            title: "Business Units",
            description: "Vos rattachements et rôles",
          },
          role: {
            title: "Rôle",
            description: "Informations définies par votre administrateur",
          },
        },
        fields: {
          firstName: "Prénom",
          lastName: "Nom",
          email: "Adresse email",
          currentPassword: "Mot de passe actuel",
          newPassword: "Nouveau mot de passe",
          linkedinUrl: "URL LinkedIn",
          jobTitle: "Intitulé de poste",
          department: "Département / Équipe / Business Unit",
          reportingManager: "Manager hiérarchique",
          notSet: "Non renseigné",
          noBusinessUnits: "Aucune Business Unit",
        },
        buttons: {
          uploadPhoto: "Télécharger une photo",
          save: "Enregistrer",
          selectBU: "Sélectionner",
        },
        passwordModal: {
          title: "Mot de passe mis à jour",
          body:
            "Vous devez vous déconnecter puis vous reconnecter avec votre nouveau mot de passe.",
          ok: "OK",
        },
        toasts: {
          profileUpdated: "Profil mis à jour",
          profileUpdateFailed: "Échec de la mise à jour du profil",
          passwordUpdated: "Mot de passe mis à jour",
          passwordUpdateFailed: "Échec de la mise à jour du mot de passe",
        },
        notifications: {
          modules: {
            partners: "ASAP Partners",
          },
          eventsApplicationHighMatch: "Candidature à score élevé reçue",
          eventsCvReceived: "CV / candidature reçu(e)",
          eventsNewRequirementFromPartner: "Nouveau besoin d'un partenaire",
          eventsIncomingConsultantFromPartner: "Consultant proposé par un partenaire",
        },
      },
      // ── ASAP Partners status labels (FR, resolved by getStatusLabel) ──
      partnersStatuses: {
        pool: { active: "Actif", archived: "Archivé" },
        poolMember: { active: "Actif", new_to_add: "Nouveau", removed: "Retiré" },
        mission: {
          draft: "Brouillon", sent: "Envoyée", active: "Active",
          filled: "Pourvue", closed: "Clôturée", expired: "Expirée",
        },
        partnerLink: { active: "Actif", suspended: "Suspendu", removed: "Retiré" },
        incoming: {
          pending: "En attente", accepted: "Acceptée",
          declined: "Refusée", completed: "Terminée",
        },
        submission: {
          submitted: "Soumis", scored: "Évalué", shortlisted: "Présélectionné",
          rejected: "Rejeté", positioned: "Positionné",
        },
      },
      // ── ASAP Partners (FR) ──
      partners: {
        communications: {
          title: "Offres d'emploi",
          history: {
            title: "Historique des communications",
            table: { date: "Date", action: "Action", details: "Détails", mission: "Mission" },
            empty: "Aucune communication pour le moment",
          },
          emailConfig: {
            title: "Configuration e-mail",
            configureButton: "Configurer",
            verifyButton: "Vérifier",
            notConfigured: "L'envoi d'e-mails n'est pas encore configuré. Cliquez sur Configurer pour commencer.",
            notConfiguredTitle: "Non configuré",
            senderEmail: "E-mail expéditeur",
            smtpHost: "Hôte SMTP",
            authProtocol: "Protocole d'authentification",
            verified: "Vérifié",
            verifiedYes: "Vérifié",
            verifiedNo: "Non vérifié",
            senderMode: "Mode d'expédition",
            senderModePlatform: "Expéditeur par défaut ASAP HUB",
            senderModeOwn: "Serveur e-mail personnel",
            senderAddress: "Adresse d'expédition",
            senderAddressAuto: "Générée automatiquement au premier envoi",
            modalTitle: "Configurer les paramètres e-mail",
            form: {
              usePlatformSender: "Utiliser l'expéditeur par défaut ASAP HUB",
              usePlatformSenderHelp: "Envoie les e-mails depuis l'adresse partagée no-reply@asap-hub.ai — aucune configuration SMTP nécessaire.",
              fromLocalPart: "Adresse d'expédition",
              fromLocalPartPlaceholder: "votreentreprise",
              fromLocalPartHelp: "L'adresse depuis laquelle vos e-mails de mission sont envoyés. Laissez vide pour la générer automatiquement à partir du nom de votre entreprise.",
              senderEmail: "E-mail expéditeur",
              senderPassword: "Mot de passe / Mot de passe d'application",
              senderPasswordPlaceholder: "Laisser vide pour conserver l'actuel",
              smtpHost: "Hôte SMTP",
              smtpPort: "Port SMTP",
              authProtocol: "Protocole d'authentification",
              authStarttls: "STARTTLS",
              authSsl: "SSL",
              authOauth2: "OAuth2",
              cancel: "Annuler",
              save: "Enregistrer la configuration",
            },
          },
          newCommunication: {
            title: "Nouvelle communication",
            description: "Créez une nouvelle mission pour envoyer des communications à vos pools de consultants. Les missions vous permettent de contacter vos partenaires avec des opportunités, collecter les disponibilités et gérer les réponses.",
            createMissionButton: "Créer une mission",
          },
          toasts: {
            configUpdated: "Configuration e-mail mise à jour",
            configFailedTitle: "Échec de la mise à jour",
            configFailedDescription: "Impossible de mettre à jour la configuration e-mail.",
            verifySuccess: "E-mail de test envoyé — configuration vérifiée",
            verifyFailed: "Échec de la vérification",
          },
        },
        jobPostings: {
          table: {
            name: "Offre d'emploi",
            recipients: "Destinataires",
            publicationDate: "Date de publication",
            cvsReceived: "CVs reçus",
            cvsAboveThreshold: "CVs au-dessus du seuil",
            additionalContacts: "Contacts additionnels",
          },
          empty: "Aucune offre d'emploi pour le moment",
          errorTitle: "Échec du chargement des offres d'emploi",
        },
        poolsAndPartners: {
          title: "Pools & Partenaires",
          eyebrow: "ASAP Partners",
        },
        partnerLinks: {
          fetchError: "Échec du chargement des liens partenaires",
          removed: "Lien partenaire supprimé",
          removeError: "Échec de la suppression du lien partenaire",
          title: "Entreprises partenaires",
          add: "Ajouter un partenaire",
          summary: { loading: "Chargement...", total: "{{count}} partenaire(s)" },
          companyName: "Entreprise",
          code: "Code",
          emptyTitle: "Aucune entreprise partenaire",
          empty: "Aucune entreprise partenaire pour le moment. Ajoutez-en une en saisissant son code partenaire.",
          remove: "Supprimer",
          removeTitle: "Supprimer le partenaire",
          confirmRemove: "Êtes-vous sûr de vouloir supprimer ce lien partenaire ? Vous pourrez toujours le rajouter plus tard.",
          removeAction: "Supprimer",
          codeRequired: "Le code partenaire est requis",
          created: "Lien partenaire créé",
          codeNotFound: "Aucune entreprise trouvée avec ce code partenaire",
          cannotLinkSelf: "Impossible de créer un lien avec votre propre entreprise",
          alreadyExists: "Ce lien partenaire existe déjà",
          addPartner: "Ajouter une entreprise partenaire",
          addDescription: "Entrez le code partenaire de l'entreprise avec laquelle vous souhaitez vous lier. Ce code est fourni par l'entreprise partenaire.",
          partnerCode: "Code partenaire",
          codePlaceholder: "ex. a1b2-c3d4-e5f6-g7h8",
        },
        myCode: {
          copied: "Code partenaire copié dans le presse-papiers",
          title: "Votre code partenaire",
          copy: "Copier",
          notSet: "Aucun code configuré pour cette unité commerciale. Contactez votre administrateur.",
          hint: "Partagez ce code avec d'autres entreprises pour qu'elles puissent se lier avec vous.",
        },
        pools: {
          title: "Pools de consultants",
          createButton: "Créer un pool",
          filters: {
            searchPlaceholder: "Rechercher par nom ou code...",
            perPage: "{{count}}/page",
          },
          summary: { loading: "Chargement...", total: "{{count}} pool(s)" },
          table: {
            name: "Nom", code: "Code", description: "Description",
            status: "Statut", members: "Membres", actions: "Actions",
            noPools: "Aucun pool trouvé",
            noPoolsHint: "Créez un pool pour commencer à regrouper vos consultants.",
          },
          pagination: {
            pageLabel: "Page {{page}} sur {{totalPages}}",
            first: "Début", prev: "Préc.", next: "Suiv.", last: "Fin",
          },
          actions: { editAria: "Modifier", archiveAria: "Archiver" },
          confirmArchive: "Êtes-vous sûr de vouloir archiver « {{name}} » ?",
          archiveConfirmTitle: "Archiver le pool ?",
          archiveConfirmButton: "Archiver",
          toasts: {
            loadFailed: "Échec du chargement des pools",
            archived: "Pool archivé",
            archiveFailedTitle: "Échec de l'archivage",
            archiveFailedDescription: "Impossible d'archiver le pool.",
          },
          form: {
            modalTitleEdit: "Modifier le pool",
            modalTitleCreate: "Créer un pool",
            fields: { name: "Nom", code: "Code", description: "Description" },
            placeholders: { name: "Nom du pool", code: "CODE_POOL", description: "Description optionnelle" },
            buttons: { cancel: "Annuler", saveChanges: "Enregistrer", create: "Créer" },
            toasts: {
              updated: "Pool mis à jour", created: "Pool créé",
              operationFailedTitle: "Échec de l'opération",
              operationFailedDescription: "Une erreur est survenue. Veuillez réessayer.",
            },
          },
          detail: {
            titleFallback: "Détails du pool",
            tabs: { info: "Infos", members: "Membres" },
            info: {
              notFound: "Pool introuvable",
              name: "Nom", code: "Code", status: "Statut",
              description: "Description", created: "Créé le", membersCount: "Membres",
            },
            members: {
              title: "Membres du pool",
              addButton: "Ajouter un membre",
              importButton: "Importer un fichier",
              summary: { loading: "Chargement...", total: "{{count}} membre(s)" },
              table: { name: "Nom", email: "E-mail", status: "Statut", actions: "Actions" },
              noMembers: "Aucun membre pour le moment",
              pagination: {
                pageLabel: "Page {{page}} sur {{totalPages}}",
                perPage: "{{count}}/page", prev: "Préc.", next: "Suiv.",
              },
            },
            editMemberAria: "Modifier",
            deleteMemberAria: "Supprimer",
            close: "Fermer",
            toasts: {
              loadMembersFailed: "Échec du chargement des membres",
              memberDeleted: "Membre retiré",
              deleteFailedTitle: "Échec de la suppression",
              deleteFailedDescription: "Impossible de retirer le membre.",
            },
            confirmDeleteMember: "Êtes-vous sûr de vouloir retirer ce membre ?",
          },
          members: {
            form: {
              modalTitleEdit: "Modifier le membre",
              modalTitleCreate: "Ajouter un membre",
              tabs: { manual: "Saisie manuelle", bulkCv: "Charger des CV" },
              fields: { firstName: "Prénom", lastName: "Nom", email: "E-mail", status: "Statut" },
              placeholders: { firstName: "Prénom", lastName: "Nom", email: "email@exemple.com" },
              buttons: { cancel: "Annuler", saveChanges: "Enregistrer", add: "Ajouter le membre" },
              toasts: {
                updated: "Membre mis à jour", created: "Membre ajouté",
                operationFailedTitle: "Échec de l'opération",
                operationFailedDescription: "Une erreur est survenue. Veuillez réessayer.",
              },
            },
            bulkCv: {
              notice: "Chargez jusqu'à 20 CV (PDF ou DOCX). Le nom et l'e-mail seront extraits automatiquement.",
              fileLabel: "Fichiers CV (PDF ou DOCX)",
              dropzone: "Glissez-déposez les CV ici, ou cliquez pour parcourir",
              accepted: "PDF, DOCX — 20 fichiers maximum",
              selectedCount: "{{count}} fichier(s) sélectionné(s)",
              added: "Ajoutés",
              failed: "En échec",
              uploadButton: "Charger {{count}} CV",
              unsupportedFiltered: "Certains fichiers ont été ignorés (seuls PDF/DOCX sont acceptés)",
              tooManyFiles: "20 fichiers maximum à la fois",
              createdTitle_one: "membre ajouté avec succès",
              createdTitle_other: "membres ajoutés avec succès",
              errorsTitle_one: "fichier n'a pas pu être traité",
              errorsTitle_other: "fichiers n'ont pas pu être traités",
              errors: {
                duplicateEmail: "L'e-mail « {{email}} » est déjà dans ce pool",
                emailNotFound: "Aucun e-mail n'a pu être extrait de ce CV",
                unsupportedType: "Type de fichier non supporté (PDF/DOCX uniquement)",
                parseFailed: "Impossible de lire le texte de ce fichier",
                uploadFailed: "Échec du stockage du fichier",
              },
              toasts: {
                uploadFailed: "Échec de l'import groupé",
              },
            },
            importFromFile: {
              modalTitle: "Importer des membres depuis CSV/XLSX",
              description:
                "Chargez un fichier CSV ou XLSX. Colonnes prises en charge : firstName, lastName (ou une colonne unique « name »), email, status (active / new_to_add / removed) et consultantId optionnel. Utilisez la colonne optionnelle « action » avec les valeurs add, update ou delete pour mettre à jour ou retirer des membres existants par e-mail.",
              fields: { file: "Fichier" },
              statusLabels: {
                total: "Total",
                added: "Ajoutés",
                updated: "Mis à jour",
                deleted: "Supprimés",
                duplicate: "Doublons",
              },
              dropzone: {
                text: "Glissez-déposez un CSV/XLSX ici ou cliquez pour sélectionner",
                accepted: "Accepté : .csv, .xlsx",
              },
              buttons: { cancel: "Annuler", import: "Importer" },
              toasts: {
                importCompleted: "Import terminé",
                importFailedTitle: "Échec de l'import",
                importFailedDescription: "Veuillez réessayer",
                invalidFileTypeTitle: "Type de fichier invalide",
                invalidFileTypeDescription: "Seuls les fichiers CSV ou XLSX sont acceptés",
                invalidExcelDescription:
                  "Le fichier Excel n'a pas pu être lu. Veuillez vérifier son format.",
                importTooLargeTitle: "Import trop volumineux",
                importTooLargeDescription:
                  "Ce fichier contient plus de 500 lignes de données. Veuillez le scinder et réessayer.",
              },
            },
          },
        },
        missions: {
          title: "Missions",
          createButton: "Nouvelle mission",
          searchPlaceholder: "Rechercher par titre...",
          allStatuses: "Tous les statuts",
          empty: "Aucune mission pour le moment",
          totalItems: "{{count}} missions",
          purpose: "Objectif",
          location: "Localisation",
          tjm: "Taux journalier",
          startDate: "Date de début",
          asap: "ASAP",
          closeDialog: {
            title: "Clôturer cette mission ?",
            body: "La mission sera clôturée et n'acceptera plus de candidatures.",
            notifyQuestion: "Souhaitez-vous envoyer une notification automatique à tous les candidats ?",
            notifyHint: "Les {{count}} candidat(s) ayant postulé à cette mission recevront un e-mail les informant que le poste a été pourvu, les remerciant pour leur candidature et proposant de conserver leurs coordonnées pour de futures opportunités.",
            notifyExclusions: "Le candidat positionné et les candidats soumis par des entreprises partenaires ne sont pas notifiés.",
            noApplicants: "Personne n'a postulé à cette mission, aucune notification ne sera envoyée.",
            yes: "Oui, notifier les candidats",
            no: "Non, clôturer sans notifier",
            cancel: "Annuler",
          },
          duration: "Durée",
          table: {
            title: "Titre", purpose: "Objectif", status: "Statut",
            recipients: "Destinataires", applications: "Candidatures", created: "Créé le",
          },
          purposeLabels: {
            contactPool: "Contact pool",
            proposeAssignment: "Mission",
          },
          form: {
            steps: {
              purpose: "Objectif", details: "Détails", skills: "Compétences",
              conditions: "Conditions", recipients: "Destinataires", review: "Récapitulatif",
            },
            purposeOptions: {
              contactPool: "Partager une mission avec une liste de candidats",
              proposeAssignment: "Proposer une mission à une entreprise partenaire",
            },
            purposeDescription: "Sélectionnez le type de communication que vous souhaitez envoyer.",
            createTitle: "Créer une mission",
            editTitle: "Modifier la mission",
            title: "Titre",
            titlePlaceholder: "ex. Développeur Full-Stack React/Python",
            description: "Description",
            descriptionPlaceholder: "Décrivez la mission, le contexte et les attentes...",
            category: "Catégorie",
            categoryPlaceholder: "ex. IT, Data, Management",
            technicalSkills: "Compétences techniques",
            technicalSkillsPlaceholder: "ex. React, Python, PostgreSQL",
            functionalSkills: "Compétences fonctionnelles",
            functionalSkillsPlaceholder: "ex. Agile, Scrum, Gestion de projet",
            businessSkills: "Compétences métier",
            businessSkillsPlaceholder: "ex. Finance, Marketing, Ventes",
            requiredSkills: "Compétences requises",
            optionalSkills: "Compétences optionnelles",
            addSkillButton: "Ajouter",
            skillsDescription: "Ajoutez les compétences par catégorie, puis marquez chacune comme requise ou optionnelle pour le scoring.",
            importanceTitle: "Importance des compétences",
            importanceDescription: "Cliquez pour basculer chaque compétence entre requise, optionnelle ou non classée. Les compétences requises pèsent 80% dans le scoring, les optionnelles 20%.",
            legendRequired: "Requise",
            legendOptional: "Optionnelle",
            legendUnclassified: "Non classée",
            location: "Localisation",
            locationPlaceholder: "ex. Paris, Télétravail",
            startDate: "Date de début",
            startAsap: "ASAP",
            tjm: "TJM",
            tjmMin: "TJM Min (€)",
            tjmMax: "TJM Max (€)",
            durationDays: "Durée (jours)",
            durationValue: "Durée",
            durationUnit: "Unité",
            unitDays: "Jours",
            unitMonths: "Mois",
            days: "jours",
            months: "mois",
            isRenewable: "Renouvelable",
            contractType: "Type de contrat",
            contractTypePlaceholder: "Sélectionnez un type de contrat",
            contractTypeFreelance: "Freelance",
            contractTypeCdi: "CDI",
            contractTypePortage: "Portage salarial",
            contractTypeSousTraitance: "Sous-traitance",
            salary: "Salaire",
            salaireMin: "Salaire Min (€/an)",
            salaireMax: "Salaire Max (€/an)",
            selonProfil: "Selon profil",
            renewable: "(renouvelable)",
            atsThreshold: "Seuil de score ATS",
            atsNone: "Aucun seuil",
            ats50: "50% minimum",
            ats80: "80% minimum",
            recipientsDescription: "Sélectionnez les pools de consultants qui recevront cette mission.",
            loadingPools: "Chargement des pools...",
            noPools: "Aucun pool disponible. Créez d'abord un pool.",
            members: "membres",
            reviewDescription: "Vérifiez votre mission avant de l'enregistrer.",
            purpose: "Objectif",
            selectedPools: "Pools sélectionnés",
            noPoolsSelected: "Aucun pool sélectionné",
            cancel: "Annuler",
            back: "Retour",
            next: "Suivant",
            save: "Enregistrer",
            createDraft: "Créer le brouillon",
            evaluationLanguage: "Langue d'évaluation",
            poolsSection: "Pools de consultants",
            partnersSection: "Entreprises partenaires",
            createAndSend: "Créer & Envoyer",
            autoMode: "Mode automatique",
            autoModeDescription: "Mode automatique : le système sélectionnera automatiquement les compétences les plus pertinentes et générera l'évaluation. Toutes les compétences ci-dessous seront envoyées pour analyse.",
            includeEvaluation: "Inclure l'évaluation SKILLS",
            evaluationDescription: "Les candidats recevront un e-mail d'évaluation séparé avec un test technique. Sélectionnez les compétences à inclure (max 5).",
            selectedSkillsCount: "Sélectionnées : {{count}} / {{max}}",
            tooManySkills: "Maximum {{max}} compétences autorisées",
            evalSkill: "Compétence",
            evalType: "Type",
            evalDifficulty: "Difficulté",
            evalImportance: "Importance",
            evalCoding: "Coding",
            ats60: "60% minimum",
            ats75: "75% minimum",
            ats85: "85% minimum",
            pilotNeed: "Pré-remplir depuis un besoin client (PILOT)",
            loadingNeeds: "Chargement des besoins clients...",
            noNeeds: "Aucun besoin client ouvert disponible.",
            selectNeed: "-- Sélectionnez un besoin client pour pré-remplir --",
            loadingPools: "Chargement des pools...",
            loadingPartners: "Chargement des entreprises partenaires...",
            noPartners: "Aucune entreprise partenaire liée. Ajoutez des partenaires depuis la page Partenaires.",
            recipientsDescriptionPools: "Sélectionnez les pools de consultants qui recevront cette offre d'emploi.",
            recipientsDescriptionPartners: "Sélectionnez les entreprises partenaires qui recevront cette mission.",
            selectedPartners: "Partenaires sélectionnés",
            noPartnersSelected: "Aucune entreprise partenaire sélectionnée",
            saveAsDraft: "Enregistrer comme brouillon",
            saveAsDraftDescription: "Enregistrer cette mission comme brouillon au lieu de l'envoyer immédiatement.",
            skillsEvaluation: "Évaluation SKILLS",
            skillsSelected: "compétences sélectionnées",
            rfpUpload: "Télécharger un cahier des charges (optionnel)",
            rfpUploadDescription: "Téléchargez un document de description de poste pour pré-remplir les champs du formulaire. Formats pris en charge : PDF, DOCX (max 10 Mo).",
            rfpDragDrop: "Glissez-déposez un fichier ici, ou cliquez pour parcourir",
            rfpExtracting: "Extraction des données du document...",
            rfpOverwriteTitle: "Écraser les données existantes ?",
            rfpOverwriteBody: "Le formulaire contient déjà des données. L'application des données extraites du cahier des charges remplacera le titre, la description, les compétences et la localisation actuels.",
            rfpOverwriteCancel: "Annuler",
            rfpOverwriteConfirm: "Appliquer les données",
          },
          sections: {
            title: "Texte publié",
            help: "Ce texte est publié dans l'email et sur la page de mission. Modifiez-le librement : vous en gardez le contrôle total.",
            generate: "Générer avec l'IA",
            regenerate: "Régénérer avec l'IA",
            generating: "Génération...",
            generateError: "Impossible de générer le texte. Veuillez réessayer.",
            responsibilities: "Missions, rôles et responsabilités",
            requiredSkills: "Compétences requises / attendues",
            candidateProfile: "Profil recherché",
            responsibilitiesPlaceholder: "Phrase d'introduction suivie de puces « - »...",
            requiredSkillsPlaceholder: "Phrase d'introduction suivie de puces « - »...",
            candidateProfilePlaceholder: "Phrase d'introduction suivie de puces « - »...",
            regenerateTitle: "Régénérer le texte ?",
            regenerateBody: "Vous avez modifié le texte publié. La régénération par IA remplacera vos modifications dans les trois sections.",
            regenerateCancel: "Garder mon texte",
            regenerateConfirm: "Régénérer",
            descriptionLabel: "Description (Contexte)",
            descriptionHelp: "Affichée comme la section Contexte de l'email. Modifiez-la ici avant l'envoi.",
            descriptionPlaceholder: "Décrivez la mission, le contexte et les attentes...",
            previewTitle: "Aperçu de l'email",
            previewHelp: "Voici l'email que les consultants recevront. Modifiez le contenu à gauche pour le mettre à jour.",
            previewLoading: "Mise à jour de l'aperçu...",
            previewError: "Impossible de charger l'aperçu. Modifiez un champ pour réessayer.",
            subjectLabel: "Objet de l'email",
            subjectHelp: "L'objet que les consultants verront. Modifiez-le librement (par exemple, pour signaler une mise à jour).",
            resetToGenerated: "Réinitialiser",
            customizeTitle: "Personnaliser l'email complet",
            customizeHelp: "Activez cette option pour réécrire entièrement l'email. Le contenu généré sert de point de départ. [Prénom] et le lien de candidature sont remplis automatiquement pour chaque consultant.",
            emailBodyLabel: "Contenu de l'email",
            revertToGenerated: "Revenir au contenu généré",
          },
          detail: {
            loading: "Chargement...",
            notFound: "Mission introuvable",
            tabs: { info: "Infos", recipients: "Destinataires", applications: "Candidatures" },
            purpose: "Objectif",
            category: "Catégorie",
            location: "Localisation",
            tjm: "TJM",
            startDate: "Date de début",
            duration: "Durée",
            contractType: "Type de contrat",
            atsThreshold: "Seuil ATS",
            created: "Créé le",
            description: "Description",
            technicalSkills: "Compétences techniques",
            functionalSkills: "Compétences fonctionnelles",
            businessSkills: "Compétences métier",
            requiredSkills: "Compétences requises",
            optionalSkills: "Compétences optionnelles",
            landingPage: "Page d'atterrissage",
            copyLinkAria: "Copier le lien",
            deliverability: {
              title: "Délivrabilité des e-mails",
              sent: "Envoyés",
              delivered: "Délivrés",
              bounced: "Rejetés",
              complained: "Plaintes",
              deliveryRate: "Taux de délivrabilité",
              bounceRate: "Taux de rejet",
              complaintRate: "Taux de plainte",
            },
            noRecipients: "Aucun destinataire ajouté",
            recipientType: "Type",
            recipientName: "Nom",
            emailSentAt: "E-mail envoyé",
            recipientTypePool: "Pool",
            recipientTypePartner: "Partenaire",
            noApplications: "Aucune candidature reçue pour le moment",
            applicant: "Candidat",
            email: "E-mail",
            score: "Score",
            appStatus: "Statut",
            appDate: "Date",
            close: "Fermer",
            copyLink: "Copier le lien",
            copyLinkedIn: "Copier le post LinkedIn",
            candidate: "Candidat",
            submissionFallback: "Soumission n°{{id}}",
            profile: "Profil",
            experience: "Expérience",
            years: "ans",
            edit: "Modifier",
            send: "Envoyer",
            closeMission: "Clôturer la mission",
            phone: "Téléphone",
            referral: "Parrainage",
            referredBy: "Référé par",
            skills: "Compétences",
            downloadCv: "Télécharger le CV",
            downloadingCv: "Téléchargement...",
            downloadError: "Échec du téléchargement du CV",
            pool: "Pool",
            selectPool: "Sélectionner un pool",
            addToPool: "Ajouter au pool",
            addedToPool: "Candidat ajouté au pool",
            addToPoolError: "Échec de l'ajout au pool",
            alreadyInPool: "Ce candidat est déjà dans le pool sélectionné",
            inPool: "Déjà dans un pool",
            consultantLinked: "Consultant #{{id}}",
            createConsultant: "Créer un consultant",
            consultantCreated: "Consultant créé avec succès",
            consultantCreatedDesc: "Le consultant #{{id}} a été ajouté à PILOT.",
            consultantError: "Échec de la création du consultant",
            alreadyConsultant: "Cette candidature a déjà un consultant associé.",
            loadError: "Échec du chargement de la mission",
            skillsEvaluation: "Évaluation SKILLS",
            evaluationAttached: "Évaluation attachée",
            noSubmissions: "Aucune soumission partenaire reçue pour le moment",
            submitter: "Soumis par",
            cost: "Coût",
            availability: "Disponibilité",
            tabs: { info: "Infos", recipients: "Destinataires", applications: "Candidatures", submissions: "Soumissions partenaires" },
          },
          toasts: {
            loadError: "Échec du chargement des missions",
            created: "Mission créée",
            updated: "Mission mise à jour",
            errorTitle: "Erreur",
            errorDescription: "Impossible d'enregistrer la mission.",
            sent: "Mission envoyée avec succès",
            sendError: "Échec de l'envoi de la mission",
            closed: "Mission clôturée",
            closedNotified: "Mission clôturée. Les candidats sont notifiés par e-mail.",
            closeError: "Échec de la clôture de la mission",
            linkCopied: "Lien de la page d'atterrissage copié",
            linkError: "Échec de la copie du lien",
            linkedInCopied: "Post LinkedIn copié dans le presse-papiers",
            linkedInCopyError: "Échec de la copie du post LinkedIn",
            rfpUnsupported: "Seuls les fichiers PDF et DOCX sont pris en charge",
            rfpTooLarge: "Fichier trop volumineux (max 10 Mo)",
            rfpError: "Échec de l'extraction des données du cahier des charges",
            rfpExtracted: "Données extraites et pré-remplies",
          },
          linkedIn: {
            notSpecified: "À préciser",
            durationMonths: "{{count}} mois",
            durationDays: "{{count}} jours",
            renewable: "(renouvelable)",
            headline: "📢 Nouvelles opportunités de mission 📢",
            intro: "Je suis actuellement à la recherche d'un consultant pour accompagner mon client sur une mission de {{title}}.",
            infoSection: "📍 Informations clés",
            locationLine: "• Localisation : {{value}}",
            startDateLine: "• Démarrage : {{value}}",
            durationLine: "• Durée : {{value}}",
            contractLine: "• Contrat : {{value}}",
            profilesSection: "👤 Profils attendus",
            experienceLine: "• Solide expérience en {{title}}",
            skillsLine: "• Maîtrise de {{skills}}",
            skillsFallback: "• Maîtrise des outils et méthodes du domaine",
            relationalLine: "• Bon relationnel et sens du service client",
            applySection: "📬 Vous êtes disponible ou ouvert(e) à de nouvelles opportunités ?",
            applyDirect: "Vous pouvez postuler directement via le lien suivant :",
            closingLine1: "Votre candidature sera analysée rapidement, et nous reprendrons contact avec vous si votre profil correspond aux besoins de nos clients.",
            closingLine2: "N'hésitez pas à liker, commenter ou partager ce post pour lui donner de la visibilité auprès des bonnes personnes.",
            closingLine3: "Merci d'avance pour votre aide et vos recommandations.",
            hashtags: "#Consultants #Mission #Recrutement",
          },
        },
        incoming: {
          title: "Exigences",
          empty: "Aucune mission entrante pour le moment.",
          totalItems: "{{count}} missions entrantes",
          searchPlaceholder: "Rechercher par titre...",
          allStatuses: "Tous les statuts",
          fetchError: "Échec du chargement des missions entrantes",
          missionTitle: "Mission",
          sender: "De",
          purpose: "Objectif",
          missionStatus: "Statut de la mission",
          submissionsCount: "Soumissions",
          accepted: "Mission acceptée",
          declined: "Mission refusée",
          table: {
            mission: "Mission", sender: "Expéditeur", purpose: "Objectif",
            status: "Statut", submissions: "Soumissions", created: "Créé le",
          },
          score: "Score",
          detail: "Détail de la mission entrante",
          accept: "Accepter",
          decline: "Refuser",
          submissions: "Soumissions",
          submitCandidate: "Soumettre un candidat",
          noSubmissions: "Aucun candidat soumis pour le moment.",
          candidate: "Candidat",
          nameRequired: "Le prénom et le nom sont requis",
          sourceCvRequired: "Veuillez téléverser un CV",
          candidateSubmitted: "Candidat soumis avec succès",
          sourceCv: "CV",
          uploadCv: "Cliquez pour télécharger un CV",
          fileTooLarge: "Fichier trop volumineux (max 10 Mo)",
          costAmount: "Coût",
          costType: "Type de coût",
          selectCostType: "Sélectionner le type",
          salary: "Salaire",
          dailyRate: "Taux journalier",
          availabilityDate: "Date de disponibilité",
          submit: "Soumettre",
          toasts: {
            loadError: "Échec du chargement des missions entrantes",
            accepted: "Mission acceptée", declined: "Mission refusée",
            updateError: "Échec de la mise à jour du statut",
          },
        },
        companyLinks: {
          title: "Entreprises partenaires",
          addButton: "Ajouter un partenaire",
          empty: "Aucune entreprise partenaire liée",
          totalItems: "{{count}} partenaire(s)",
          table: {
            companyName: "Entreprise", partnerCode: "Code partenaire",
            status: "Statut", created: "Créé le", actions: "Actions",
          },
          form: {
            modalTitle: "Ajouter une entreprise partenaire",
            partnerCode: "Code partenaire",
            partnerCodePlaceholder: "ex. a1b2-c3d4-e5f6-g7h8",
            cancel: "Annuler", add: "Ajouter le partenaire",
          },
          toasts: {
            created: "Entreprise partenaire liée",
            createError: "Échec de la liaison du partenaire",
            removed: "Lien partenaire supprimé",
            removeError: "Échec de la suppression du lien partenaire",
          },
          confirmRemove: "Supprimer ce lien partenaire ?",
        },
        dashboard: {
          title: "ASAP PARTNERS — Tableau de bord",
          fetchError: "Échec du chargement des données du tableau de bord",
          poolSection: "Pools de consultants",
          missionSection: "Missions",
          applicationSection: "Candidatures",
          partnerSection: "Entreprises partenaires",
          communicationSection: "Communications",
          activePools: "Pools actifs",
          totalPoolMembers: "Membres des pools",
          totalMissions: "Missions totales",
          missionsByPurpose: "Missions par objectif",
          totalApplications: "Candidatures totales",
          avgScore: "Score moy. : {{score}}",
          activePartnerLinks: "Liens partenaires actifs",
          totalIncoming: "Missions entrantes",
          totalSubmissions: "Soumissions partenaires",
          totalCommunications: "Communications totales",
          sections: {
            pools: "Pools", missions: "Missions", applications: "Candidatures",
            partners: "Partenaires", communications: "Communications",
          },
          stats: {
            activePools: "Pools actifs", totalMembers: "Membres totaux",
            totalMissions: "Missions totales", missionsByStatus: "Par statut",
            missionsByPurpose: "Par objectif",
            totalApplications: "Candidatures totales", averageScore: "Score moyen",
            applicationsByStatus: "Par statut",
            activePartnerLinks: "Liens partenaires", totalIncoming: "Missions entrantes",
            incomingByStatus: "Par statut",
            totalSubmissions: "Soumissions", submissionsByStatus: "Par statut",
            totalCommunications: "Communications",
          },
        },
        sentRequests: {
          title: "Demandes envoyées",
          tabs: { missions: "Missions", consultants: "Consultants" },
          consultantsAllStatuses: "Tous les statuts",
          toasts: {
            loadMissionsError: "Échec du chargement des missions",
            loadProposalsError: "Échec du chargement des propositions de consultants",
          },
        },
        receivedRequests: {
          title: "Demandes reçues",
          tabs: { missions: "Missions", consultants: "Consultants" },
          fetchError: "Échec du chargement des demandes reçues",
          fetchProposalsError: "Échec du chargement des propositions de consultants",
        },
        stats: {
          chartTitle: "Activité mensuelle",
          total: "Total : {{count}}",
          barLabel: "Nombre",
          noData: "Aucune activité sur cette période",
        },
        consultantProposals: {
          createButton: "Proposer un consultant",
          empty: "Aucune proposition de consultant envoyée pour le moment",
          emptyReceived: "Aucune proposition de consultant reçue pour le moment.",
          partnerNumber: "Partenaire",
          statusLabels: {
            pending: "En attente",
            accepted: "Acceptée",
            declined: "Refusée",
          },
          table: {
            recipient: "Destinataire",
            from: "De",
            role: "Poste",
            dailyRate: "TJM",
            availability: "Disponibilité",
            status: "Statut",
            created: "Envoyée le",
          },
          form: {
            title: "Proposer un consultant",
            anonymisedNotice:
              "Le partenaire destinataire ne verra que le profil anonymisé (sans nom, e-mail ni téléphone), votre TJM et la date de disponibilité.",
            source: "Source",
            sourcePilot: "Choisir depuis Pilot",
            sourceCv: "Charger un CV",
            pilotDisabled:
              "Le mode Pilot n'est pas disponible — votre unité commerciale n'a pas accès à ASAP Pilot.",
            consultant: "Consultant",
            selectConsultant: "Sélectionnez un consultant",
            cvFile: "Fichier CV (PDF ou DOCX)",
            cvDropzone: "Glissez-déposez un CV ici, ou cliquez pour parcourir",
            cvAccepted: "PDF ou DOCX, un fichier",
            dailyRate: "TJM",
            currency: "Devise",
            availabilityDate: "Date de disponibilité",
            recipients: "Envoyer aux entreprises partenaires",
            noPartners:
              "Aucun lien partenaire actif — ajoutez d'abord un partenaire.",
            submit: "Envoyer la proposition",
          },
          detail: {
            title: "Proposition de consultant",
            anonymisedNotice:
              "Vue anonymisée — le nom, l'e-mail et le téléphone ne sont pas partagés.",
            sender: "De",
            role: "Poste",
            description: "Profil",
            dailyRate: "TJM",
            availability: "Disponible à partir du",
            location: "Localisation",
            skillsTechnical: "Compétences techniques",
            skillsFunctional: "Compétences fonctionnelles",
            skillsBusiness: "Compétences métier",
            note: "Note (facultatif)",
            notePlaceholder: "Un message à destination de l'expéditeur...",
            matchTitle: "Comparer à mes besoins ouverts",
            runMatch: "Lancer le scoring",
            noMatches: "Aucun besoin correspondant au-dessus du seuil.",
            matchesFound_one: "{{count}} besoin ouvert correspondant trouvé",
            matchesFound_other: "{{count}} besoins ouverts correspondants trouvés",
            viewMatches: "Voir les correspondances",
            accept: "Accepter",
            decline: "Refuser",
          },
          matches: {
            title: "Besoins ouverts correspondants",
            empty: "Aucun besoin correspondant au-dessus du seuil.",
            table: {
              need: "Besoin",
              client: "Client",
              score: "Score",
              reasoning: "Justification",
            },
          },
          toasts: {
            created: "Proposition de consultant envoyée",
            accepted: "Proposition acceptée",
            declined: "Proposition refusée",
          },
          errors: {
            loadPartners: "Échec du chargement des entreprises partenaires",
            loadConsultants: "Échec du chargement des consultants",
            unsupportedFile: "Seuls les fichiers PDF et DOCX sont acceptés",
            noRecipients: "Sélectionnez au moins une entreprise partenaire",
            noConsultant: "Choisissez un consultant dans votre vivier",
            noCv: "Chargez un fichier CV",
            createFailed: "Échec de l'envoi de la proposition",
            decisionFailed: "Échec de l'enregistrement de la décision",
            matchFailed: "Échec du calcul des correspondances",
          },
        },
      },
      superAdmin: {
        deleteCompany: {
          action: "Supprimer",
          tooltip: "Supprimer l'entreprise et ses environnements",
          title: "Supprimer l'entreprise",
          body: "Cette action retirera \"{{name}}\" et tous ses environnements des tableaux de bord, et révoquera immédiatement l'accès de tous ses utilisateurs. Elle est irréversible depuis le portail. Continuer ?",
          confirm: "Supprimer l'entreprise",
          success: "Entreprise supprimée",
          error: "Échec de la suppression de l'entreprise",
        },
        deleteBusinessUnit: {
          title: "Supprimer l'environnement",
          body: "Cette action retirera l'environnement \"{{name}}\" et révoquera l'accès de ses membres. Elle est irréversible depuis le portail. Continuer ?",
          confirm: "Supprimer l'environnement",
          success: "Environnement supprimé",
          error: "Échec de la suppression de l'environnement",
        },
      },
      common: {
        pagination: {
          pageLabel: "Page {{page}} sur {{total}}",
          page: "page",
          first: "Début", prev: "Préc.", next: "Suiv.", last: "Fin",
        },
        name: "Nom", email: "E-mail", status: "Statut", createdAt: "Date",
        firstName: "Prénom", lastName: "Nom",
        description: "Description", cancel: "Annuler",
        close: "Fermer",
        days: "jours", delete: "Supprimer",
        comingSoon: "Bientôt disponible",
        allStatuses: "Tous les statuts",
        error: "Erreur",
        unknownError: "Une erreur est survenue",
        confirmDelete: "Confirmer la suppression",
        confirmDeleteMessage: "Voulez-vous vraiment supprimer cet élément ?",
      },
      evaluation: {
        soundOn: "Son activé",
        soundOff: "Son désactivé",
        timeRunningLow: "Temps bientôt écoulé",
        micCheck: {
          title: "Test du microphone",
          instructions:
            "Nous allons enregistrer un court test pour vérifier que votre microphone et le service de dictée fonctionnent. Votre voix n'est pas conservée.",
          testButton: "Tester le microphone",
          testing: "Test en cours…",
          successTitle: "Microphone opérationnel",
          successMessage: "Parfait — votre microphone et la dictée sont prêts.",
          failureTitle: "Échec du test du microphone",
          failureMessage:
            "Nous n'avons pas pu vérifier votre microphone. Vérifiez que votre navigateur a bien accès au micro et réessayez.",
          troubleshooting: "Conseils de dépannage",
          tipPermission: "Autorisez l'accès au microphone lorsque votre navigateur le demande.",
          tipDevice: "Vérifiez que le bon microphone est sélectionné dans les paramètres système.",
          tipNetwork: "Une connexion internet stable est nécessaire pour la dictée en direct.",
          retry: "Réessayer",
          continue: "Continuer",
          continueWithout: "Continuer sans microphone",
          skipExplanation:
            "Vous pourrez quand même faire le test au clavier. La dictée sera désactivée.",
        },
        sampleQuestion: {
          pageTitle: "Question d'entraînement",
          banner:
            "Question d'entraînement — elle n'entre pas dans votre note finale. Utilisez-la pour vous familiariser avec l'interface.",
          yourAnswer: "Votre réponse",
          submit: "Soumettre la réponse d'entraînement",
          submitting: "Évaluation en cours…",
          resultTitle: "Résultat de l'entraînement",
          score: "Score",
          comment: "Commentaire",
          startTest: "Commencer le vrai test",
          skip: "Passer l'entraînement",
          fetchError:
            "Impossible de charger la question d'entraînement. Vous pouvez passer directement au test.",
          previewError:
            "Impossible d'évaluer votre réponse d'entraînement. Vous pouvez continuer malgré tout.",
        },
        practice: {
          heading: "Question d'entraînement",
          banner:
            "Question d'entraînement — elle ne compte pas dans votre note finale. Utilisez-la pour vous familiariser avec l'interface.",
          fetchError:
            "Impossible de charger la question d'entraînement. Vous pouvez démarrer le vrai test directement.",
          startTest: "Commencer le vrai test",
          yourAnswer: "Votre réponse",
          answerPlaceholder: "Tapez votre réponse ou utilisez le microphone...",
          startMic: "Dicter au microphone",
          stopMic: "Arrêter la dictée",
          verify: "Vérifier ma réponse",
          previewError: "Impossible d'évaluer votre réponse d'entraînement. Veuillez réessayer.",
          resultTitle: "Résultat de l'entraînement",
          score: "Score",
          comment: "Commentaire",
          footer:
            "Quand vous êtes prêt(e), cliquez sur « Commencer le vrai test ». Les réponses d'entraînement ne sont pas enregistrées.",
          sectionContext: "Contexte",
          sectionQuestion: "Question",
          sectionScoring: "Règles de notation",
          questionTypes: {
            OPEN: "Question ouverte",
            CODING: "Question de code",
            QCM: "Question à choix unique",
            "MULTI-CHOICE": "Question à choix multiples",
            KATA_OPEN: "Mini-exercice ouvert",
            KATA_CODING: "Mini-exercice de code",
            CODE_COMPLETION: "Complétion de code",
          },
          tour: {
            timerTitle: "Minuteur",
            timer:
              "Chaque question a un compte à rebours. La barre passe au rouge quand le temps est presque écoulé. La question d'entraînement ne se valide pas automatiquement — prenez le temps qu'il vous faut.",
            questionTextTitle: "Contenu de la question",
            questionText:
              "Les vraies questions affichent ici le contexte, l'énoncé et les règles de notation. Lisez attentivement avant de répondre.",
            verifyTitle: "Vérifier votre réponse",
            verify:
              "Cliquez sur « Vérifier ma réponse » pour obtenir un retour rapide sur la question d'entraînement. Pendant le vrai test, ce bouton donne un score de complétude pour affiner votre réponse.",
            microphoneTitle: "Microphone",
            microphone:
              "Utilisez le microphone pour dicter votre réponse plutôt que de la taper. Parlez en phrases courtes et faites une pause entre chaque — la transcription apparaît au fil de la parole.",
            progress: "{{current}} / {{total}}",
            skip: "Passer",
            next: "Suivant",
            done: "Compris",
          },
        },
      },
      // Additional portal sections will be added here (settings, asapCV, asapSkills, cvModule)
    },
    public: {
      landing: {
        applyingFor: "Candidature pour",
        footer: { poweredBy: "Propulsé par" },
        missionClosed: "Cette mission n'accepte plus de candidatures.",
        missionNotFound: "Cette mission n'existe pas ou le lien est invalide.",
        loadFailed: "Échec du chargement de la mission. Veuillez réessayer plus tard.",
        loading: "Chargement...",
        cannotApply: "Candidature impossible",
        skillsForMission: "Compétences pour cette mission :",
        required: "requis",
        niceToHave: "souhaité",
        firstName: "Prénom",
        lastName: "Nom de famille",
        email: "E-mail",
        phone: "Téléphone",
        cvResume: "CV",
        extractingSkills: "Extraction des compétences de votre CV...",
        dragDropCv: "Glissez-déposez votre CV ici, ou cliquez pour parcourir",
        cvFormats: "PDF, DOC ou DOCX (max 10 Mo)",
        fileTooLarge: "Fichier trop volumineux (max 10 Mo)",
        fillRequired: "Veuillez remplir tous les champs obligatoires",
        submitApplication: "Soumettre ma candidature",
        submitting: "Envoi en cours...",
        submissionFailed: "Échec de la soumission",
        error: "Erreur",
        referral: "J'ai été recommandé(e)",
        referrerFirstName: "Prénom du référent",
        referrerLastName: "Nom du référent",
        tac: {
          label: "J'accepte les",
          linkText: "Conditions Générales d'Utilisation",
        },
        renewable: "(renouvelable)",
        success: {
          title: "Candidature envoyée !",
          message: "Votre candidature a bien été reçue et est en cours d'examen. Vous serez contacté(e) si votre profil correspond aux exigences.",
          closePage: "Vous pouvez fermer cette page.",
        },
        welcome: {
          loading: "Chargement des détails de la mission...",
          missionNotFound: "Cette mission n'existe pas ou le lien est invalide.",
          loadFailed: "Échec du chargement de la mission. Veuillez réessayer plus tard.",
          errorTitle: "Mission introuvable",
          closedTitle: "Mission clôturée",
          closedMessage: "Cette mission n'accepte plus de candidatures.",
          purposePool: "Pool de consultants",
          purposeAssignment: "Mission",
          purposeBench: "Inter-contrat",
          location: "Localisation",
          dailyRate: "Taux journalier (TJM)",
          from: "À partir de",
          upTo: "Jusqu'à",
          startDate: "Date de début",
          duration: "Durée",
          days: "jours",
          months: "mois",
          requiredSkills: "Compétences requises",
          niceToHave: "Compétences souhaitées",
          applyNow: "Postuler maintenant",
        },
        terms: {
          title: "Conditions Générales d'Utilisation",
          placeholder: "Contenu à venir.",
        },
        submitNow: "Postuler maintenant",
        sections: {
          contexte: "Contexte",
          missionsRoles: "Missions, Rôles et responsabilités",
          competencesRequises: "Compétences requises",
          profilRecherche: "Profil recherché",
          informationsPratiques: "Informations pratiques",
          locationLabel: "Lieu",
          startDateLabel: "Démarrage",
          asap: "ASAP",
          durationLabel: "Durée",
          contractLabel: "Contrat",
          contractTypes: {
            freelance: "Freelance",
            cdi: "CDI",
            portage: "Portage salarial",
            sous_traitance: "Sous-traitance",
          },
          budgetLabel: "Budget",
          tjmLabel: "TJM",
          salaryLabel: "Salaire",
          selonProfil: "selon profil",
          days: "jours",
          months: "mois",
          year: "an",
          day: "jour",
          renewable: "(renouvelable)",
        },
      },
      unsubscribe: {
        title: "Confirmer la désinscription",
        confirmPrompt: "Vous êtes sur le point de vous désinscrire ({{email}}). Souhaitez-vous confirmer ?",
        confirmCta: "Me désinscrire",
        successTitle: "Vous êtes désinscrit·e",
        successBody: "Vous ne recevrez plus d'emails de cette liste.",
        invalidToken: "Ce lien de désinscription est invalide ou a expiré.",
        loadFailed: "Une erreur est survenue. Veuillez réessayer.",
        cannotProcess: "Désinscription impossible",
        poolName: "Liste : {{name}}",
        error: "La désinscription a échoué.",
      },
    },
  },
};

i18n.use(initReactI18next).init({
  resources,
  lng: savedLanguage,
  fallbackLng: "en",
  interpolation: {
    escapeValue: false,
  },
  ns: ["portal", "public"],
  defaultNS: "portal",
});

export default i18n;
